export default {
  app: {
    title: 'Channels',
    description: 'Create and manage your video websites',
  },
  title: 'Websites',
  newButton: 'New',
  demoTitle: 'Watch the presentation',
  createButton: 'Create a website',
  backToList: 'Back to websites',
  header: {
    title: 'Your',
    titleAccent: 'publishing spaces',
    hint: 'Your own branded site to publish your content — videos, audio, articles — and meet your audience.',
  },
  status: {
    active: 'Active',
    inactive: 'Inactive',
    maintenance: 'Maintenance',
    draft: 'Draft',
    offline: 'Offline',
  },
  previewLink: {
    open: 'Preview',
    failed: 'The preview could not be opened.',
    noAddress: 'Your channel answers on no address yet: connect one to open it.',
  },
  error: {
    title: 'Failed to load websites',
    description: 'An error occurred while loading your websites. Please try again.',
    retryButton: 'Retry',
  },
  emptyState: {
    title: 'No websites yet',
    description: 'Create your first website to get started.',
    buttonText: 'Create a website',
  },
  card: {
    actions: 'Actions',
    rename: {
      title: 'Rename channel',
      label: 'Channel name',
      done: 'Channel renamed',
      error: 'Renaming failed',
      stale: 'Somebody saved first, so nothing was written. The list has just been read again.',
    },
    delete: {
      title: 'Delete channel',
      message: 'Deleting "{name}" takes its site offline: visitors no longer reach it, and its items, pages and members are deleted with it. Its subdomain is released after the retention period. The media stay in your library.',
      confirmLabel: 'Type {name} to confirm',
      confirmHint: 'Enter the channel\'s name exactly as it is written',
      done: 'Channel deleted',
      error: 'Deletion failed',
      stale: 'Somebody saved first, so nothing was written. The list has just been read again.',
      forbidden: 'Only the channel owner can delete it.',
    },
  },
  nav: {
    redirects: 'Redirects',
    overview: 'Overview',
    dashboard: 'Dashboard',
    statistics: 'Statistics',
    content: 'Content',
    categories: 'Categories',
    items: 'Items',
    publishedMedia: 'Published Media',
    users: 'Users',
    usersList: 'Users',
    inbox: 'Inbox',
    submissions: 'Submissions',
    messages: 'Messages',
    groups: 'Groups',
    monetization: 'Monetization',
    subscriptions: 'Subscriptions',
    coupons: 'Coupons',
    productCatalog: 'Product Catalog',
    sales: 'Sales',
    salesOverview: 'Overview',
    salesSubscriptions: 'Active Subscriptions',
    salesTransactions: 'Transactions',
    salesRevenue: 'Revenue & invoicing',
    salesProducts: 'Product Catalog',
    settings: 'Settings',
    information: 'Information',
    domain: 'Domain',
    external: 'External',
    viewSite: 'View site',
    communication: 'Communication',
    comments: 'Comments',
    campaigns: 'Campaigns',
    audiences: 'Audiences',
    blog: 'Blog',
    pages: 'Pages',
    editSite: 'Edit site',
    accessRequests: 'Access Requests',
    myContent: 'My content',
    newChannel: 'New channel',
  },
  stats: {
    title: 'Statistics',
    subtitle: 'Understand your audience and the performance of your content',
    seeAll: 'See all',
    vsPrevious: 'vs previous period',
    loadError: 'Failed to load statistics. Please try again.',
    filtersRefused: 'The period or the filters chosen were refused. Change them, then try again.',
    unavailable: 'Statistics are not available at the moment.',
    empty: 'No data over this period.',
    loadMore: 'Show more',
    rankingCap: 'The ranking stops at the first 100 contents.',
    filters: {
      button: 'Filters',
      title: 'Filters',
      subtitle: 'Refine the statistics for this channel',
      audience: 'Audience',
      audienceAll: 'Everyone',
      audienceRegistered: 'Registered',
      devices: 'Devices',
      countries: 'Countries',
      noCountries: 'No country over this period',
      contentTypes: 'Content types',
      activeCount: '{count} active',
      noActive: 'No active filter',
      clear: 'Clear',
    },
    contentTypes: {
      video: 'Video',
      audio: 'Audio',
      image: 'Image',
      collection: 'Collection',
      thirdParty: 'External content',
    },
    periods: {
      '7d': '7 days',
      '30d': '30 days',
      '90d': '90 days',
      '12m': '12 months',
    },
    tabs: {
      overview: 'Overview',
      content: 'Content',
      users: 'Users',
      audience: 'Audience',
    },
    kpi: {
      views: 'Plays',
      viewsDesc: 'Total number of content plays on your channel',
      watchTime: 'Play time',
      watchTimeDesc: 'Total time visitors spent playing your content',
      uniqueViewers: 'Unique visitors',
      uniqueViewersDesc: 'Number of distinct visitors who played your content',
      completionRate: 'Completion rate',
      completionRateDesc: 'Average share of content played before visitors stop',
      avgSession: 'Avg. session duration',
      avgSessionDesc: 'Average time spent per playback session over the period',
      activeUsers: 'Active users',
      activeUsersDesc: 'Registered users who played content during the period',
      registeredUsers: 'Registered users',
      registeredUsersDesc: 'Total number of users registered on your channel',
      newUsers: 'New sign-ups',
      newUsersDesc: 'Users who signed up during the period',
    },
    overview: {
      trend: {
        title: 'Audience trend',
        subtitle: 'Plays and unique visitors over the period',
      },
      topContent: {
        title: 'Most viewed content',
      },
      activeUsers: {
        title: 'Most active users',
      },
    },
    users: {
      title: 'Most active users',
      subtitle: 'Click a column to sort',
      rankingCap: 'The ranking stops at the first 100 users.',
      columns: {
        user: 'User',
        watchTime: 'Play time',
        videos: 'Content',
        sessions: 'Sessions',
        completion: 'Completion',
        lastActivity: 'Last activity',
      },
    },
    content: {
      title: 'Most viewed content',
      subtitle: 'Click a column to sort',
      categoriesTitle: 'Top categories',
      deleted: 'Deleted',
      unknownCategory: 'Deleted category',
      columns: {
        title: 'Content',
        views: 'Plays',
        uniqueViewers: 'Unique visitors',
        watchTime: 'Play time',
        completion: 'Completion',
      },
    },
    audience: {
      devices: 'Devices',
      countries: 'Countries',
      trafficSources: 'Traffic sources',
      trafficSourcesSubtitle: 'How your audience reaches your content',
      referrers: 'Top referrers',
      referrersSubtitle: 'Referring domains',
      peakHours: 'Peak hours',
      peakHoursSubtitle: 'Activity by day and hour, in your local time',
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      heatmapLess: 'Less',
      heatmapMore: 'More',
    },
    devices: {
      desktop: 'Desktop',
      mobile: 'Mobile',
      tablet: 'Tablet',
      tv: 'TV',
      bot: 'Bot',
      smart_tv: 'Smart TV',
      game_console: 'Game console',
      unknown: 'Unknown',
    },
    sources: {
      direct: 'Direct',
      social: 'Social',
      organic: 'Organic search',
      referral: 'Referral',
      email: 'Email',
      paid: 'Paid',
      unknown: 'Unknown',
    },
    user: {
      title: 'Playback statistics',
      subtitle: 'Activity of this user across your channel',
      memberSince: 'Member since',
      lastActive: 'Last active',
      plays: 'plays',
      openContent: 'Open content page',
      sessionsSummary: '{count} sessions · {time} · up to {completion}%',
      summary: {
        watchTime: 'Total play time',
        watchTimeDesc: 'Total time this user spent playing content',
        videosWatched: 'Content played',
        videosWatchedDesc: 'Number of distinct content items this user played',
        sessions: 'Sessions',
        sessionsDesc: 'Number of playback sessions by this user',
        completion: 'Avg. completion',
        completionDesc: 'Average share of content this user played before stopping',
        firstSeen: 'First seen',
        lastActivity: 'Last activity',
      },
      activityTitle: 'Activity over time',
      historyTitle: 'Playback history',
      historySubtitle: 'What this user played, session by session',
      topContentTitle: 'Favorite content',
      categoriesTitle: 'Favorite categories',
      devicesTitle: 'Devices',
      locationsTitle: 'Locations',
      history: {
        content: 'Content',
        watched: 'Played on',
        duration: 'Played',
        completion: 'Completion',
        stretches: 'Stretches played',
        device: 'Device',
      },
      empty: 'No playback activity yet',
      historyError: {
        title: 'The viewing history could not be loaded',
        description: 'Something went wrong while loading this user\'s sessions. Please try again.',
        more: 'The rest of the history could not be loaded.',
        retry: 'Retry',
      },
    },
  },
  publish: {
    title: 'Put \'{name}\' online',
    subtitle: 'Your channel becomes visible at {address}',
    summary: 'This page goes live at {address}, with your logo and colors.',
    preview: {
      itemNone: 'No content yet',
      visit: 'See it for real',
    },
    // The price is framed, and the line under the label finally says what was
    // missing most: where the money comes from. The card no longer qualifies
    // anything — the rent is debited from the wallet, and a card on file with no
    // credit pays nothing.
    feeLabel: 'Channel online',
    feeHint: 'Debited from your wallet, every month',
    walletLabel: 'Your wallet',
    // "Usage included" because the figure shown is the shorter of two: the
    // consumption rate the server measures, or the balance divided by the rent.
    // Announcing the flattering one would turn against us at the first cut-off.
    runwayMonths: 'About {months} months left, usage included.',
    runwayDays: 'About {days} days left, usage included.',
    missing: '{amount} short of the first month.',
    insufficient: 'Without a top-up, the first debit will fail: the channel stays online for 14 days of grace, then goes offline.',
    // Neither runway nor "short of": "without a top-up" means nothing to someone
    // who never topped up. This invites, it does not warn.
    neverFunded: 'You have not topped up yet: purchased credit pays your channel subscription and your usage.',
    neverFundedNote: 'Credit pays for everything: the channel subscription, storage, subtitles, and delivery past the {size} free every month.',
    neverFundedNoteNoAllowance: 'Credit pays for everything: the channel subscription, storage, subtitles, and delivery from the first GB.',
    creditPending: 'Payment accepted. Credit sometimes takes a minute to arrive — the channel goes online as soon as it does.',
    creditLate: 'Your payment went through, but the credit has not arrived yet. Try publishing again in a moment.',
    fineprint: 'The first month is debited within 24 hours of the channel going online, then every month, along with the rest of your usage. You can take the channel offline whenever you like: the subscription stops and the month already paid stays yours.',
    contract: "This channel's monthly fee is part of your contract: nothing is charged here.",
    // An exempt channel: the promise made to those that were online before publishing became
    // paid. No price and no wallet to show.
    exempt: 'This channel went online before publishing became paid: it stays free, its custom addresses included. Nothing is charged.',
    blocked: 'Your wallet is empty: your public content is already unavailable. Top it up so your channel answers.',
    published: 'Your channel is online: {address} now answers for everyone.',
    publishedNoAddress: 'Your channel is online.',
    failed: 'The channel could not be put online.',
    stale: 'Somebody changed the channel before you: nothing was written. It has just been read again, you can try once more.',
    topUpTitle: 'Top up your wallet',
    topUpAndPublish: 'Top up and publish',
    later: 'Later',
    confirm: 'Put online',
  },
  dashboard: {
    viewSite: 'View site',
    noDescription: 'No description',
    proBanner: {
      title: 'Upgrade to Pro',
      description: 'to put your site online and unlock all features',
      button: 'Discover',
    },
    hero: {
      status: {
        online: 'Online',
        draft: 'Draft',
        offline: 'Offline',
      },
      stats: {
        medias: 'media',
        categories: 'categories',
        users: 'users',
      },
      copyUrl: 'Copy URL',
      preview: {
        unavailable: 'Preview temporarily unavailable',
        noCapture: 'No preview here: open the site to see your homepage',
        noAddress: 'Preview available once your channel has an address',
        refresh: 'Refresh preview',
        capturedAt: 'Captured on {date}',
      },
      actions: {
        view: 'View site',
        preview: 'Preview',
        edit: 'Edit',
        editSEO: 'Edit information',
        editURL: 'Configure URL',
        editDesign: 'Customize design',
      },
    },
    shapePromotion: {
      message:
        'Your channel has a second piece of content: categories, media list and search are now available.',
      action: 'Create a category',
    },
    banners: {
      pastDue: {
        title: 'Your channel fee could not be charged',
        description: 'Your wallet was empty. Your site stays online until {date}, then it will be taken offline automatically.',
        descriptionNoDate: 'Your wallet was empty. Top it up to keep your site online.',
        button: 'Top up the wallet',
      },
      suspended: {
        title: 'Your site is offline for non-payment',
        description: 'The monthly fee could not be charged. Your visitors see nothing. Top up your wallet to bring it back — your content, categories and settings are untouched.',
        button: 'Top up and republish',
      },
      private: {
        title: 'Private site',
        description: 'Upgrade to a Pro plan to make your site publicly accessible.',
      },
      draft: {
        title: 'Your channel is a draft',
        description: 'Nobody sees it yet: its visitors find a "Coming online soon" page until you put it online. The preview shows it to you as it will be.',
        button: 'Put online',
      },
      // The channel's own fee in its dunning (4.2): read by a billing manager only.
      feeUnpaid: {
        title: 'The channel’s last payment could not be collected',
        description:
          'Without a top-up, the channel goes offline on {date}. | Without a top-up, the channel goes offline on {date}, in 1 day. | Without a top-up, the channel goes offline on {date}, in {n} days.',
      },
      feeSuspended: {
        title: 'The channel is offline for non-payment',
        description: 'A top-up brings it back online on its own until {date}. After that, it has to be published again.',
      },
      feeTopUp: 'Top up',
      takenDown: {
        title: 'Your channel is offline',
        description: 'Its visitors find a "Coming online soon" page. Your content and settings are intact: put it back online whenever you like.',
        button: 'Put back online',
      },
      offline: {
        title: 'Site in maintenance',
        description: 'Your site is currently in maintenance mode and is not accessible to the public.',
        button: 'Put online',
        error: 'Putting the site back online failed.',
        stale: 'Somebody saved first, so nothing was written. The channel has just been read again.',
      },
      publishConfirm: {
        title: 'Publish site',
        description: 'Are you sure you want to publish your site? It will be publicly accessible.',
      },
      onlineConfirm: {
        title: 'Put online',
        description: 'Are you sure you want to put your site back online?',
      },
    },
    setupSteps: {
      title: 'Getting started',
      subtitle: 'Complete these steps to finalize your site setup.',
      completed: 'completed',
      steps: {
        security: {
          title: 'Configure security',
          description: 'Set up authentication settings for your site.',
          actionConfigure: 'Configure',
          actionEdit: 'Edit',
        },
        url: {
          title: 'Add your own domain',
          description: 'Make your site answer on an address you already own.',
          actionSet: 'Add',
          actionEdit: 'Edit',
        },
        category: {
          title: 'Create categories',
          description: 'Organize your content by creating categories.',
          actionCreate: 'Create',
          actionAdd: 'Add',
        },
        media: {
          title: 'Add media',
          description: 'Import your videos, images, and other content.',
          actionImport: 'Import',
          actionAdd: 'Add',
        },
        design: {
          title: 'Customize design',
          description: 'Adapt your site\'s appearance to your brand.',
          action: 'Customize',
        },
        monetization: {
          actionSee: 'See my request',
        },
        users: {
          title: 'Invite users',
          description: 'Add collaborators or members to your site.',
          actionStart: 'Start',
          actionInvite: 'Invite',
        },
      },
    },
    latestCategories: {
      title: 'Latest created categories',
      seeMore: 'See all',
      items: 'item',
      items_plural: 'items',
      emptyState: {
        title: 'No categories',
        description: 'Create your first category to organize your content.',
        buttonText: 'Create a category',
      },
    },
    latestItems: {
      title: 'Latest published content',
      titleSingle: 'Your content',
      seeMore: 'See all',
      add: 'Add content',
      secondUnlocks:
        'A second piece of content turns your channel into a catalogue: categories, media list and search appear on their own.',
      draft: 'Draft',
      paid: 'Paid',
      types: {
        video: 'Video',
        audio: 'Audio',
        image: 'Image',
        document: 'Document',
        playlist: 'Playlist',
        media: 'Media',
        other: 'Other',
      },
      emptyState: {
        title: 'No content',
        description: 'Import your first media to display on your site.',
        buttonText: 'Add content',
      },
    },
    pendingAccessRequests: {
      title: 'Pending access requests',
      seeMore: 'See all',
    },
    latestUsers: {
      title: 'Latest registered users',
      seeMore: 'See all',
      roles: {
        owner: 'Owner',
        staff: 'Staff',
        fullAccess: 'Full access',
      },
      labels: {
        added: 'Added on',
        lastActivity: 'Last activity',
        active: 'Active',
        inactive: 'Inactive',
      },
      emptyState: {
        title: 'No users',
        descriptionWithoutAuth: 'Enable authentication to allow users to register.',
        descriptionWithAuth: 'Invite users to join your site.',
        buttonWithoutAuth: 'Configure authentication',
        buttonWithAuth: 'Invite users',
      },
    },
    statistics: {
      title: 'Statistics',
      subtitle: 'Performance over the last 30 days.',
      seeAll: 'See all',
      videoViews: {
        title: 'Plays',
        subtitle: 'Last 30 days',
        comparison: 'vs previous period',
      },
      watchTime: {
        title: 'Average play time',
        subtitle: 'Per play',
      },
      uniqueViewers: {
        title: 'Unique visitors',
        subtitle: 'Last 30 days',
        progress: 'Completion rate',
      },
      registeredUsers: {
        title: 'Registered users',
        subtitle: 'Total',
      },
      trafficSources: {
        title: 'Traffic sources',
        comingSoon: 'Coming soon',
        noData: 'Data not available',
      },
    },
    tutorials: {
      title: 'Learn in 5 minutes',
      viewAll: 'All tutorials',
      readingTime: '{minutes} min read',
    },
    monetization: {
      title: 'Monetization',
      heading: 'Monetize your content',
      description: 'Generate revenue by selling subscriptions and access to premium content.',
      features: {
        securePayments: 'Secure payments',
        subscriptions: 'Recurring subscriptions',
        promoCodes: 'Promotional codes',
        salesDashboard: 'Sales dashboard',
      },
      offerTitle: 'Sell your content',
      offerDescription: 'Offer your content one-off or by subscription. Opening it takes a short review on our side.',
      featureHints: {
        securePayments: 'Card, transfer, Apple Pay',
        subscriptions: 'Monthly or yearly, renewed on their own',
        promoCodes: 'Discounts and trial periods',
        salesDashboard: 'Revenue, subscribers, transactions',
      },
      activate: 'Activate monetization',
      seeRequest: 'See my request',
      learnMore: 'Learn more',
      enabled: {
        title: 'Monetization',
        subtitle: 'Active',
        subtitleHint: 'Manage sales, subscriptions and promo codes.',
        paymentsPending: 'Payments not connected',
        salesOverview: 'Sales',
        manageSubscriptions: 'Subscriptions',
        managePrices: 'Catalog',
        promoCodes: 'Promo codes',
        settings: 'Settings',
      },
    },
    stats: {
      items: 'Items',
      categories: 'Categories',
      users: 'Users',
      domains: 'Domains',
    },
    quickActions: {
      title: 'Quick actions',
      categories: 'Manage categories',
      items: 'Manage items',
      users: 'Manage users',
      settings: 'Settings',
    },
    info: {
      title: 'Website information',
      domain: 'Primary domain',
      created: 'Created',
      theme: 'Theme',
    },
  },
  create: {
    title: 'Create a channel',
    resumed: 'Draft resumed',
    startOver: 'Start over',
    help: 'Need help?',
    skip: 'Skip',
    success: 'Channel created.',
    lastQuestion: 'Last question',
    questionsLeft: '{count} questions to go',
    questionsLeftOne: 'One question to go',
    errors: {
      creation: 'The channel could not be created.',
      draft: 'The channel was created but stayed online.',
      nameRequired: 'Your channel needs a name first.',
      addressUnavailable: 'That address is no longer available. Please pick another one.',
      notMigrated: 'The channel could not be created: your organization is still served by the previous platform. Creating a channel opens once it has been migrated.',
      handleTaken: 'The channel could not be created: “{handle}” is already another channel\'s handle. Change the address, then try again.',
      conflict: 'The channel could not be created. Either “{handle}” is already another channel\'s handle — change the address —, or your organization is still served by the previous platform, and creating a channel opens once it has been migrated.',
      changeAddress: 'Change the address',
      partial: 'Your channel exists, but {parts} could not be applied.',
      accessNotApplied: 'the access settings',
      logoNotApplied: 'the logo',
      catalogueNotSeeded: 'the catalogue',
    },
    welcome: {
      notMigrated: 'Your organization is still served by the previous platform: creating a channel here opens once it has been migrated.',
      title: 'Create your channel',
      subtitle: 'A channel is your video site: an address of your own, your catalogue, your colours.',
      meta: '6 questions · two minutes',
      stepsLabel: 'In three steps',
      step1: 'The layout',
      step1Hint: 'The shape of your catalogue, then a template.',
      step2: 'Your identity',
      step2Hint: 'A name, a sentence, a colour.',
      step3: 'Your content',
      step3Hint: 'What goes live right away, and who gets to see it.',
      outcomeLabel: 'At the end',
      outcomeAddress: 'your-channel.{domain}',
      outcome: 'An address to share. The site stays a draft until you publish it.',
      start: 'Get started',
      fine: 'Nothing is final: everything can be changed later.',
    },
    detect: {
      rich: '<strong>{assets} media</strong> in your space, {sorted} {folders}.',
      richOne: '<strong>1 media item</strong> in your space, filed in a folder.',
      richSorted: '<strong>{count}</strong> of them filed',
      richSortedOne: '<strong>1</strong> of them filed',
      richFolders: 'across {count} folders',
      richFoldersOne: 'in 1 folder',
      unsorted: '<strong>{assets} media</strong> in your space, none filed in a folder.',
      unsortedOne: '<strong>1 media item</strong> in your space, not filed in any folder.',
      light: '<strong>{assets} media</strong> in your space.',
      empty: 'Your space is empty — <strong>we build the site first</strong>, you add your videos afterwards.',
    },
    shape: {
      title: 'What are you publishing?',
      titleEmpty: 'How much do you plan to publish?',
      subtitle: 'This decides the blocks placed on your home page. Nothing is locked: the site adapts as your catalogue grows.',
      suggested: 'suggested',
      suggestedFrom: 'Based on the {assets} media in your space.',
      single: 'A single piece of content',
      singleHint: 'A film, a talk, a webinar',
      small: 'A small selection',
      smallHint: 'A handful of items, no filing needed',
      catalog: 'A catalogue',
      catalogHint: 'Organised in categories, with search',
    },
    template: {
      title: 'Choose a template',
      colourNote: 'The template colour. You can change it at the next step.',
      comingSoon: 'Coming soon',
      comingSoonNote: 'Learning mode is on its way. This template will open once it ships.',
    },
    identity: {
      title: 'Your identity',
      subtitle: 'Three fields, and the site on the right becomes yours.',
      name: 'Site name',
      address: 'Address:',
      handle: 'Handle:',
      domainNote: 'Already own a domain name? You will be able to connect it from the settings, once the site is created.',
      pitch: 'In one sentence, what will people find here?',
      pitchHint: 'It becomes your home page subtitle and the description shown when the link is shared.',
      logo: 'Logo',
      optional: 'optional',
      logoHint: 'It appears in the header of your site, next to the name.',
      color: 'Main colour',
      colorCustom: 'Another colour',
      checking: 'checking…',
      available: 'available',
      addressRefused: 'This address is not available.',
      chooseAnother: 'Pick another address: your site keeps its name, only the address has to be unique.',
      handleLabel: 'Address of the site',
      addressPinned: 'The address no longer follows the site name.',
      addressUseName: 'Use {address}',
      colorHint: 'Pre-filled from the template.',
    },
    content: {
      title: 'Your content',
      subtitle: 'Each folder becomes a category. Everything is selected — uncheck what should stay out.',
      subtitleEmpty: 'Nothing in your space yet. Start now — the import runs while you carry on.',
      drop: 'Drop your first media here',
      dropHint: 'Videos, images, audio — up to {count} files here',
      uploadRunning: '{done} of {count} sent. Sending keeps running while you answer, and your media join the channel once they are ready.',
      uploadCapped: 'Only {count} files can be sent from here — add the rest from your media library afterwards.',
      later: "I'll do it later",
      loading: 'Reading your media library…',
      folderCount: '{count} folders',
      folderCountOne: '{count} folder',
      itemsLabel: '{count} media',
      itemsLabelOne: '{count} media',
      categoriesLabel: '{count} categories',
      categoriesLabelOne: '{count} category',
      selectAll: 'Select all',
      unselectAll: 'Unselect all',
      consequence: '<strong>{items}</strong> published in <strong>{categories}</strong>.',
      consequenceOne: '<strong>{items}</strong> published in <strong>{categories}</strong>.',
      consequenceNone: 'Nothing selected: your channel is created <strong>empty</strong>, and stays a draft.',
      unsorted: 'Media outside any folder',
      unsortedNote:
        'Media outside a folder are published without a category: they show in the catalogue and in search, but fill no row on the home page.',
      consequenceLoose: '<strong>{items}</strong> published, with no category.',
      consequenceLooseOne: '<strong>{items}</strong> published, with no category.',
      consequenceMixed:
        '<strong>{items}</strong> published: {sorted} across {categories}, {loose} without one.',
      subtitleSingle: 'Which one are you publishing?',
      pickLabel: 'The content to publish',
      showMore: 'Show more ({count})',
      consequenceSingle: 'Your home page will open on this content. You can publish more later.',
      consequenceNoPick:
        'Nothing chosen yet: your channel is created <strong>empty</strong>, and stays a draft.',
      noMedia:
        'Nothing publishable in your space yet. Your channel is created empty and you add your content from the media library.',
      noFolders:
        'Your media are not sorted into folders yet, so there is nothing to turn into categories. Your channel is created empty and you add content from the media library.',
    },
    access: {
      title: 'Who can see your site?',
      subtitle: 'First of two access questions. This one is not about money.',
      open: 'Everyone',
      openHint: 'The site is public, findable by search engines and by models',
      account: 'People with an account',
      accountHint: 'Visitors must sign in to reach the content',
      free: 'Anyone can create an account',
      freeHint: 'Open sign-up, you keep control over the rights',
      invite: 'By invitation only',
      inviteHint: 'You decide who gets in, one by one or by group',
      openConsequence: 'A public site is the only one that can be found — by search engines as much as by language models.',
      freeConsequence: 'The catalogue stays visible and indexable, only the content asks for an account.',
      inviteConsequence: 'Your content will not be indexed: nobody can see the home page without an account.',
    },
    money: {
      title: 'Is it paid?',
      subtitle: 'Second access question, independent from the first.',
      free: 'Everything is free',
      freeHint: 'No payment, no plans',
      freemium: 'Part free, the rest paid',
      freemiumHint: 'Enough to make people want more before asking them to pay',
      accountsNote: 'Buying needs an account: we switch visitor accounts on for you. Your site stays browsable without signing in.',
      reviewNote: 'Selling opens after a short review, which you request from your channel\'s Settings › Monetization once your site exists.',
      sellLater: 'Your channel can sell its content later — purchase, rental or subscription — from its sales screens. Nothing is made paid here.',
    },
    preview: {
      withAnswers: 'Preview · with your answers',
      withTemplate: 'Template {name} · with your answers',
      hintTitle: 'Your site, taking shape.',
      hintText: 'It changes with every answer; the videos shown are not yours yet.',
      desktop: 'Desktop',
      mobile: 'Mobile',
      tag: 'preview',
      ghostEmpty: 'Your content will appear here.',
      ghostUnselected: "You haven't chosen your content yet.",
      ghostHint: 'On the published site, a section without content is not displayed at all.',
    },
    ready: {
      title: 'Your site is ready to be created',
      subtitle: 'Check what is about to be set up. Your channel is created as a draft: nobody sees it until you put it online.',
      create: 'Create my channel',
      receiptItems: '{items} media',
      receiptItemsOne: '{items} media',
      receiptCategories: '{categories} categories',
      receiptCategoriesOne: '{categories} category',
      receiptEmpty: 'Nothing yet',
      summaryTemplate: 'Template',
      summaryAddress: 'Address',
      summaryAccess: 'Access',
      summaryContent: 'Content',
      summaryContentValue: '{items} in {categories}',
      summaryContentSingle: 'One content',
      summaryContentImporting: '{count} being sent',
      summaryContentEmpty: 'Nothing yet',
      done: 'Your channel exists',
      doneSubtitle: 'It is a draft: nobody can see it until you put it online.',
      onlineSubtitle: 'Anyone with the address can watch it.',
      address: 'Your address',
      addressManage: 'Manage',
      copy: 'Copy',
      copied: 'Copied',
      missingContent: 'One thing is missing before you can publish: content.',
      importArriving: 'Your {count} media are still being prepared. They will join your channel on their own, with nothing more to do.',
      importPending: 'Your {count} media are still being prepared. Add them to your channel once they are ready.',
      publishable: 'Your catalogue is in place. Publishing is the only thing left.',
      visit: 'View my site',
      publish: 'Put my channel online',
      published: 'Your channel is online.',
      online: 'Your channel is online: the address above works for everyone.',
      leftover: '{count} media could not be added. Open the media library to publish the rest.',
      leftoverTruncated: 'Your selection was more than a creation publishes in one go, and the rest was not added. Publish it from the media library.',
      leftoverLink: 'Add the rest',
      addContent: 'Add my content',
      openEditor: 'Open the editor',
      monetization: 'Set up selling',
      dashboard: 'Go to the channel',
    },
  },
  categories: {
    conflict: 'Somebody saved this category before you: nothing was written. The screen has just been re-read — make your change again and save.',
    saveSuccess: 'Category saved',
    saveError: 'Failed to save the category',
    moveError: 'This category could not be moved',
    duplicateSlug: 'A category already answers at that address. Pick another name.',
    slugTaken: 'Another category already uses that address. Pick another one.',
    maskedByParent: 'Hidden (parent switched off)',
    deleteDone: 'Category deleted — {promoted} subcategory(ies) promoted, {unfiled} item(s) now filed nowhere.',
    itemCount: '{count} item(s)',
    title: 'Categories',
    description: 'Reorganize your categories by drag and drop to set their order or hierarchy.',
    btnCreateCategory: 'Create a category',
    searchPlaceholder: 'Search categories...',
    deleteTitle: 'Delete category',
    deleteMessage: 'Are you sure you want to delete "{name}"? It will be removed from the site.',
    deleteKeepsItems: 'Its content will not be deleted: it will simply have no category. | Its {count} contents will not be deleted: they will simply have no category.',
    deleteMovesChildren: 'Its subcategory moves up one level. | Its {count} subcategories move up one level.',
    addSubcategory: 'Add a subcategory',
    emptySubcategories: 'No subcategories',
    inline: {
      namePlaceholder: 'Subcategory name...',
    },
    empty: {
      title: 'No categories',
      description: 'Create your first category to start organizing your content.',
      noResults: 'No categories found',
      noResultsDescription: 'Try adjusting your filters or search terms.',
    },
    stats: {
      total: 'Total',
      active: 'Active',
      items: 'Filed items',
    },
    filters: {
      all: 'All',
      active: 'Active',
      inactive: 'Inactive',
    },
    table: {
      title: 'Title',
      description: 'Description',
      parent: 'Parent',
      root: '— (top level)',
      items: 'Items',
      status: 'Status',
    },
    emptyState: {
      title: 'No categories created',
      description: 'Click "Create a category" to add your first category.',
    },
    danger: {
      title: 'Danger zone',
      description: 'Actions that remove things from the site',
      deleteTitle: 'Delete this category',
      deleteDescription: 'Once deleted, the category leaves the site. Its items are not deleted: they are left with no category, and its subcategories move up one level.',
      deleteButton: 'Delete category',
    },
    newCategory: 'New category',
    saveCategoryFirst: 'Save the category to enable this tab',
    createSuccess: 'Category created successfully',
    createError: 'Failed to create category',
    updateSuccess: 'Category updated successfully',
    updateError: 'Failed to update category',
    deleteSuccess: 'Category deleted successfully',
    deleteError: 'Failed to delete category',
    invalidForm: 'Please fill in all required fields',
    tabs: {
      information: 'Information',
      items: 'Items',
      access: 'Access rights',
    },
    sections: {
      information: {
        title: 'Category information',
        description: 'Configure the name, slug, and description of the category.',
      },
      seo: {
        title: 'SEO',
        description: 'Optimize how this category appears in search engine results.',
      },
      appearance: {
        title: 'Appearance',
        description: 'Customize the visual aspects of your category.',
      },
      visibility: {
        title: 'Visibility settings',
        description: 'Control where and how your category appears.',
      },
    },
    fields: {
      name: 'Category name',
      namePlaceholder: 'e.g. Tutorials',
      slug: 'URL slug',
      slugPlaceholder: 'tutorials',
      slugHint: 'The category\'s address on the site (/categories/your-slug/). Changing it leaves a redirect from the old one.',
      description: 'Description',
      descriptionPlaceholder: 'Describe this category...',
      seoTitle: 'SEO title',
      seoTitlePlaceholder: 'Title shown in search engine results',
      seoTitleHint: 'Recommended: 50–60 characters. Leave empty to use the category name.',
      seoDescription: 'SEO description',
      seoDescriptionPlaceholder: 'Short description shown in search engine results',
      seoDescriptionHint: 'Recommended: 150–160 characters.',
      parentCategory: 'Parent category',
      parentCategoryPlaceholder: 'None (root)',
      icon: 'Icon',
      iconHelper: 'Recommended size: 64x64. Only used in the page builder "Categories" blocks.',
      thumbnail: 'Thumbnail',
      thumbnailHelper: 'Square picture, 600x600 recommended — the category card is square.',
      banner: 'Banner image',
      bannerHelper: 'Recommended size: 1320x400. Shown in the header according to the banner style set for the category page in the editor.',
      color: 'Accent color',
      colorPlaceholder: 'No color selected',
      colorHelper: "The colour of the category page's title and of its card. Without one, the title follows the site's theme and the card its primary colour.",
      isActive: 'Active category',
      isActiveHelper: 'The category is visible on your site',
      isOnHomepage: 'Display on homepage',
      isOnHomepageHelper: 'Display this category on the site homepage',
      isOnNavbar: 'Display in navigation bar',
      isOnNavbarHelper: 'Include this category in the main navigation',
    },
    validation: {
      nameRequired: 'Category name is required',
      nameTooShort: 'Name must be at least 2 characters',
      nameTooLong: 'Name must not exceed 100 characters',
      nameNoAddress: 'The name needs at least one letter or digit: the category\'s address is made from it.',
      slugRequired: 'URL slug is required',
      slugInvalid: 'Slug can only contain lowercase letters, numbers, and hyphens',
    },
    display: {
      title: 'Display',
      description: 'Configure how items are displayed in this category.',
      bannerItem: 'Banner content',
      bannerItemHint: 'The video this category page opens on',
      bannerItemAuto: 'The first item that plays',
      useManualOrdering: 'Manual ordering',
      useManualOrderingHint: 'Drag and drop items to set their order',
      automaticOrderBy: 'Sort by',
      automaticOrderByPlaceholder: 'Select a sort order',
      orderOptions: {
        created_at: 'Creation date',
        published_at: 'Publication date',
        title: 'Title (A-Z)',
        '-title': 'Title (Z-A)',
        '-created_at': 'Newest first',
        '-published_at': 'Latest published',
      },
    },
    itemsTab: {
      manualOrderNotEditable: 'The hand-made order already recorded is honoured on the site, but it cannot be rearranged from this screen.',
      title: 'Items',
      description: 'Manage the content included in this category.',
      emptyTitle: 'No items in this category',
      emptyDescription: 'Add content to display them in this category.',
      btnAdd: 'Add items',
      addExisting: 'Add existing items',
      importFromMediasbox: 'Import from MediasBox',
      addItems: 'Add items',
      restrictedAlert: '{count} item(s) have restricted access',
      restrictedAlertLink: 'Manage access in the Access tab',
      itemCount: {
        zero: '0 items',
        one: '1 item',
        other: '{count} items',
      },
      removeDialog: {
        title: 'Remove item',
        description: 'What would you like to do with "{name}"?',
        removeFromCategory: 'Remove from category',
        deleteItem: 'Delete item',
      },
      toast: {
        itemsAdded: 'Items added to category',
        itemRemoved: 'Item removed from category',
        itemDeleted: 'Item deleted',
        reorderSaved: 'Item order saved',
        reorderFailed: 'Failed to save item order',
        addFailed: 'Failed to add items',
        removeFailed: 'Failed to remove item',
        deleteFailed: 'Failed to delete item',
      },
      orderBy: {
        updatedAsc: 'Last change (oldest first)',
        updatedDesc: 'Last change (newest first)',
        position: 'Manual (drag & drop)',
        nameAsc: 'Name (A-Z)',
        nameDesc: 'Name (Z-A)',
        createdAsc: 'Created (oldest first)',
        createdDesc: 'Created (newest first)',
        publishedAsc: 'Published (oldest first)',
        publishedDesc: 'Published (newest first)',
      },
      table: {
        title: 'Title',
        published: 'Published',
        actions: '',
      },
      itemPickerModal: {
        title: 'Add existing items',
        searchPlaceholder: 'Search items...',
        noItems: 'No items available to add.',
        noResults: 'No items match your search.',
        addSelected: 'Add ({count})',
        column: {
          title: 'Title',
          type: 'Type',
          status: 'Status',
        },
      },
    },
    accessTab: {
      expired: 'Expired',
      categoryOnly: 'This access covers this category alone, not its sub-categories.',
      subcategoriesTitle: 'Its sub-category is opened separately, from its own access screen: | Its sub-categories are opened separately, each from its own access screen:',
      fullAccessTitle: 'Full access',
      fullAccessHint: 'These users and groups see every item of the channel, these included. Full access is withdrawn from their own page.',
      fullAccessGroup: 'Group · {count} member(s)',
      fullAccessChip: 'Full access',
      grantCounts: 'Open to {groups} group(s) and {members} member(s).',
      moreItems: 'More items are filed here — this list shows the first page.',
      title: 'Restricted-access content',
      description: 'These items are only visible to the authorized users and groups listed below.',
      noRestrictedItems: 'No restricted items in this category. Access management applies to restricted items only.',
      authorizedTitle: 'Authorized users & groups',
      authorizedDescription: 'Users and groups with access to the {count} restricted item(s).',
      btnGrantAccess: 'Grant access',
      btnRevoke: 'Revoke',
      noAuthorized: 'No users or groups have access to restricted items yet.',
      usersTitle: 'Users',
      groupsTitle: 'Groups',
      itemAccessCount: '{count}/{total} restricted items',
      memberCount: '{count} member(s)',
      revokeDialogTitle: 'Revoke access',
      revokeDialogMessage: 'Revoke access for "{name}" from all restricted items in this category?',
      revokeSuccess: 'Access revoked successfully',
      revokeError: 'Failed to revoke access',
      levels: {
        public: 'Public',
        authenticated: 'Authenticated',
        restricted: 'Restricted',
      },
      table: {
        title: 'Title',
        accessLevel: 'Access level',
      },
      modal: {
        title: 'Manage category access rights',
        usersTab: 'Users',
        groupsTab: 'Groups',
        searchUsers: 'Search users...',
        searchGroups: 'Search groups...',
        userColumn: 'User',
        groupColumn: 'Group',
        membersColumn: 'Members',
        noUsers: 'No users found',
        noGroups: 'No groups found',
        grantSuccess: 'Access rights updated successfully',
        grantError: 'Failed to update access rights',
      },
    },
    addItemsModal: {
      title: 'Add items to category',
      alreadyInCategory: '{count} content(s) already in this category — they will be skipped.',
      tabs: {
        channelContent: 'Channel content',
        mediaLibrary: 'Media library',
      },
      channelContent: {
        searchPlaceholder: 'Search items...',
        noItems: 'No items available to add.',
        noResults: 'No items match your search.',
        addSelected: 'Add selected ({count})',
        column: {
          title: 'Title',
          type: 'Type',
          status: 'Status',
        },
      },
      mediaLibrary: {
        description: 'Select assets from your media library to add as new content or link existing channel items to this category.',
        browseButton: 'Browse media library',
        badgeInChannel: 'Already in channel',
        badgeInCategory: 'Already in category',
        existingInfo: '{count} content(s) already in your channel — will be added to this category directly.',
        newInfo: '{count} new content(s) — will be published as new items.',
        goBack: 'Change selection',
        publishBtn: 'Publish',
        addBtn: 'Add to category',
        addAndPublishBtn: 'Add & publish',
      },
      toast: {
        success: '{count} item(s) added to category',
        successMixed: '{existing} existing + {created} new item(s) added',
        error: 'Failed to add items',
      },
    },
    editCategory: {
      btnBack: 'Back to categories',
      btnAddContent: 'Add content',
      information: 'Information',
      access: 'Access rights',
      itemsTitle: 'Content',
      btnDelete: 'Delete this category',
      info: {
        name: 'Name',
        description: 'Description',
        url: 'URL on the site',
        parentDescription: 'If no selection is made below, this category will be at the root.',
        parentLabel: 'Parent category',
        visibility: {
          title1: 'Category visibility',
          subtitle1: 'By disabling this option, this category will no longer be visible on the site.',
          title2: 'Show on homepage',
          subtitle2: 'By disabling this option, this category will no longer be visible on the site homepage.',
          title3: 'Show in navigation bar',
          subtitle3: 'By disabling this option, this category will no longer be visible in the navigation bar.',
        },
      },
      accessRights: {
        description1: 'The access rights defined here will apply to all videos',
        description2: 'Unlisted',
        description3: 'in this category.',
        noItem: 'No restricted content in this category.',
        btnAddAccessRight: 'Add an access right',
        user: 'User',
        group: 'Group',
        title1: 'User / Group',
        title2: 'Type',
      },
      items: {
        title: 'Content',
        description: 'Manage the content included in this category.',
        emptyState: 'No content in this category',
        btnAdd: 'Add content',
        btnDeleteSelected: 'Delete selection',
        btnAutorizeUsers: 'Authorize users',
        noAccessItems: 'items have restricted access',
        views: 'views',
        sampleVideo1: 'Introduction to Vue.js',
        samplePlaylist1: 'API Project Documentation',
      },
    },
  },
  form: {
    sortNameAsc: 'Name (A-Z)',
    sortNameDesc: 'Name (Z-A)',
    sortDateAsc: 'Oldest first',
    sortDateDesc: 'Newest first',
  },
  editor: {
    heroFields: {
      mediaType: 'Media type',
      image: 'Image',
      video: 'Video',
      videoUrl: 'Video address (MP4)',
      videoUrlHint: 'Played on a loop, muted, beside the text.',
    },
    blocksSaveError: 'The blocks could not be saved.',
    blocksReorderError: 'The block order could not be saved.',
    blocksStale: 'Somebody changed this page in the meantime. Nothing was saved — the page was reloaded.',
    canvas: {
      frameTitle: 'Preview of the page being edited',
      unavailableTitle: 'Preview unavailable',
      noHost:
        "This channel does not answer on any address yet: connect one in the Domain tab to preview its pages. Meanwhile, blocks are added, configured and saved as usual.",
      unavailable:
        "The preview could not be prepared. Blocks are added, configured and saved as usual; to see the result, open the channel's site.",
      openSite: 'Open the site',
      blockCount: 'no block | 1 block on this page | {count} blocks on this page',
      loadFailed: 'The preview could not be updated: it shows the last version drawn.',
      bandRefused: 'This block cannot be shown as it stands — {reason}',
      savedMenus: 'Edited menus appear in the preview once they are saved.',
    },
    viewAs: {
      label: 'View as',
      stranger: 'Anonymous visitor',
      sharedPassword: 'Visitor with the password',
      member: 'Member',
      hint: 'The preview shows the page as this visitor sees it. The member here holds nothing but the membership: no purchase, subscription, group or granted access — paid content stays locked.',
    },
    templatePages: {
      categoryList: 'Categories page',
      categoryDetail: 'Category page',
      itemDetail: 'Content page',
    },
    // The settings of the three pages the channel draws out of its catalogue.
    templatePageSettings: {
      pages: {
        categoryList: { title: 'Categories page', description: 'How the list of categories is shown' },
        categoryDetail: { title: 'Category page', description: 'How one category\'s page looks' },
        itemDetail: { title: 'Item page', description: 'How one item\'s page is laid out' },
      },
      categoryDisplay: 'Display mode',
      bannerStyle: 'Banner style',
      bannerStyleHint: 'The same style for every category. Each category then provides its picture, or the item it features.',
      bannerStyles: {
        simple: 'Simple',
        simpleHint: 'The category\'s title and description',
        framed: 'Framed',
        framedHint: 'The category\'s picture in a frame',
        hero: 'Hero',
        heroHint: 'The category\'s picture, full width',
        featured: 'Featured',
        featuredHint: 'One item, full size, with the grid over it: the one chosen on the category, else the first',
      },
      rows: 'Sub-category rows',
      rowsHint: 'Rows always start at the content\'s edge. This setting decides whether they stop at the page\'s width or run off screen.',
      itemStyle: 'Page style',
      options: {
        grid: 'Grid',
        gridHint: 'Shown as a grid',
        slider: 'Carousel',
        sliderHint: 'Horizontal scrolling',
        contained: 'Within the page',
        edge: 'Off screen',
        cinematic: 'Cinematic',
        showcase: 'Showcase',
        spotlight: 'Centred player',
        classic: 'Classic',
        minimal: 'Minimal',
        twoColumn: 'Two columns',
      },
      // Which kind of publication the canvas draws the item page with; it saves nothing.
      previewKind: 'Content type',
      previewKindContent: 'Content',
      previewKindCollection: 'Collection',
      previewKindHint: 'Choose which kind of page to preview',
    },
    settingsSaveError: 'The site settings could not be saved.',
    blockDeleteError: 'The block could not be deleted — it is still live.',
    pageErrors: {
      addressReserved: 'This address is kept by the channel’s own site — its catalogue, its blog, its sign-in, its legal pages… A page there would never be shown: pick another address.',
      addressTaken: 'This address already belongs to another page or to a published item of the channel. Pick another address, or change the other one first.',
      refused: 'This change was refused. A homepage, for instance, must stay public: change its access or pick another homepage.',
      load: 'The pages could not be loaded.',
      create: 'The page could not be created.',
      save: 'The page settings could not be saved.',
      delete: 'The page could not be deleted.',
      duplicate: 'The page could not be duplicated.',
      publish: 'The page could not be published.',
      unpublish: 'The page could not be unpublished.',
      setHomepage: 'The page could not be made the homepage.',
      stale: 'Somebody changed this page in the meantime. Nothing was saved — reload and try again.',
    },
    itemPicker: {
      // Generic labels: the Media block lets any content of the channel be
      // picked, whatever its type.
      // The empty state does not mean "nothing shows": the block then follows
      // the most recently published content. Not saying so made creators think
      // their block was mute while their page worked perfectly well.
      emptyTitle: 'The most recent content',
      emptyDescription: 'Click to pick a specific content instead',
      dialogTitle: 'Select a content',
      add: 'Add content',
      select: 'Select content',
      selectedCount: 'none selected | 1 selected | {count} selected',
      missing: 'Content deleted',
      searchPlaceholder: 'Search for a content...',
      noResults: 'No content found',
      noItems: 'No content available',
      // Labels for the pickers restricted to a single type (Hero, Chapters,
      // Transcript): saying "content" where only videos are listed would send
      // people looking for a podcast that will never show up.
      emptyTitleVideo: 'The most recent video',
      emptyDescriptionVideo: 'Click to pick a specific video instead',
      dialogTitleVideo: 'Select a video',
      searchPlaceholderVideo: 'Search for a video...',
      noResultsVideo: 'No videos found',
      noItemsVideo: 'No videos available',
      change: 'Change',
      paid: 'Paid',
      free: 'Free',
      onRequest: 'On request',
      paidNoPrice: 'Paid · no price set',
      filterAll: 'All',
      filterVideo: 'Videos',
      filterAudio: 'Audio',
      filterImage: 'Images',
      filterCollection: 'Collections',
      typeVideo: 'Video',
      typeAudio: 'Audio',
      typeImage: 'Image',
      typeCollection: 'Collection',
    },
    mediaPicker: {
      selectMedia: 'Choose a picture',
      library: 'Library',
      upload: 'Upload',
      change: 'Change',
      remove: 'Remove',
      video: 'Video',
      dragOrClick: 'Drag & drop or click to upload',
      fileTooLarge: 'File too large (max {size} MB)',
      invalidFileType: 'Invalid file type',
      uploadFailed: 'Upload failed',
      libraryNoPicture: 'This media has no picture that can be used.',
      savedNoPreview: 'Image saved',
    },
    untitledPage: 'Untitled page',
    editorTitle: 'Editor',
    pagePublished: 'Page published',
    pageDraft: 'Page draft',
    draft: 'Draft',
    discard: 'Discard changes',
    discardTitle: 'Discard your changes?',
    discardConfirm: 'Everything not saved yet is lost: the blocks, the look, the menus and the page settings.',
    discardConfirmAction: 'Discard all',
    leavePageTitle: 'Leave this page?',
    leavePageConfirm: 'The blocks and page settings you have not saved will be lost.',
    leavePageAction: 'Leave without saving',
    blockCategories: {
      vod: 'VOD / Video',
      layout: 'Layout',
      content: 'Content',
      interactive: 'Interactive',
      other: 'Other',
    },
    leaveEditor: {
      title: 'Leave the editor?',
      text: 'Everything not saved yet is lost: the blocks, the look, the menus and the page settings.',
      stay: 'Stay',
      leave: 'Leave without saving',
    },
    // A block's panel: its two tabs, and every setting of the Style tab.
    blockPanel: {
      tabs: { content: 'Content', style: 'Style' },
      content: {
        texts: 'Text',
        title: 'Title',
        subtitle: 'Subtitle',
        description: 'Description',
        mediaType: {
          label: 'Media type',
          all: 'All',
          videos: 'Videos',
          photos: 'Photos',
          hint: 'Videos: videos and video collections. Photos: pictures and albums.',
        },
      },
      style: {
        layout: 'Layout',
        typography: 'Typography',
        elements: {
          title: 'Title',
          subtitle: 'Subtitle',
          description: 'Description',
          body: 'Body text',
          ctaPrimary: 'Primary button',
          ctaSecondary: 'Secondary button',
        },
        questionColor: 'Question colour',
        answerColor: 'Answer colour',
        alignment: 'Alignment',
        horizontal: 'Horizontal',
        vertical: 'Vertical',
        buttons: 'Buttons',
        align: {
          left: 'Left',
          center: 'Centre',
          right: 'Right',
          top: 'Top',
          bottom: 'Bottom',
          likeContent: 'Like the content',
        },
        background: 'Background',
        bgColor: 'Colour',
        bgMedia: 'Media',
        bgGradient: 'Gradient',
        pickColor: 'Choose a colour',
        mediaImage: 'Picture',
        mediaVideo: 'Video',
        videoUrl: 'Video address (MP4)',
        videoUrlHint: 'A full address to an MP4 file, played in a loop and muted.',
        videoStoredFileHint: 'A video file stored with this band plays behind it. An address typed here replaces it.',
        videoPoster: 'Picture shown before playback',
        videoSourceChannel: 'From the channel',
        videoSourceUrl: 'By address',
        videoFromChannelHint:
          'One of the channel\'s videos, played in a loop and muted, to visitors allowed to watch it. The others see the picture below, or the video\'s own. Not played in the editor.',
        fitCover: 'Fill',
        fitContain: 'Contain',
        overlayEnable: 'Enable overlay',
        opacity: 'Opacity',
        overlayOpacity: 'Overlay opacity',
        overlayColor: 'Overlay colour',
        gradientOverlayNote: 'The gradient already tints the background. To darken it, choose darker colours above.',
        size: 'Section size',
        height: 'Height',
        padding: 'Inner spacing',
        heights: { auto: 'Auto', small: 'Small', medium: 'Medium', large: 'Large', fullscreen: 'Full screen' },
        paddings: { none: 'None', small: 'Small', medium: 'Medium', large: 'Large' },
      },
      textStyle: {
        font: 'Font',
        themeFont: 'Default (theme)',
        size: 'Size',
        sizes: {
          sm: 'Small',
          base: 'Normal',
          lg: 'Large',
          xl: 'XL',
          '2xl': '2XL',
          '3xl': '3XL',
          '4xl': '4XL',
          '5xl': '5XL',
          '6xl': '6XL',
          '7xl': '7XL',
          '8xl': '8XL',
        },
        bold: 'Bold',
        italic: 'Italic',
        caseAsTyped: 'As typed',
        caseUpper: 'ALL CAPITALS',
        caseCapitalize: 'Capitalise Each Word',
        alignLeft: 'Align this element left',
        alignCenter: 'Centre this element',
        alignRight: 'Align this element right',
        appearance: 'Appearance',
        shapes: { filled: 'Filled', tonal: 'Tonal', outlined: 'Outlined', text: 'Text' },
        themeColour: 'Theme colour',
        buttonColour: 'Button colour',
        buttonTextColour: 'Text colour',
        buttonTextColourAuto: 'Automatic',
        lineHeight: 'Line height',
        letterSpacing: 'Letter spacing',
      },
      banner: {
        media: 'Media',
        mediaImage: 'Picture',
        mediaVideo: 'Video',
        videoUrl: 'Video address (MP4)',
        videoUrlHint: 'Played in a loop, muted, behind the words.',
        videoPoster: 'Picture shown while it loads',
        videoPosterHint: 'A full address to a picture. Empty: the Style tab\'s picture.',
      },
      newsletter: {
        invitation: 'Invitation',
        buttonText: 'Button text',
        buttonHint: 'The button leads to creating an account on the channel: no address is collected without one.',
        privacy: 'Privacy',
        showPrivacy: 'Show the privacy notice',
        privacyText: 'Notice text',
        privacyPlaceholder: 'By signing up, you agree to our privacy policy.',
      },
    },
    // The picker's starters (`mocks/blockPresets.ts`), by block type: the previous builder's examples.
    presets: {
      hero_section: {
        centered_video_background: 'Centered – Video Background',
        centered_image_background: 'Centered – Image Background',
        centered_gradient_background: 'Centered – Gradient Background',
        centered_media_below: 'Centered + Media Below',
        split_left_text_image: 'Split Left – Text & Image',
        split_right_image_text: 'Split Right – Image & Text',
        overlay_bottom_content: 'Overlay – Bottom Content',
        featured_video_netflix_style: 'Featured Video – Netflix Style',
        compact_split_left: 'Compact Split Left',
        compact_split_right: 'Compact Split Right',
      },
      banner: {
        image_banner: 'Image Banner',
        video_banner: 'Video Banner',
        banner_with_text: 'Banner with Text',
      },
      search: {
        hero_search: 'Hero Search',
        carte_flottante: 'Carte flottante',
        minimal: 'Minimal',
      },
      custom_html: {
        iframe: 'Iframe',
      },
      continue_watching: {
        reprendre_la_lecture: 'Reprendre la lecture',
      },
      blog: {
        blog_grid_standard: 'Blog Grid – Standard',
        blog_carousel: 'Blog Carousel',
        featured_articles: 'Featured Articles',
        blog_list: 'Blog List',
        compact_grid: 'Compact Grid',
      },
      categories: {
        grid_cards: 'Grid Cards',
        grid_minimal: 'Grid Minimal',
        grid_overlay: 'Grid Overlay',
        grid_poster: 'Grid Poster',
        carousel: 'Carousel',
        carousel_poster: 'Carousel Poster',
        list_horizontal: 'List Horizontal',
        list_detailed: 'List Detailed',
      },
      content_block: {
        texte_image_droite: 'Texte + Image (droite)',
        texte_image_gauche: 'Texte + Image (gauche)',
        texte_seul: 'Texte seul',
        image_seule: 'Image seule',
      },
      faq: {
        faq_simple_accordion: 'FAQ Simple – Accordion',
        faq_premium_cartes_bord_es: 'FAQ Premium – Cartes bordées',
        faq_grille_cartes: 'FAQ Grille – Cartes',
        faq_long_deux_colonnes: 'FAQ Long – Deux colonnes',
      },
      features: {
        grid_3_colonnes: 'Grid – 3 colonnes',
        alternating: 'Alternating',
        cards: 'Cards',
      },
      items: {
        hero_carousel: 'Hero Carousel',
        top_10_netflix_style: 'Top 10 – Netflix Style',
        derniers_ajouts: 'Derniers ajouts',
        films_populaires_grid: 'Films populaires – Grid',
        d_couvrir_backdrop: 'À découvrir – Backdrop',
        liste_d_taill_e: 'Liste détaillée',
        en_direct: 'En direct',
        item_en_vedette: 'Item en vedette',
        hero_carousel_portrait: 'Hero + Carousel Portrait',
        showcase_derni_res_sorties: 'Showcase – Dernières sorties',
      },
      logo_cloud: {
        grid_partenaires: 'Grid – Partenaires',
        carousel_ils_nous_font_confiance: 'Carousel – Ils nous font confiance',
        featured_partenaire_principal: 'Featured – Partenaire principal',
      },
      pricing: {
        cartes_modernes: 'Cartes modernes',
        compact: 'Compact',
        horizontal: 'Horizontal',
      },
      team: {
        grille_4_colonnes: 'Grille 4 colonnes',
        carrousel_quipe: 'Carrousel équipe',
        cartes_d_taill_es: 'Cartes détaillées',
        vue_compacte: 'Vue compacte',
        spotlight: 'Spotlight',
      },
      testimonials: {
        grille_3_colonnes: 'Grille 3 colonnes',
        citation_centr_e: 'Citation centrée',
        carrousel_moderne: 'Carrousel moderne',
      },
    },
    homepage: 'Homepage',
    preview: 'Preview',
    save: 'Save',
    saved: 'Saved',
    siteSettings: 'Settings',
    addBlock: 'Add block',
    addThisBlock: 'Add this block',
    searchBlocks: 'Search blocks...',
    noBlocksFound: 'No blocks found',
    pageBlocks: 'Page blocks',
    addFirstBlock: 'Add your first block',
    selectBlockToEdit: 'Select a block to edit',
    noFieldsAvailable: 'No fields available',
    loadingPreview: 'Loading preview...',
    noPreviewAvailable: 'Preview not available',
    noPageSelected: 'No page selected',
    imageUrl: 'Image URL',
    variant: 'Variant',
    hide: 'Hide',
    show: 'Show',
    moveUp: 'Move up',
    moveDown: 'Move down',
    duplicate: 'Duplicate',
    duplicateBlock: 'Duplicate block',
    delete: 'Delete',
    deleteBlock: 'Delete block',
    deleteBlockConfirm: 'Are you sure you want to delete this block?',
    tabs: {
      blocks: 'Blocks',
      editor: 'Edit',
      settings: 'Page',
      menus: 'Menus',
    },
    backToBlocks: 'Back to blocks',
    pageSettings: 'Page settings',
    pageTitle: 'Page title',
    pageSlug: 'URL slug',
    pageSlugHint: 'Used in the page URL',
    seo: 'SEO',
    metaTitle: 'SEO title',
    metaDescription: 'SEO description',
    options: 'Options',
    setAsHomepage: 'Set as homepage',
    advanced: 'Advanced',
    customCss: 'Custom CSS',
    customJs: 'Custom JavaScript',
    menus: {
      title: 'Menus',
      addMenu: 'Add a menu',
      noMenus: 'No menus created',
      visible: 'Visible',
      hidden: 'Hidden',
      noItems: 'No items in this menu',
      addItem: 'Add an item',
      createMenu: 'Create a menu',
      editItem: 'Edit item',
      menuName: 'Menu name',
      menuType: 'Menu type',
      deleteMenu: 'Delete menu',
      deleteMenuConfirm: 'Are you sure you want to delete the menu "{name}"?',
      itemLabel: 'Label',
      itemType: 'Link type',
      selectPage: 'Select a page',
      selectCategory: 'Select a category',
      sectionId: 'Section ID',
      email: 'Email address',
      phone: 'Phone number',
      openIn: 'Open in',
      icon: 'Icon (optional)',
      types: {
        navbar: 'Navigation bar',
        footer: 'Footer',
        sidebar: 'Sidebar',
        custom: 'Custom',
      },
      itemTypes: {
        external: 'External link',
        internal: 'Internal page',
        category: 'Category',
        section: 'Section (anchor)',
        item: 'Catalogue video',
        asset: 'Medium',
        email: 'Email',
        phone: 'Phone',
      },
      addLink: 'Add a link',
      addSubItem: 'Add a sub-item',
      // Destinations the channel has out of the box: they are not page-builder
      // pages, but they do have a public address.
      systemRoutes: {
        browse: 'Catalogue',
        categories: 'Categories',
        subscriptions: 'Subscriptions',
        contact: 'Contact',
        login: 'Sign in',
        register: 'Sign up',
        profile: 'My profile',
        blog: 'Blog',
        legal: 'Legal notice',
      },
    },
    pageDrawer: {
      title: 'Pages',
      subtitle: 'Manage the pages of your site',
      page: 'Page',
      searchPlaceholder: 'Search a page...',
      draft: 'Draft',
      published: 'Published',
      publishedHint: 'The page is visible on the site',
      draftHint: 'The page is not visible yet',
      layoutPages: 'Layout',
      noPageFound: 'No page found',
      createPage: 'Create a page',
      newPage: 'New page',
      newPageTitle: 'Page title',
      selectAPage: 'Select a page',
      duplicate: 'Duplicate',
      slugWarning: 'Changing the slug changes this page\'s URL. If it was already published, its old links redirect to the new address.',
      authorizedGroups: 'Authorised groups',
      selectGroups: 'Select groups',
      searchGroupPlaceholder: 'Search a group...',
      subscriptionPlans: 'Subscription plans',
      noSubscriptionPlan: 'No subscription plan configured',
      plansReadError: 'The plans that open this page could not be read.',
      fullAccess: 'Full access',
      deleteTitle: 'Delete the page',
      deleteMessage: 'Are you sure you want to delete "{title}"? This cannot be undone.',
      sections: {
        information: 'Information',
        layout: 'Layout',
        seo: 'SEO',
        access: 'Access',
        publication: 'Publication',
        actions: 'Actions',
      },
      makeHomepage: 'Make it the homepage',
      layoutDefault: "The channel's default layout",
      layoutDefaultNamed: 'Default layout ({name})',
      fields: {
        title: 'Title',
        slug: 'Slug',
        path: 'Path (URL)',
        metaTitle: 'Meta title',
        metaDescription: 'Meta description',
        metaDescriptionPlaceholder: 'Description for search engines...',
      },
      access: {
        public: { label: 'Public', description: 'Open to everyone' },
        authenticated: { label: 'Signed in', description: 'Requires an account' },
        restricted: { label: 'Restricted', description: 'Access on approval' },
      },
    },
  },
  siteSettings: {
    title: 'Site Settings',
    description: 'These settings apply to all pages',
    tabs: {
      branding: 'Branding',
      menus: 'Menus',
      layout: 'Styles',
      footer: 'Footer',
      code: 'CSS',
      advanced: 'Advanced code',
    },
    branding: {
      logo: {
        title: 'Logo',
        placeholder: 'Logo URL or ID',
        hint: 'The logo appears in the navigation and footer',
      },
      favicon: {
        title: 'Favicon',
        hint: 'The icon shown in the browser tab. A square image, PNG or WebP.',
      },
      colors: {
        title: 'Colors',
        primary: 'Primary color',
        secondary: 'Secondary color',
      },
      theme: {
        title: 'Default theme',
        description: 'The default theme for your site visitors',
        light: 'Light',
        dark: 'Dark',
      },
      login: {
        title: 'Sign-in page style',
        description: "The layout of your members' sign-in, sign-up and password pages",
        card: 'Centred card',
        splitImage: 'Side image',
        imageHint:
          "The sign-in picture set in Settings › Authentication, shown to the left of the form. Without one, the channel's share picture is used, otherwise a gradient of the secondary colour.",
      },
    },
    menus: {
      topbar: {
        title: 'Top bar',
        description: 'Links shown at the very top, above the navigation. Ignored when a raw HTML bar is set in the Code tab.',
      },
      header: {
        title: 'Main menu (header)',
        description: 'Links displayed in the navigation bar',
      },
      footer: {
        title: 'Footer menu',
        description: 'Links displayed in the footer',
      },
      saveError: 'The menus could not be saved.',
      saveStale: 'The menus were changed elsewhere in the meantime: they have been re-read, make your changes again.',
      saveErrorEmptyLink: 'A menu entry has no destination: pick a page or a category, enter an address, or remove that entry.',
    },
    code: {
      topbar: {
        title: 'Top bar',
        description: 'HTML shown at the very top of every page, above the navigation',
        placeholder: '<iframe src="https://…" style="width:100%;height:36px;border:0"></iframe>',
        hint: 'Meant for a bar you maintain elsewhere — an iframe, a script. Its height is measured automatically and the navigation shifts to match.',
        framingHint: 'If you embed an iframe and it stays blank: the embedded site must allow framing from this domain. An X-Frame-Options header or a strict CSP frame-ancestors directive makes the browser refuse it, with nothing shown on the page.',
      },
      css: {
        title: 'Custom CSS',
        description: 'Add CSS that applies to every page of your site',
        hint: 'This CSS is loaded on all pages and overrides the default theme. An invalid rule can break your site layout.',
        fontsTitle: 'Using your own font',
        hooksTitle: "Targeting a band",
        hooks: "Every band carries its type's class, .<type>-block, and its arrangement's, .<type>-block--<arrangement> — for instance .items-block--carousel or .faq-block--accordion — as well as the CSS classes and id you give it in the editor.",
        // `@` opens a linked message in vue-i18n: left bare it breaks the
        // message compilation and the line never renders. `{'@'}` makes it a
        // literal.
        fontsGoogle: "Hosted font (Google Fonts and the like): put the {'@'}import line at the very top of the field, before any other rule — otherwise the browser ignores it.",
        fontsSelfHosted: "Self-hosted font: declare it with {'@'}font-face. The server delivering the file must allow cross-origin sharing (Access-Control-Allow-Origin header), or the font is refused with no error message.",
      },
    },
    footer: {
      text: {
        title: 'Footer text',
        description: 'Shown under the logo in the « Columns » and « Centered » footers. The minimal footer shows neither.',
        tagline: 'Tagline',
        taglinePlaceholder: 'One sentence that sums up your channel',
        about: 'About',
        aboutPlaceholder: 'A few sentences about your organisation',
      },
      socials: {
        title: 'Social networks',
        description: "One icon per network filled in, in the footer. Paste your page's full address or your user name ({'@'}channel).",
        placeholder: "https://… or {'@'}channel",
        networks: {
          facebook: 'Facebook',
          twitter: 'X (Twitter)',
          instagram: 'Instagram',
          youtube: 'YouTube',
          linkedin: 'LinkedIn',
          tiktok: 'TikTok',
        },
      },
    },
    advanced: {
      warning: 'This code runs on every content page of your site, for every visitor. A faulty script can break the display or playback: try it on a demo channel first. It is never loaded on the sign-in, sign-up or payment pages.',
      head: {
        title: 'Tags in <head>',
        description: "Tags added to every page's head: a tool's verification, a meta tag, an external stylesheet…",
      },
      javascript: {
        title: 'Custom JavaScript',
        description: 'A script run at the end of the page, without a surrounding <script> tag.',
      },
    },
  },
  // The channel's old addresses — what the public site reads before it answers a not-found.
  redirects: {
    title: 'Redirects',
    subtitle: 'The old addresses your site still forwards.',
    btnCreate: 'Add a redirect',
    table: {
      path: 'Old address',
      target: 'Leads to',
      origin: 'Written by',
      created: 'Created',
    },
    kinds: {
      item: 'Item',
      category: 'Category',
      page: 'Page',
      article: 'Article',
    },
    origins: {
      import: 'Migration',
      rename: 'Rename',
      manual: 'By hand',
      importHint: 'Written by the migration off the previous platform.',
      renameHint: 'Written automatically when the object\'s address changed.',
      manualHint: 'Typed by an operator, for an address this product never served.',
    },
    empty: {
      title: 'No redirects',
      description: 'Changing the address of an item, a category or a published page writes one automatically. You can also type one for an address that came from an older site.',
    },
    create: {
      title: 'Add a redirect',
      description: 'The old address will lead to the object you pick. An existing redirect on the same address is replaced.',
      path: 'Old address',
      pathPlaceholder: '/old-page/',
      pathHint: 'From its leading slash, with no query and no fragment. Compared byte for byte, the trailing slash being optional.',
      target: 'Leads to',
      targetHint: 'Type to search the channel\'s items.',
      success: 'Redirect added',
      error: 'Failed to add the redirect',
      refused: 'This address cannot be redirected: the channel\'s own site answers it (sign-in, catalogue, legal pages…), or it is malformed.',
      occupied: 'An item, a page, a category or an article of the channel already answers at this address, and the site shows it before it reads a redirect. Change its address first, or pick another one.',
    },
    delete: {
      title: 'Delete the redirect',
      message: 'The address {path} will answer not-found again. Confirm?',
      success: 'Redirect deleted',
      error: 'Failed to delete',
    },
  },
  items: {
    // The media behind a publication is no longer playable: the badge names the cause rather
    // than letting "draft" stand where the author did nothing.
    assetStatus: {
      notReady: 'Media unavailable',
      error: 'Media failed',
      hint: 'The media in the library is not playable yet: this item does not play on the site until it is.',
    },
    order: {
      newest: 'Newest first',
      oldest: 'Oldest first',
    },
    inEveryPlan: 'Included in every plan',
    loadMore: 'Load more',
    loadedCount: '{count} item(s) shown',
    // The list's pager, once it can count what it pages through.
    pager: {
      range: '{first}–{last} of {total}',
      rangeUncounted: '{first}–{last}',
      perPage: 'Per page',
      previous: 'Previous page',
      next: 'Next page',
    },
    allMatchingSelected: 'All {count} items matching the filter are selected.',
    bulkDone: '{count} item(s) done',
    bulkPartial: '{done} item(s) done, {failed} failed',
    bulkFailed: 'No item could be processed',
    // Sheet numbers band: with a single content, the channel's figures are the
    // content's figures.
    numbers: {
      views: 'Views',
      completion: 'Watched in full',
      completionHint: 'Share of the playback carried through to the end, on average.',
      viewers: 'Viewers',
      watchTime: 'Watch time',
      purchases: 'Purchases',
      revenue: 'Revenue',
    },
    title: 'Items',
    subtitle: 'Manage the content published on your site.',
    searchPlaceholder: 'Search items...',
    btnAdd: 'Add items',
    filterByAccess: 'Access level',
    filterByType: 'Type',
    filterByStatus: 'Status',
    filterByCategory: 'Category',
    filterUncategorized: 'Uncategorized',
    table: {
      title: 'Title',
      type: 'Type',
      status: 'Status',
      access: 'Access',
      pricing: 'Pricing',
      engagement: 'Engagement',
      date: 'Date',
    },
    accessLevel: {
      public: 'Public',
      authenticated: 'Authenticated',
      restricted: 'Restricted',
      accessRequest: 'Access Request',
    },
    status: {
      archived: 'Archived',
      published: 'Published',
      unpublished: 'Scheduled',
      scheduled: 'Scheduled',
      scheduledOn: 'Scheduled for {date}',
      draft: 'Draft',
      mediaDeleted: 'Media deleted',
      mediaDeletedHint: 'The media was deleted from the library: this content went back to draft and is no longer visible on the site. Restore the media from the trash (within 30 days) to publish it again.',
    },
    unlisted: {
      label: 'Unlisted',
      hint: 'This content is live and reachable through its link, but it appears in no public listing: not the catalogue, not the categories, not the search.',
    },
    types: {
      video: 'Video',
      audio: 'Audio',
      image: 'Image',
      collection: 'Collection',
      thirdParty: 'External video',
    },
    visibility: {
      public: 'Public',
      private: 'Private',
      notListed: 'Unlisted',
    },
    type: {
      video: 'Video',
      playlist: 'Playlist',
      image: 'Image',
      audio: 'Audio',
      document: 'Document',
    },
    emptyState: {
      title: 'No items yet',
      description: 'Add your first content to display on your site.',
      noResults: 'No items found',
      noResultsDescription: 'Try adjusting your filters or search terms.',
    },
    deleteTitle: 'Delete item',
    deleteMessage: 'Are you sure you want to delete "{title}"? It will be removed from the site, with its prices, its granted access and its comments. The media stays in your library.',
    deleteSuccess: 'Item deleted successfully',
    deleteError: 'Failed to delete item',
    addSuccess: '{count} item(s) added successfully',
    addError: 'Failed to add items',
    paid: 'Paid',
    free: 'Free',
    noPricing: 'No pricing',
    priceCount: '{count} price | {count} prices',
    subscriptionCount: '{count} plan | {count} plans',
    bulkSelected: '{count} item(s) selected',
    selectAllMatching: 'Select all {count} matching',
    selectAllCapped: 'Select first {max}',
    bulkSelectCapped: '{max} max per bulk action',
    bulkSelectError: 'Could not select all items',
    publish: 'Publish',
    publishOn: 'Publish on {date}',
    schedule: 'Schedule',
    unpublish: 'Unpublish',
    publishSuccess: 'Item published',
    scheduleSuccess: 'Item scheduled',
    scheduleSuccessOn: 'Will be published on {date}',
    publishError: 'Failed to publish item',
    unpublishSuccess: 'Item reverted to draft',
    unpublishError: 'Failed to unpublish item',
    unpublishConfirmTitle: 'Unpublish this item?',
    unpublishConfirmMessage: 'It will be reverted to a draft and hidden from the public site. It stays in your back-office and can be published again anytime.',
    unpublishOrDeleteHint: 'If you don\'t plan to republish it, you can also delete it from the danger zone.',
    bulkPublish: 'Publish',
    bulkUnpublish: 'Unpublish',
    bulkUnpublishTitle: 'Unpublish {count} item(s)?',
    bulkUnpublishMessage: 'They will be reverted to drafts and hidden from the public site. They stay in your back-office and can be published again anytime.',
    bulkStatusSuccess: '{count} item(s) updated',
    bulkStatusError: 'Failed to update items',
    bulkEdit: 'Bulk edit',
    bulkEditSuccess: '{count} item(s) updated successfully',
    bulkEditError: 'Failed to update items',
    bulkDelete: 'Delete',
    bulkDeleteModal: {
      title: 'Delete {count} item(s)',
      message: 'The selected items will be removed from the site and from their categories, with their prices, their granted access and their comments. The media stay in your library.',
      confirmLabel: 'Type {count} to confirm',
      confirmHint: 'Enter the number of items to delete',
    },
    bulkDeleteSuccess: '{count} item(s) deleted successfully',
    bulkDeleteError: 'Failed to delete items',
    bulkAddPrice: 'Apply a price',
    bulkPriceSuccess: 'Price applied to {count} items',
    bulkPriceError: 'Failed to apply price',
    bulkEditModal: {
      title: 'Bulk edit',
      subtitle: '{count} item(s) selected',
      enableAccessLevel: 'Change access level',
      enableCategories: 'Add categories',
      categoriesHint: 'Categories will be added to existing ones, not replaced.',
      enableAccessRights: 'Configure access rights',
      accessRightsHint: 'Selected groups and users will be granted access to all selected items.',
      enablePrice: 'Apply a price',
      priceDisabledHint: 'Change the access level to Paid first',
      priceMonetizationDisabledHint: 'Enable monetization in the website settings first',
      paidNoPriceHint: 'Items set to paid — consider adding a price so users can purchase access.',
      applyChanges: 'Apply changes',
      noChanges: 'No changes to apply',
    },
    addModal: {
      title: 'Add items to channel',
      existingInfo: '{count} content(s) already in your channel — they will be skipped.',
      newInfo: '{count} new content(s) will be published.',
      allExisting: 'All selected items are already in your channel.',
      goBack: 'Change selection',
      publishBtn: 'Publish ({count})',
      badgeInChannel: 'Already in channel',
      badgeNew: 'New',
      viewItem: 'View item',
      scheduleTitle: 'Schedule publication',
      scheduleDateLabel: 'Publication date',
      scheduleBtn: 'Schedule ({count})',
      showPreview: 'Show preview before publish',
      showPreviewHint: 'Display a preview even before the publication date.',
      toast: {
        success: '{count} item published | {count} items published',
        successSkipped: '{count} item published (already in the channel: {skipped}) | {count} items published (already in the channel: {skipped})',
        allFailed: 'The item could not be created. | None of the {count} items could be created.',
        partialSuccess: '{success} item published, failed: {failed} | {success} items published, failed: {failed}',
      },
    },
    addStepper: {
      title: 'Add items',
      subtitle: '{count} asset(s) selected',
      back: 'Back',
      next: 'Next',
      steps: {
        selection: 'Selection',
        visibility: 'Visibility',
        categories: 'Categories',
      },
      validation: {
        required: 'Please complete the required fields',
      },
      publish: {
        visibilityTitle: 'Visibility',
        accessLevel: 'Access level',
        paid: 'Paid',
        categoriesTitle: 'Categories',
        advancedOptions: 'Advanced options',
      },
      visibility: {
        restrictedTitle: 'Access rights',
        restrictedDescription: 'Select the groups and users who will have access to these items.',
        groupsLabel: 'Groups',
        groupsPlaceholder: 'Select groups...',
        usersLabel: 'Users',
        usersPlaceholder: 'Select users...',
        priceTemplateTitle: 'Price template',
        priceTemplateDescription: 'Apply a default price to all created items.',
        paidConfigTitle: 'Pricing configuration',
        paidConfigDescription: 'Choose how users access this paid content. You can combine subscriptions and individual pricing.',
        subscriptionCardTitle: 'Subscription plans',
        subscriptionCardSubtitle: 'Include in existing subscription plans',
        priceCardTitle: 'Individual price',
        priceCardSubtitle: 'Set a one-time purchase or rental price',
        paidValidationWarning: 'Select at least one subscription plan, or add a price with a name and amount, to continue.',
        paidValidationTooltip: 'Configure your pricing',
      },
      categories: {
        description: 'Organize your items by assigning them to categories.',
        emptyWarning: 'No category selected — this content will be published but won\'t appear in your channel\'s category navigation. It stays reachable through search, the "All content" page and its direct link.',
      },
      processing: {
        creating: 'Creating items...',
        permissions: 'Configuring access rights...',
        prices: 'Applying prices...',
      },
      toast: {
        permissionsPartial: 'Failed to set permissions on {failed}/{total} items.',
        pricesPartial: 'Failed to apply price template to some items.',
      },
      monetization: {
        selectPlansTitle: 'Subscription plans',
        selectPlansDescription: 'Associate these items with subscription plans so that subscribers can access them.',
        selectPlans: 'Select the subscription plans that will include these items:',
        noPlans: 'No subscriptions configured',
        noPlansDescription: 'Create a subscription plan first to link paid items to it.',
        manageSubscriptions: 'Manage subscriptions',
        fullAccess: 'Full access',
        fullAccessNotice: 'These plans unlock the whole catalogue: these items are already included, nothing to select.',
        periods: {
          daily: 'day',
          weekly: 'week',
          monthly: 'month',
          yearly: 'year',
          days: '{count} days',
        },
      },
    },
    detail: {
      conflict: 'Somebody saved this item before you: nothing was written. The screen has just been re-read — make your change again and save.',
      viewInMediasbox: 'View in Mediasbox',
      tabs: {
        general: 'General',
        access: 'Access',
        monetization: 'Monetization',
        sales: 'Sales',
        comments: 'Comments',
      },
      backToItems: 'Back to items',
      saveSuccess: 'Item updated successfully',
      saveError: 'Failed to update item',
      information: {
        title: 'Information',
        description: 'Identifier and public URL of this content.',
        slug: 'URL slug',
        slugHint: 'Allowed characters: a-z, 0-9, hyphens',
        slugPrefix: '/',
        viewOnChannel: 'Open on the channel',
        publishedAt: 'Published at',
        slugChangeWarning: 'Changing the slug changes the public URL and breaks existing links and embeds.',
      },
      publication: {
        title: 'Publication',
        description: 'Control when and where this content is visible.',
        statusLabel: 'Status',
        dateHint: 'A future date schedules the content — it goes live automatically on that date.',
        draftDateHint: 'Applied when you publish the content: left empty, it takes today\'s date; a future date schedules it.',
        dayAtTime: '{day} at {time}',
        goesLiveHint: 'Goes live automatically on this date.',
        draftBanner: 'This content is a draft: hidden from the public site, visible only here. Publish it to make it live.',
      },
      categories: {
        title: 'Categories',
        description: 'Organize this item by adding it to one or more categories.',
        categories: 'Categories',
        categoriesPlaceholder: 'Select categories',
        primaryCategory: 'Primary category',
        primaryCategoryPlaceholder: 'Select a primary category',
        primaryCategoryHint: 'Sets the breadcrumb on the public page and is used to suggest related content.',
      },
      access: {
        expired: 'Expired',
        title: 'Access',
        description: 'Choose who can see this content.',
        level: 'Access level',
        publicHint: 'This content will be visible to everyone.',
        authenticatedHint: 'Access reserved for logged-in users of your site.',
        restrictedHint: 'Access limited to a selection of users or groups.',
        accessRequestHint: 'Users must submit a request and be approved by an admin.',
        paidGrantHint: 'Offer access to this content to a member or a group, without them having to pay.',
        accessRequestGrantHint: 'Granting access here is the same as approving a request: the member or group sees this content without having to ask. The requests you approve also appear in this list.',
        noGrantedPaid: 'No access offered yet: this content opens on purchase or through a subscription.',
        noGrantedAccessRequest: 'No access granted yet: this content opens to the members whose request you approve.',
        offerAccessLink: 'Offer access',
        paidUnavailable: {
          closed: 'Selling is not open on this channel yet.',
          needsAccounts: 'Opening it also switches on visitor accounts, needed to buy. Your site stays browsable without signing in.',
          closedAction: 'Open selling',
          review: 'Your request to sell is under review. We email you as soon as it has been looked at.',
          rejected: 'Your request to sell was declined.',
          rejectedAction: 'See the reason',
          waiting: 'Under review',
        },
        configureAccessLink: 'Configure access rights',
        manageAccess: 'Manage access (users / groups)',
        noAuthorized: 'No users or groups have been granted access to this item yet.',
        usersTitle: 'Users',
        groupsTitle: 'Groups',
        memberCount: '{count} member(s)',
        fullAccessTitle: 'Full access',
        fullAccessHint: 'These users and groups see every item of the channel. Full access is withdrawn from their own page.',
        fullAccessGroup: 'Group · {count} member(s)',
        openHolder: 'Open',
        btnRevoke: 'Revoke',
        revokeDialogTitle: 'Revoke access',
        revokeDialogMessage: 'Are you sure you want to revoke access for "{name}"?',
        revokeSuccess: 'Access revoked successfully',
        revokeError: 'Failed to revoke access',
        saveSuccess: 'Access updated successfully',
        saveError: 'Failed to update access',
        modal: {
          fullAccessChip: 'Full access',
          title: 'Manage access rights',
          usersTab: 'Users',
          groupsTab: 'Groups',
          searchUsers: 'Search users...',
          searchGroups: 'Search groups...',
          userColumn: 'User',
          groupColumn: 'Group',
          membersColumn: 'Members',
          noUsers: 'No users found',
          noGroups: 'No groups found',
        },
      },
      options: {
        title: 'Options',
        description: 'Additional settings for this item.',
        isListed: 'Listed',
        isListedHint: 'Item appears in listings and search results',
        allowComments: 'Allow comments',
        allowCommentsHint: 'Visitors can comment on this content',
        commentsOff: 'Comments are turned off on the channel.',
        newsletterOff: 'The newsletter is turned off on the channel.',
        channelOptionsLink: 'Channel options',
        isNewsletterEligible: 'Newsletter eligible',
        isNewsletterEligibleHint: 'Include in newsletter notifications',
        isDownloadable: 'Allow download',
        isDownloadableHint: 'Visitors can download the original file',
        isEmbeddable: 'Allow embedding',
        isEmbeddableHint: 'This content can be embedded on other websites',
        showPreview: 'Show preview before publish',
        showPreviewHint: 'Display a preview even before the publication date',
        isShownLocked: 'Show locked',
        isShownLockedHint: 'The page and thumbnail stay visible, without playback, to whoever has no access. Off, the item cannot be found by them.',
      },
      embed: {
        action: 'Embed',
        title: 'Embed this content',
        intro: 'Paste this code on any website. Free content plays there; paid, on-request or members-only content shows its trailer, or a card, linking to its page on your channel.',
        code: 'Code to paste',
        link: 'Player link',
        copyCode: 'Copy code',
        copyLink: 'Copy link',
        copied: 'Copied',
        size: 'Size',
        responsive: 'Responsive',
        audioHint: 'An audio player is a fixed-height strip: it fills the available width, no ratio involved.',
        responsiveHint: 'The player fills the width available on the host page and keeps its ratio.',
        fixedHint: 'Fixed size in pixels. On a narrower page the player will overflow.',
        ratio: 'Ratio',
        options: 'Player options',
        showTitleBar: 'Show the title and the channel name',
        showEndCard: 'Show an end card after playback',
        autoPlay: 'Start playing automatically',
        autoPlayHint: 'Most browsers block this unless the player is muted',
        disabledWebsite: 'Embedding is turned off for this channel. Enable it in the channel options to get a code.',
        disabledItem: 'Embedding is turned off for this content. Turn on « Allow embedding » in the General tab, Options section, then save.',
        disabledNoDomain: 'This channel has no domain yet. Publish it to get an embed code.',
      },
      paid: {
        trial: '{period} trial',
        planConflict: 'That plan changed in the meantime: nothing was written. Reopen the tab and try again.',
        planSaveError: 'Failed to update the plan',
        priceConflict: 'That price changed in the meantime: nothing was written. Reopen it and try again.',
        title: 'Monetization',
        description: 'Configure pricing and subscription access for this item.',
        hint: 'This content is available for purchase or via subscriptions.',
        configureMonetizationLink: 'Configure monetization',
        monetizationNotEnabled: 'Monetization is not enabled on this website.',
        enableLink: 'Enable',
        trailer: 'Trailer',
        trailerHint: 'A short preview shown to visitors before they purchase access.',
        trailerChange: 'Change',
        trailerEmptyTitle: 'Select a trailer video',
        trailerEmptyDescription: 'Click to browse your video library',
        trailerDialogTitle: 'Select a trailer video',
        subscriptions: 'Subscriptions',
        subscriptionsDescription: 'Select which subscription plans give access to this item.',
        inEveryPlan: 'Included in every plan',
        inEveryPlanHint: 'Every subscription plan of the channel opens this item, including plans created later.',
        selectSubscriptions: 'Select subscription plans',
        selectSubscriptionsPlaceholder: 'Choose plans...',
        saveSubscriptions: 'Save subscriptions',
        subscriptionsSaveSuccess: 'Subscription associations updated successfully',
        subscriptionsSaveError: 'Failed to update subscription associations',
        selectAll: 'Select all',
        deselectAll: 'Deselect all',
        noPlans: 'No subscription plans',
        noPlansDescription: 'Create subscription plans first, then come back to associate them with this item.',
        manageSubscriptions: 'Manage subscriptions',
        recommended: 'Recommended',
        trialDays: '{count}-day free trial',
        subscribers: '{count} subscribers',
        fullAccess: 'Full access',
        fullAccessNoticeTitle: 'Also included in your full-access plans',
        fullAccessNoticeDescription: 'These plans unlock the whole catalogue, so this item is already included — nothing to select. They appear on the public page under "Or get access to all the content on this platform".',
        periodDaily: 'day',
        periodWeekly: 'week',
        periodMonthly: 'month',
        periodYearly: 'year',
        periodDays: '{count} days',
        prices: 'Prices',
        addPrice: 'Add a price',
        noPrices: 'No prices configured.',
        priceCreateSuccess: 'Price created successfully',
        priceUpdateSuccess: 'Price updated successfully',
        priceDeleteSuccess: 'Price deleted successfully',
        priceDeleteError: 'Failed to delete price',
        priceSaveError: 'Failed to save price',
        setupRequired: 'This item is set as paid but has no pricing configured. Add at least one price or select a subscription plan so users can access it.',
      },
      sales: {
        title: 'Sales',
        description: 'Purchases of this item.',
        searchPlaceholder: 'Search by customer...',
        filterStatus: 'Status',
        filterType: 'Type',
        table: {
          customer: 'Customer',
          price: 'Price',
          type: 'Type',
          status: 'Status',
          date: 'Date',
        },
        status: {
          active: 'Active',
          expired: 'Expired',
          refunded: 'Refunded',
          cancelled: 'Cancelled',
        },
        type: {
          purchase: 'Purchase',
          rental: 'Rental',
        },
        emptyState: {
          title: 'No sales yet',
          description: 'Sales will appear here once customers purchase this item.',
          noResults: 'No sales found',
          noResultsDescription: 'Try adjusting your filters or search terms.',
        },
      },
      comments: {
        description: 'View and moderate comments on this item.',
      },
      trailer: {
        title: 'Trailer',
        description: 'A preview visitors can play without opening the content.',
        hint: 'On a paid content, this is what visitors see before they buy access. On a free one, it is a teaser.',
      },
      danger: {
        title: 'Danger zone',
        description: 'Actions that remove things from the site',
        deleteTitle: 'Delete this item',
        deleteDescription: 'Once deleted, this item is removed from your site. The media stays in your library.',
        deleteButton: 'Delete item',
      },
    },
  },
  users: {
    cockpit: {
      kpis: {
        total: 'Members',
        totalHint: 'every member',
        active: 'Active (30 d)',
        activeHint: 'came in the last 30 days',
        joined: 'New (30 d)',
        joinedHint: 'joined in the last 30 days',
        pending: 'Waiting',
        pendingHint: 'to approve',
        invited: 'Invited',
        invitedHint: 'no account yet',
        customers: 'Customers',
        customersHint: 'a purchase or a subscription',
        subscribers: 'Subscribers',
        subscribersHint: 'running subscription',
        trial: 'In trial',
        trialHint: 'free trial running',
      },
      quick: {
        all: 'All',
        active: 'Active',
        pending: 'Waiting',
        invited: 'Invited',
        suspended: 'Suspended',
        expired: 'Access expired',
        customers: 'Customers',
        subscribers: 'Subscribers',
      },
      reset: 'Reset',
      client: 'Customer',
      clientNone: '—',
      subscriber: 'Subscriber · {plan}',
      formerSubscriber: 'Former subscriber',
      inTrial: 'In trial · {plan}',
      trialUntil: 'until {date}',
      trialNotConverted: 'Trial not converted',
      purchases: '{count} purchase | {count} purchases',
      invitedOn: 'Invited on {date}',
      invitationExpired: 'Invitation expired',
      export: 'Export (CSV)',
      exporting: 'Exporting…',
      exportError: 'The export failed',
      selected: '{count} member selected | {count} members selected',
      clearSelection: 'Clear selection',
      bulk: {
        approve: 'Approve',
        addToGroup: 'Add to a group',
        removeFromGroup: 'Remove from a group',
        fullAccess: 'Give full access',
        export: 'Export',
        delete: 'Delete',
        deleteTitle: 'Delete {count} member? | Delete {count} members?',
        deleteMessage: 'Their memberships leave the channel. Their purchases stay in the books.',
        done: '{count} member updated | {count} members updated',
        partial: '{done} of {total} updated: the others changed meanwhile, reload the list.',
      },
    },
    title: 'Users & Groups',
    subtitle: 'Manage users and groups for your website.',
    searchPlaceholder: 'Search users...',
    btnInvite: 'Invite',
    picker: {
      label: 'Users',
      placeholder: 'Select users...',
      noResults: 'No users found',
    },
    filterByStatus: 'Status',
    filterByGroup: 'Group',
    filterByAccess: 'Access',
    filterByJoined: 'Joined',
    filterByActivity: 'Activity',
    filterByTrial: 'Free trial',
    trialFilter: {
      current: 'In trial',
      ever: 'Had a trial',
      converted: 'Trial converted',
      not_converted: 'Trial not converted',
    },
    fullAccessFilter: {
      full: 'Full access',
      standard: 'Standard access',
    },
    order: {
      newest: 'Newest first',
      oldest: 'Oldest first',
    },
    approve: 'Approve membership',
    approveSuccess: 'Membership approved',
    approveError: 'The approval failed',
    staleMessage: 'Somebody saved first and nothing was written. The record has just been re-read.',
    standing: {
      active: 'Active',
      suspended: 'Suspended',
      expired: 'Expired',
      awaiting_approval: 'Awaiting approval',
      no_password: 'Registration unfinished',
      unverified_email: 'Email unverified',
    },
    standingHint: {
      active: 'Nothing stands between this person and the channel.',
      suspended: 'Their membership is closed. Their account and their purchases are kept.',
      expired: 'Their access window has closed: they reach nothing, whatever else they hold.',
      awaiting_approval: 'Nobody has let them in yet. A waiting membership grants nothing at all.',
      no_password: 'Their account has no password: they never finished registering.',
      unverified_email: 'Their mailbox has never answered. The channel may withhold itself until it does.',
    },
    passwordReset: {
      issued: 'Reset link sent',
      no_password: 'This account has no password, so no reset link can reach it. Send them an invitation instead.',
      password_login_closed: 'This channel does not allow signing in with a password.',
      throttled: 'A link was just sent. Try again in a few minutes.',
    },
    tabs: {
      users: 'Users',
      groups: 'Groups',
      invitations: 'Invitations',
    },
    invitations: {
      title: 'Invitations',
      subtitle: 'Manage pending email invitations and invitation links.',
      emailTitle: 'Email invitations ({count})',
      linksTitle: 'Invitation links ({count})',
      empty: 'No pending invitations',
      noLinks: 'No invitation links yet',
      unnamed: 'Untitled link',
      access: {
        full: 'Full access',
        standard: 'Standard',
      },
      table: {
        email: 'Email',
        access: 'Access',
        invitedBy: 'Invited by',
        expires: 'Expires',
        sendCount: 'Sends',
        delivery: 'Delivery',
        name: 'Name',
        status: 'Status',
        uses: 'Uses',
        accessExpiry: 'Access granted',
      },
      actions: {
        resend: 'Resend',
        cancel: 'Cancel',
        copyLink: 'Copy link',
        activate: 'Activate',
        deactivate: 'Deactivate',
        deleteLink: 'Delete link',
      },
      status: {
        active: 'Active',
        accepted: 'Accepted',
        outstanding: 'Outstanding',
        noAuthority: 'Issuer has no authority',
        inactive: 'Inactive',
        exhausted: 'Exhausted',
      },
      delivery: {
        queued: 'Email queued',
        no_relay: 'No relay: the link is the whole delivery',
        sent: 'Sent',
      },
      urlOnceHint: 'Copy these links now: they will not be shown again. If needed, resend the invitation from the member\'s page.',
      noAuthorityHint: 'The account that issued this link no longer has authority over this organization: it is listed and enrols nobody.',
      // Both lists answer to the key that mints, not to the one that reads the roster.
      viewOnly: 'Invitations and links are read with the right to manage members, which your role does not have. Ask an organization administrator for it.',
      lastUsed: 'Last used on {date}',
      expiry: {
        noExpiry: 'No expiry',
        expired: 'Expired',
        today: 'Today',
        tomorrow: 'Tomorrow',
        expiresIn: 'In {days} days',
      },
      toast: {
        resent: 'Invitation resent',
        noRelay: 'Invitation created. This deployment has no mail relay: copy the link to hand it over.',
        resendError: 'Could not resend the invitation.',
        cancelled: 'Invitation cancelled',
        cancelError: 'Could not cancel the invitation.',
        linkCopied: 'Link copied',
        linkUpdated: 'Link updated',
        linkDeleted: 'Link deleted',
        urlUnavailable: 'This link was created elsewhere: its address cannot be given back. Create a new one.',
        linkError: 'Could not update the link.',
      },
    },
    table: {
      email: 'User',
      standing: 'Standing',
      groups: 'Groups',
      joinedAt: 'Joined',
      lastActivity: 'Last activity',
    },
    badges: {
      fullAccess: 'Full access',
      owner: 'Owner',
      staff: 'Staff',
      inactive: 'Inactive',
      pending: 'Pending',
      expired: 'Expired',
      pendingRegistration: 'Pending signup',
      pendingRegistrationHint: "This user hasn't completed their account setup yet (no password defined).",
    },
    labels: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      expired: 'Expired',
      neverActive: 'Never',
      lastActivityHint: 'Written at most once an hour: a « seen around then », not a timestamp to reason with.',
      recentlyActiveHint: 'Recently active',
    },
    filters: {
      joinedThisMonth: 'Joined this month',
      joinedThisYear: 'Joined this year',
      activityLt7: 'Active (< 7 days)',
      activityLt30: 'Active (< 30 days)',
      activityGt30: 'Inactive (> 30 days)',
      activityNever: 'Never active',
      clear: 'Clear filter',
    },
    emptyState: {
      title: 'No users',
      description: 'Invite users to join your website.',
      noResults: 'No users found',
      noResultsDescription: 'Try adjusting your filters or search terms.',
    },
    deleteTitle: 'Remove user',
    deleteMessage: 'Are you sure you want to remove "{name}"? This action cannot be undone.',
    deleteSuccess: 'User removed successfully',
    deleteError: 'Failed to remove user',
    sendPasswordReset: 'Send password reset link',
    passwordResetSuccess: 'Password reset email sent to {email}',
    passwordResetError: 'Failed to send password reset email',
    detail: {
      backToUsers: 'Back to users',
      saveSuccess: 'User updated successfully',
      saveError: 'Failed to update user',
      reactivateSuccess: 'User reactivated successfully',
      reactivateError: 'Failed to reactivate user',
      deactivateSuccess: 'User deactivated',
      deactivateError: 'Failed to deactivate user',
      deleteSuccess: 'User removed successfully',
      deleteError: 'Failed to remove user',
      deleteTitle: 'Remove user',
      deleteMessage: 'Are you sure you want to remove "{name}"?',
      deleteConfirmMessage: 'Are you sure you want to remove this user?',
      inactiveAlert: 'This user has not signed up yet.',
      pendingAlert: 'This user was invited but has not completed registration. They cannot access the site.',
      awaitingApprovalAlert: 'This person is waiting to be let in. Until they are approved, their membership grants nothing.',
      unverifiedEmailAlert: 'Their mailbox has never answered. If the channel requires verification, they cannot get in.',
      deactivatedAlert: 'This user has been deactivated and cannot access the site.',
      expiredAlert: 'This user\'s access expired on {date}. They can no longer access the site.',
      expiredGroupsAlert: 'This user belongs to {count} expired group(s): {names}. Access to content from these groups is no longer active.',
      resendInvitation: 'Resend invitation',
      sendInvitation: 'Send invitation',
      copyRegistrationLink: 'Copy registration link',
      linkCopied: 'Link copied!',
      linkError: 'Failed to generate registration link',
      extendAccess: 'Extend access',
      btnReactivate: 'Reactivate',
      reactivate: 'Reactivate',
      deactivate: 'Deactivate account',
      deactivateTitle: 'Deactivate this account?',
      deactivateMessage: '{name} will no longer be able to sign in or reach any content. The account, its groups and its access are kept — you can reactivate it at any time.',
      status: {
        active: 'Active',
        inactive: 'Inactive',
        pending: 'Pending signup',
        deactivated: 'Deactivated',
        expired: 'Expired',
        groupExpired: 'Group expired',
      },
      roles: {
        owner: 'Owner',
        staff: 'Staff',
      },
      tabs: {
        overview: 'Overview',
        purchases: 'Purchases',
        information: 'Information',
        access: 'Access',
        statistics: 'Statistics',
      },
      overview: {
        viewingTitle: 'Viewing over 90 days',
        allStatistics: 'All statistics',
        watchTime: 'Watch time',
        contentsWatched: 'Contents watched',
        sessions: 'Sessions',
        completion: 'Average completion',
        recentTitle: 'Latest sessions',
        noSessions: 'Nothing watched over this period.',
        watchedAt: 'watched {pct}',
        customerTitle: 'Customer',
        seePurchases: 'See purchases',
        notCustomer: 'Has bought nothing on this channel yet.',
        spentSince: 'spent · {refunded} refunded',
        subscription: 'Subscription',
        noSubscription: 'None',
        nextRenewal: 'Next charge',
        accessUntil: 'Access until',
        purchasesCount: 'Purchases and rentals',
        lastPurchase: 'Last purchase',
        groupsTitle: 'Groups',
        noGroups: 'In no group.',
        manageGroups: 'Manage',
        joinTitle: 'Registration',
      },
      invitation: {
        invited: 'Invited on {date}',
        invitedBy: 'by {name}',
        reminded: 'Reminded {count} times',
        accepted: 'Invitation accepted on {date}',
        joined: 'Member since {date}',
        registeredAlone: 'Registered on their own on {date}',
        notCreated: 'Account not created yet',
        expiresOn: 'The invitation expires on {date}',
        expiredOn: 'The invitation expired on {date}',
        firstSeen: 'First visit on {date}',
        lastSeen: 'Last activity {when}',
        neverSeen: 'Never came to the channel',
        banner: 'This person was invited on {date} and has not created their account yet.',
        bannerExpired: 'The invitation sent on {date} expired before the account was created.',
        bannerReminded: 'Reminded {count} times.',
        bannerNeverSent: 'Added on {date}, this person never received an invitation and has never come to the channel.',
        chip: 'Invited · no account yet',
        chipExpired: 'Invitation expired',
        chipNeverSent: 'Never invited',
      },
      purchases: {
        totalSpent: 'Total spent',
        totalSpentHint: 'Tax included, refunds deducted',
        subscription: 'Subscription',
        noSubscription: 'No running subscription',
        purchases: 'Purchases and rentals',
        refunded: 'Refunded',
        paymentsTitle: 'Payments',
        paymentsHint: 'Every payment this member made on the channel',
        noPayments: 'No payment.',
        imported: 'imported',
        kind: {
          purchase: 'Purchase',
          rental: 'Rental',
          contribution: 'Support contribution',
          subscription: 'Subscription',
        },
      },
      accessView: {
        accountTitle: 'Account access',
        fullAccess: 'Full access',
        fullAccessHint: 'sees the whole catalogue, no subscription needed',
        endTitle: 'Account access ends',
        endHint: 'after this date the account can no longer sign in',
        groupsTitle: 'Groups',
        groupsHint: 'Saved together with the page',
        groupsPicker: 'Member of',
        noGroupToPick: 'This channel has no group yet',
        groupUnsaved: 'Added, applies once saved',
        noGroups: 'In no group.',
        groupGrants: 'gives access to {count} content | gives access to {count} contents',
        title: 'What {name} can access',
        subtitle: 'Every content, and what gives access to it',
        grant: 'Give access to a content',
        all: 'All',
        kind: {
          full_access: 'Full access',
          subscription: 'Subscription',
          group: 'Groups',
          category: 'Categories',
          purchase: 'Purchases',
          direct: 'Direct access',
        },
        reason: {
          direct: 'Direct access',
          group: 'Group {name}',
          category: 'Category {name}',
          categoryViaGroup: 'Category {name} · group {group}',
          purchase: 'Purchase',
          rental: 'Rental',
          subscription: 'Subscription {name}',
          fullAccess: 'Via full access',
          fullAccessGroup: 'Via full access · group {name}',
          fullAccessPlan: 'Via full access · subscription {name}',
        },
        until: 'Until',
        never: 'no end',
        content: 'Content',
        reachedBy: 'Access through',
        revokeDirect: 'Remove direct access',
        revokeCategory: 'Remove access to category {name}',
        revoked: 'Access removed',
        revokeError: 'The removal failed',
        empty: 'No content granted in particular.',
        notLive: 'This membership grants nothing today (suspended, expired or waiting): the rights below come back with it.',
        everything: 'Sees the whole catalogue:',
        everythingMember: 'the member\'s full access',
        everythingGroup: 'group {name}',
        everythingPlan: 'subscription {name}',
        openItems: '+ {count} item open to every member (not shown here) | + {count} items open to every member (not shown here)',
        truncated: 'Partial list: this member holds too many rights to show them all.',
        pagesTitle: 'Granted pages',
      },
      commerce: {
        subscriber: 'Subscriber · {plan}',
        formerSubscriber: 'Former subscriber',
        purchases: '{count} purchase | {count} purchases',
        rentalUntil: 'Rental until {date}',
      },
      information: {
        accountTitle: 'Account',
        securityTitle: 'Security',
        accessUntil: 'Access ends',
        accessNoEnd: 'Never',
        passwordSet: 'Password set',
        passwordNone: 'No password',
        readOnly: 'Read-only',
        profileTitle: 'Profile',
        profileEditable: 'The account\'s name and address. Editable because this account exists on this channel only.',
        accountDescription: 'The membership\'s key dates.',
        passwordTitle: 'Password',
        passwordDescription: 'Nobody sees the password. You can send the member a link to change it.',
        newsletterTitle: 'Newsletter',
        newsletterLabel: 'Receives the channel\'s newsletter',
        registrationTitle: 'Registration form',
        registrationDescription: 'What the member answered when signing up. Fields marked internal are not shown to them.',
        memberSince: 'Member since {date}',
        internal: 'internal',
        passwordSendLink: 'Send a reset link',
        identityTitle: 'Identity',
        identityOwnedHint: 'The address and the name belong to the account rather than to the membership: they are changed from the person\'s own account.',
        emailVerified: 'Verified',
        emailUnverified: 'Unverified',
        noName: 'No name',
        approvedAt: 'Approved on',
        notApproved: 'Not approved yet',
        noPasswordHint: 'This account has no password, so a reset link would not reach it. Send them an invitation instead.',
        newsletterOptOutHint: 'A member is subscribed on arrival. You may record an unsubscribe they asked for; you may not subscribe them.',
        newsletterOptedOut: 'This member unsubscribed: only they can subscribe again, from their account.',
        identityDescription: 'User account identity.',
        email: 'Email',
        emailGlobalHint: 'This email identifies the account across the whole platform. Changing it updates the login identifier everywhere.',
        name: 'Name',
        preferencesTitle: 'Preferences',
        preferencesDescription: 'Newsletter and notification preferences.',
        newsletterEnabled: 'Newsletter',
        newsletterEnabledHint: 'User receives newsletter emails',
        passwordHint: 'Send an email to reset this user\'s password.',
        metadataTitle: 'Custom Fields',
        metadataDescription: 'Additional information collected for this user.',
        metadataPrivate: 'Admin only',
        metadataSave: 'Save custom fields',
        metadataSaved: 'Custom fields saved',
        metadataError: 'Failed to save custom fields',
      },
      access: {
        title: 'Content access',
        subtitle: 'Items this user can access.',
        description: 'Items this user can access.',
        banner: {
          title: '{count} accessible item(s)',
          description: 'Access from: {sources}',
          fullAccess: 'Full access enabled',
          fullAccessHint: 'This user can access all restricted content on the site.',
        },
        sources: {
          direct: '{count} direct',
          groups: '{count} group(s)',
          subscriptions: '{count} subscription(s)',
          purchases: '{count} purchase(s)',
          rentals: '{count} rental(s)',
        },
        accessSettings: {
          title: 'Full access',
          description: 'Grant unrestricted access to all content on the site.',
          fullAccess: 'Access to all content',
          fullAccessHint: 'This user sees all content on the site, without restriction.',
          groupsTitle: 'Groups',
          groupsHint: 'Groups add access, they never remove any. A group\'s expiration only cuts the content shared through that group; the expiration date above closes the whole site to this user, whatever their groups.',
          groupsLabel: 'Groups',
          groupsCount: 'In {count} group | In {count} group | In {count} groups',
          groupsManagedOnGroup: 'Group membership is managed from the group: that is where it is written and where it is read.',
          groupsPlaceholder: 'Select groups...',
          expirationTitle: 'Expiration',
          expiresAt: 'Expiration date',
          expiresAtHint: 'Once past this date, the user will no longer be able to access the site — regardless of other access sources.',
        },
        table: {
          title: 'Title',
          slug: 'Slug',
          status: 'Status',
          directAccess: 'Direct',
          accessType: 'Access',
        },
        published: 'Published',
        unpublished: 'Unpublished',
        directBadge: 'Direct',
        directAccess: 'Direct',
        groupAccess: 'Via group',
        btnRevoke: 'Revoke',
        revoke: 'Revoke access',
        emptyState: 'No items accessible.',
        emptyTitle: 'No accessible items',
        emptyDescription: 'This user doesn\'t have access to any restricted items.',
        purchasesAndSubscriptions: {
          title: 'Purchases & subscriptions',
          count: '{count} source | {count} sources',
        },
        subscriptions: {
          title: 'Subscriptions',
          fullAccess: 'Full access',
          items: '{count} item(s)',
          autoRenew: 'Auto-renew',
          expiresOn: 'Ends on {date}',
          complimentary: 'Complimentary',
          giftBtn: 'Gift subscription',
          cancel: 'Cancel',
          cancelConfirmComplimentary: 'Are you sure you want to cancel this complimentary subscription? Access will be revoked immediately.',
          cancelIrreversible: 'This action is irreversible.',
          cancelConfirmPaid: 'The user will lose access at the end of the current billing period on {date}. The subscription will not be renewed.',
          cancelTypePrompt: 'Type "{word}" to confirm:',
          cancelConfirmWord: 'CANCEL',
          cancelConfirmBtn: 'Cancel',
          cancelSuccess: 'Subscription cancelled',
          cancelError: 'Failed to cancel subscription',
          noSubscriptions: 'No subscriptions',
          cancelledOn: 'Ended on {date}',
          expiredOn: 'Ended on {date}',
          renewsOn: 'Renews on {date}',
          noEnd: 'No end date',
          status: {
            active: 'Active',
            past_due: 'Payment pending',
            cancelled: 'Cancelled',
            expired: 'Ended',
            paused: 'Paused',
          },
        },
        purchases: {
          title: 'Purchases',
          purchasedOn: 'Purchased on {date}',
          download: 'Download allowed',
        },
        rentals: {
          title: 'Rentals',
          expiresOn: 'Expires on {date}',
          hoursRemaining: '{hours}h remaining',
          expiringSoon: 'Expiring soon',
        },
        directItems: {
          title: 'Direct content access',
          description: 'Give this user access to all content, or to specific content.',
          specificTitle: 'Specific content',
          fullAccessActive: 'Full access is enabled: this user already has access to all content. Specific grants are unnecessary while full access stays on.',
          btnGrant: 'Grant access',
          revokeConfirmTitle: 'Revoke access',
          revokeConfirmMessage: 'Are you sure you want to revoke direct access to "{title}"?',
          revokeSuccess: 'Access revoked successfully',
          revokeError: 'Failed to revoke access',
          grantSuccess: 'Access granted successfully',
          grantError: 'Failed to grant access',
          emptyTitle: 'No direct access',
          emptyDescription: 'This user has no items with direct access. Use the button above to grant access.',
          selectedCount: '{count} item(s) selected',
          revokeSelection: 'Revoke selection',
          batchRevokeMessage: 'Are you sure you want to revoke access to {count} item(s)?',
          batchRevokeSuccess: '{count} access(es) revoked successfully',
          batchRevokePartial: '{completed} revoked, {failed} failed',
        },
        groupItems: {
          title: 'Access via groups',
          expired: 'Expired',
          items: '{count} item(s)',
          viewAll: 'View all {count} items',
          viewGroup: 'View group',
        },
        grantDialog: {
          title: 'Grant item access',
          searchPlaceholder: 'Search restricted items...',
          noResults: 'No restricted items found',
          grantByCategory: 'Grant by category',
          categoryHint: 'Grant access to all items in the selected category.',
          grantItems: 'Grant access',
          grantCategory: 'Grant category',
        },
      },
      statistics: {
        title: 'Statistics',
        description: 'Overview of this user\'s activity.',
        lastActivity: 'Last activity',
        registrationDate: 'Registration date',
        groupsCount: 'Groups',
        never: 'Never',
      },
    },
    invite: {
      title: 'Invite to your channel',
      header: {
        subtitle: 'They will each receive a personal invitation by email.',
        subtitleLink: 'One link to share: anyone who opens it can join the channel.',
      },
      mode: {
        switchToLink: 'Create a shareable link',
        switchToLinkHint: 'Share one link so people can join — no need to enter their emails.',
        switchToEmail: 'Back to email invitation',
        linkHistory: 'Link history',
      },
      history: {
        back: 'Back to the link',
        created: 'created on {date}',
      },
      access: {
        title: 'Access level',
        standard: {
          label: 'Standard access',
          desc: 'Public content of the channel',
          hint: 'They can browse everything published publicly on this channel.',
        },
        full: {
          label: 'Full access',
          desc: 'All content, including restricted',
          hint: 'They can also access private and restricted content on this channel.',
        },
      },
      groups: {
        title: 'Add to groups (optional)',
        placeholder: 'Select groups…',
        hint: 'Members inherit the content access each group grants.',
        count: '{count} group(s)',
      },
      expiry: {
        title: 'Access expiration',
        never: 'Never',
        duration: 'Duration',
        date: 'On a date',
        days: '{count} days',
        year: '1 year',
        summaryNever: 'Never expires',
        summaryDuration: 'Expires {count} days after joining',
        summaryDate: 'Expires on {date}',
        durationHint: 'Counted from each person\'s registration: somebody who arrives late keeps the whole period.',
      },
      grantsHint: 'A link arriving with full access or a group changes what people see, so it also takes the right to manage access.',
      grantsRefused: 'You do not hold the right to manage access: a link carrying full access or a group will be refused.',
      form: {
        nameLabel: 'Link name (optional)',
        nameHelper: 'Only to recognize it later — a dated name is used if you leave it empty.',
        namePlaceholder: 'e.g. Newsletter subscribers',
        defaultName: 'Share · {date}',
        quotasTitle: 'Limits',
        generateButton: 'Generate link',
      },
      settings: {
        maxUses: {
          title: 'Usage limit',
          hint: 'Link will be disabled after reaching this limit',
          unlimited: 'Unlimited',
          uses: '{count} use(s)',
        },
        expiration: {
          title: 'Validity period',
          hint: 'Link will automatically expire',
          never: 'Never',
          default: 'Default (30 days)',
          '1d': '1 day',
          '7d': '7 days',
          '15d': '15 days',
          '30d': '30 days',
        },
      },
      review: {
        recipients: 'Recipients',
        access: 'Access level',
        willSee: 'They will get',
        editButton: 'Edit',
        sendButton: 'Send no invitation | Send 1 invitation | Send {count} invitations',
      },
      result: {
        title: 'Invitation link generated',
        sentTitle: 'Invitations sent',
        sentDescription: 'The memberships exist and each person has been handed their personal link.',
        urlsLabel: 'Links to hand over',
        description: '"{name}" is ready to share. You can switch it off, back on, or copy its address from the Invitations tab; for other access settings, create a new link.',
        createAnother: 'Create another link',
        inviteMore: 'Invite more people',
        resentDescription: 'No membership was created: each person received a new personal link, with the access they already had.',
      },
      errors: {
        alreadyMembers: 'These people are already members of this channel:',
        sendFailed: 'Failed to send invitations to:',
        reinvited: 'These addresses already held a membership that was never finished: the invitation was sent again, and their current access is unchanged:',
      },
      toast: {
        invitesSent: 'No invitation sent | 1 invitation sent | {count} invitations sent',
        emailsAdded: '{count} email(s) added',
        linkCreated: 'Invitation link created',
        linkCreateFailed: 'Failed to create link',
        linkCopied: 'Link copied to clipboard',
        linkActivated: 'Link activated',
        linkDeactivated: 'Link deactivated',
        linkUpdateFailed: 'Failed to update link',
      },
    },
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage your website configuration.',
    saveSuccess: 'Settings saved successfully',
    saveError: 'Failed to save settings',
    conflict: 'Somebody saved before you and nothing was written. The screen has just been read again — make your change once more and save it.',
    tabs: {
      information: 'Information',
      domain: 'Domain',
      authentication: 'Authentication',
      template: 'Template',
      options: 'Options',
      notifications: 'Notifications',
      monetization: 'Monetization',
      legal: 'Legal',
    },
    legal: {
      onlineWithoutNotice:
        'Your channel is online without a legal notice. Every publicly reachable site needs one: fill in who publishes the channel below, then review and publish the prefilled text.',
      identity: {
        title: 'Who publishes this channel',
        description:
          'These details identify your organisation. They are shared by all of its channels and feed your legal documents.',
        seeded: 'Taken from your monetization application.',
        locked: {
          body: 'These details are locked: they appear on the legal notices already online. Our team updates them after checking.',
        },
        saved: 'Details saved',
        legalName: 'Registered name',
        legalForm: 'Legal form',
        legalFormPlaceholder: 'Ltd, LLC, non-profit…',
        registrationNumber: 'Registration number',
        registrationCity: 'City of registration',
        taxId: 'VAT number',
        shareCapital: 'Share capital',
        addressLine1: 'Registered office',
        addressLine2: 'Address line 2',
        postalCode: 'Postal code',
        city: 'City',
        country: 'Country',
        publicationDirector: 'Publication director',
        publicationDirectorHint: 'Usually the legal representative. No other screen asks you for it.',
        contactEmail: 'Contact shown on the site',
        contactEmailHint: 'The address your visitors can write to.',
        contactPhone: 'Phone shown on the site',
        host: 'Host',
        hostFilled: 'Filled in by Videas',
        hostValue: 'VIDEAS SAS — Frelinghien, France',
        countryHint: 'Two-letter country code, for instance FR.',
        shareCapitalHint: 'In euros. Leave empty if your legal form does not require it.',
        save: 'Save',
        saveFailed: 'Saving failed.',
        conflict: 'Somebody changed these details before you: they have been reloaded. Make your change again.',
        invalid: 'A value was refused: check the country code (two letters), the contact address and the capital.',
      },
      documents: {
        title: 'Your documents',
        description:
          'Each one has its own public page and address. They are prefilled from a template and the details above: you review them, adjust them if needed, then publish them.',
        blockedOne: 'One detail is missing before your documents can be generated: {fields}. Fill it in above, then save.',
        blockedMany: 'Some details are missing before your documents can be generated: {fields}. Fill them in above, then save.',
        outdated: 'Your details have changed since this text was generated.',
        generate: 'Generate',
        generated: 'Document generated, review it before publishing',
        generateFailed: 'Generation failed',
        review: 'Review',
        reviewAndPublish: 'Review and publish',
        viewPage: 'View page',
        openMonetization: 'Open monetization',
        updatedOn: 'Updated on {date}',
        write: 'Write it',
        publishedOn: 'Published on {date}',
        takenDownSince: 'Published on {date}, taken down since',
        otherLanguage: 'This text is in {language}, while your channel publishes in another language.',
        withdrawn: {
          legalNotice: 'This document was taken off the site: it no longer applies to this channel.',
          privacy: 'This document was taken off the site: it no longer applies to this channel.',
          termsOfUse: 'Taken off the site: your channel no longer opens accounts. The text is kept — publish it again if you reopen them.',
          termsOfSale: 'Taken off the site: your channel no longer sells. The text is kept — publish it again if you start selling.',
        },
      },
      languages: {
        fr: 'Français',
        en: 'English',
      },
      kinds: {
        legalNotice: 'Legal notice',
        privacy: 'Privacy and cookies',
        termsOfUse: 'Terms of use',
        termsOfSale: 'Terms of sale',
      },
      why: {
        legalNotice: 'Required as soon as a site is reachable by the public.',
        privacy: 'Your channel measures its audience and sets cookies, so this one is required.',
        termsOfUse: 'What your members agree to when they create an account on the channel.',
        termsOfSale: 'What your buyers agree to. Yours to write: they describe what you sell.',
      },
      status: {
        absent: 'Absent',
        review: 'Awaiting review',
        published: 'Published',
        custom: 'Custom',
        notApplicable: 'Not applicable',
        withdrawn: 'Withdrawn',
      },
      fields: {
        legalName: 'Registered name',
        legalForm: 'Legal form',
        shareCapital: 'Share capital',
        registrationNumber: 'Registration',
        registrationCity: 'City of registration',
        taxId: 'VAT',
        address: 'Registered office',
        publicationDirector: 'Publication director',
        contactEmail: 'Contact',
        contactPhone: 'Phone',
        host: 'Host',
        hostAddress: 'Host address',
        siteName: 'Site name',
      },
      sources: {
        identity: 'Your organisation',
        videas: 'Videas',
        channel: 'This channel',
      },
      review: {
        caveat:
          'Template text. It does not replace legal advice, and you remain responsible for its content.',
        content: 'Document text',
        edit: 'Edit the text',
        provenanceTitle: 'Inserted details',
        generatedHint:
          'Change your details and this document updates itself. Rewrite the text here and it becomes yours, never to be regenerated.',
        customHint: 'You rewrote this text: it will never be regenerated without your say-so.',
        restore: 'Restore the generated text',
        unpublish: 'Unpublish',
        save: 'Save',
        publish: 'Publish',
        published: 'Document published',
        publishFailed: 'Publishing failed',
        restored: 'Generated text restored',
        restoreFailed: 'Restoring failed',
        unpublished: 'Document unpublished',
        markdown: 'Document text (Markdown)',
        markdownHint: 'Headings start with #, lists with -. The public page lays it all out.',
        saveDraft: 'Save without publishing',
        saved: 'Draft saved',
        saveFailed: 'Saving failed.',
        unpublishFailed: 'Taking it down failed.',
        conflict: 'Somebody saved this document before you and nothing was written. Read it again and make your change once more.',
      },
    },
    template: {
      current: {
        title: 'Your appearance',
        description:
          'Seven settings decide your site\'s skeleton: its navigation, its cards, its content pages. Choosing a template rewrites all of them; your content does not move.',
        changed: '· changed',
        driftNoticeOne:
          'One setting has been changed from the editor. Choosing a template rewrites all seven settings, including that one.',
        driftNoticeMany:
          '{count} settings have been changed from the editor. Choosing a template rewrites all seven settings, including those.',
        none: 'No template',
        noneHint:
          'This channel was created before templates existed. Choosing one rewrites its appearance; its content stays put.',
        change: 'Change template',
        restore: 'Restore {template} settings',
      },
      gallery: {
        title: 'Change template',
        description:
          'Layouts that are genuinely different from one another. Choosing one rewrites the site\'s appearance; your content, your categories and your settings stay put.',
        current: 'Current',
      },
      fields: {
        layout_navbar_style: 'Navigation',
        layout_footer_style: 'Footer',
        layout_item_card_template: 'Cards',
        layout_item_detail_style: 'Content page',
        layout_category_banner_width: 'Category banner',
        layout_category_banner_content: 'Banner content',
        base_theme: 'Theme',
      },
      values: {
        navbar: {
          transparent: 'Transparent',
          solid: 'Solid',
          pill: 'Pills',
          underline: 'Underlined',
        },
        footer: {
          columns: 'Columns',
          centered: 'Centred',
          minimal: 'Minimal',
        },
        card: {
          cinematic: 'Cinematic',
          minimal: 'Minimal',
          poster: 'Poster',
          posterSmall: 'Compact poster',
          formation: 'Course',
          formation2: 'Course 2',
          arte: 'Arte',
          netflix: 'Netflix',
          reveal: 'Reveal on hover',
          media: 'Media',
        },
        detail: {
          cinematic: 'Cinematic',
          showcase: 'Showcase',
          spotlight: 'Spotlight',
          classic: 'Classic',
          minimal: 'Minimal',
          twoColumn: 'Two columns',
        },
        banner: {
          contained: 'Within the page width',
          full: 'Full width',
          none: 'Title only',
          image: 'The category image',
          item: 'A video from the category',
        },
        theme: {
          light: 'Light',
          dark: 'Dark',
        },
      },
      shapes: {
        single: 'a single page',
        small: 'a small selection',
        catalog: 'a catalogue',
      },
      confirm: {
        title: 'Switch to the {template} template?',
        restoreTitle: 'Restore the {template} settings?',
        applyLead: '{template} rewrites the seven settings below, across every page, right away.',
        lead: 'Your channel carries {from}. {to} rewrites its appearance across every page, right away.',
        restoreLead: 'The settings you changed since will go back to their {template} values.',
        changes: 'What changes',
        changed: '{field}: {from} → {to}',
        unchanged: '{field}: {value} (unchanged)',
        kept: 'What stays put',
        keptContent: 'Your content and your categories',
        keptDomain: 'Your domain and your address',
        keptSales: 'Your subscriptions and your sales',
        keptAccess: 'Your access settings',
        keptPages: 'Your other pages',
        colorTitle: 'Take the template\'s colour too',
        colorHint: 'Unticked, your current main colour is kept.',
        homepageTitle: 'Replace my home page as well',
        homepageHint:
          'Your channel holds {count} pieces of content: the home page will be composed as {shape}, from what it holds today.',
        homepageWarningOne:
          'The block on your home page will be replaced. An unpublished copy is kept as "Home — before {template}": you can put it back at any time from your pages.',
        homepageWarningMany:
          'Your {count} current blocks will be replaced. Everything you arranged in the editor disappears from the live page. An unpublished copy is kept as "Home — before {template}": you can put it back at any time from your pages.',
        apply: 'Apply {template}',
      },
      editorHint: {
        label: 'These settings come from the channel template',
        change: 'Change',
      },
      applied: {
        simple: 'Your channel now carries {template}.',
        withHomepage:
          'Your channel now carries {template}. The previous home page is kept as "{backup}".',
        error: 'The template could not be applied.',
      },
    },
    publication: {
      title: 'Publication',
      description: 'Control the visibility of your website.',
      online: 'Online',
      offline: 'Offline',
      draft: 'Draft',
      onlineHint: 'Your website is publicly accessible',
      maintenance: 'Under maintenance',
      maintenanceHint: 'Your site is online but closed: visitors see the maintenance page. Bring it back online from the dashboard.',
      offlineHint: 'Your website is not accessible to visitors',
      draftHint: 'Your channel has never been online: nobody can see it until you switch it on',
      // U7: a fee is the whole month, charged at its anniversary and never prorated.
      feeStops: 'Taking it offline, the current month remains due; nothing more is charged for the channel after it.',
    },
    general: {
      title: 'General information',
      description: 'Basic details about your website.',
      displayName: 'Display name',
      displayNamePlaceholder: 'My Website',
      descriptionField: 'Description',
      descriptionPlaceholder: 'A brief description of your website',
      thumbnailHelper: 'Recommended: 1280x720px, JPG or PNG',
    },
    authentication: {
      title: 'Authentication',
      description: 'Configure access control for your website.',
      modeChange: {
        title: 'Change the access mode?',
        confirm: 'Change mode',
        membersLose: '{count} member will lose their access: signing in disappears from the site and their rights on restricted content stop applying. Their account and rights are kept and come back if you restore the Users mode. | {count} members will lose their access: signing in disappears from the site and their rights on restricted content stop applying. Their accounts and rights are kept and come back if you restore the Users mode.',
        accountsGo: 'Signing in and signing up disappear from the site.',
        passwordWhole: 'Every visitor will have to type the password to see the site.',
        passwordPartial: 'Restricted content will only open with the password.',
        signInRequired: 'Visitors who are not signed in will no longer see anything of the site: they will need an account.',
        passwordGone: 'The shared password is removed: visitors who typed it will no longer use it.',
        generic: 'Visitors will see the site under the new mode as soon as it is saved.',
      },
      modeTitle: 'Access mode',
      modeDescription: 'Choose how visitors access your website.',
      monetizationAlert: 'Monetization is active on this website. Some authentication modes may be restricted.',
      mode: {
        public: 'Public',
        publicDescription: 'No authentication required. All content is publicly accessible.',
        publicDisabledMonetization: 'Public mode is not available when monetization is active.',
        password: 'Password',
        passwordDescription: 'Visitors must enter a shared password to access content.',
        passwordDisabledMonetization: 'Password mode is not available when monetization is active.',
        usersOptional: 'Users (optional)',
        usersOptionalDescription: 'Visitors can browse freely and create an account to access additional content.',
        usersRequired: 'Users (required)',
        usersRequiredDescription: 'Visitors must create an account and log in to access content.',
      },
      passwordTitle: 'Password',
      passwordSubtitle: 'Set a shared password for visitors.',
      password: 'Shared password',
      passwordPlaceholder: 'Enter a password',
      passwordCopy: 'Copy the password',
      passwordCopied: 'Password copied',
      passwordKeep: 'Unchanged — leave empty to keep the one in place',
      passwordRequired: 'Set a shared password to enable password-protected mode.',
      passwordUnreadable: 'This password was saved before the back office kept a readable copy: type it again to be able to show it here.',
      passwordWholeSite: 'Protect the whole channel',
      passwordWholeSiteOn: 'The password is asked as soon as a visitor arrives.',
      passwordWholeSiteOff: 'The home and the catalogue stay public; only members-only content asks for the password.',
      usersTitle: 'User accounts',
      usersSubtitle: 'Configure user registration and login settings.',
      allowRegistration: 'Allow registration',
      allowRegistrationHint: 'Users can create their own accounts',
      requireTwoFactor: 'Require two-factor authentication',
      requireTwoFactorHint: 'Every member will confirm their sign-in with a one-time code from their authenticator app.',
      loginBackground: 'Login page background',
      loginBackgroundHint: 'Shown behind the channel\'s sign-in and registration pages.',
    },
    options: {
      display: {
        title: 'Display',
        description: 'Control what is shown on your website.',
        shareBtn: 'Share button',
        shareBtnHint: 'Display a share button on content pages',
        embed: 'Embedding on other sites',
        embedHint: 'Let a content\'s player be pasted on a third-party site, and offer its code in the share button',
        viewsCount: 'View count',
        viewsCountHint: 'Display the number of views on content',
      },
      player: {
        title: 'Player',
        description: 'Set how the player behaves on content pages.',
        autoplay: 'Autoplay',
        autoplayHint: 'Videos start on their own, muted, when a content page opens',
        continueWatching: 'Continue watching',
        continueWatchingHint: 'Show playback progress and the “Continue watching” row, so viewers pick up where they left off',
      },
      interactions: {
        title: 'Interactions',
        description: 'Enable visitor engagement features.',
        likes: 'Likes',
        likesHint: 'Allow visitors to like content',
        comments: 'Comments',
        commentsHint: 'Allow visitors to comment on content',
        commentsModeration: 'Comment moderation',
        commentsModerationHint: 'Require approval before comments are published. When off, comments are published automatically.',
      },
      access: {
        title: 'Access',
        description: 'How your visitors get to the contents that are not open to them.',
        accessRequest: 'Access requests',
        accessRequestHint: 'Adds an "On request" level to your contents: a signed-in visitor asks for access, and you grant or refuse it from Users → Access requests.',
        requiresMembers: 'Requires member accounts.',
        requiresMembersLink: 'Set the channel\'s access',
      },
      contact: {
        title: 'Contact',
        description: 'Configure the contact form on your website.',
        contactForm: 'Contact form',
        contactFormHint: 'Display a contact form on your website',
        contactFormRecipient: 'Recipient email',
        contactFormRecipientPlaceholder: "contact{'@'}example.com",
        contactFormRecipientHint: 'Leave empty to use the administrator\'s email address',
      },
      content: {
        title: 'Content management',
        description: 'Configure content features on your website.',
        pageBuilder: 'Page builder',
        pageBuilderHint: 'Enable the drag-and-drop page builder',
        blog: 'Blog',
        blogHint: 'Enable a blog / articles section on your website',
      },
      upload: {
        title: 'File submissions',
        description: 'Allow members to send you content.',
        assetsInbox: 'Drop box',
        assetsInboxHint: 'Allow members to submit files to your channel',
        inboxWorkspace: 'Destination workspace',
        inboxWorkspaceHint: 'What members send lands in this workspace of your library, waiting for review.',
        inboxWorkspaceSuspended: 'This workspace is in the trash: the channel\'s inbox is closed. Choose another workspace.',
        inboxWorkspaceTrashed: '{name} (in the trash)',
      },
      newsletter: {
        title: 'Newsletter',
        description: 'Email subscription settings.',
        newsletter: 'Newsletter',
        newsletterHint: 'Allow visitors to subscribe to your newsletter',
      },
    },
    notifications: {
      userEmails: {
        title: 'Emails sent to users',
        description: 'Configure the automatic emails your users receive.',
        account: {
          title: 'Account',
          welcomeEmail: 'Welcome email',
          welcomeEmailHint: 'Sent when a new user registers — or, on a channel that approves sign-ups, when you approve theirs',
          accountExpired: 'Account expired',
          accountExpiredHint: 'When a user account reaches its expiration date',
        },
        purchases: {
          title: 'Purchases & Subscriptions',
          purchaseConfirmation: 'Purchase confirmation',
          purchaseConfirmationHint: 'Receipt sent after a successful purchase',
          newSubscription: 'New subscription',
          newSubscriptionHint: 'Subscription creation confirmation',
          subscriptionRenewal: 'Subscription renewal',
          subscriptionRenewalHint: 'Automatic renewal confirmation',
          subscriptionExpiring: 'Subscription expiring soon',
          subscriptionExpiringHint: 'Reminder before subscription expiration',
          configureDelay: 'Configure',
          expiringDelayLabel: 'Reminder delay',
          expiringDelaySuffix: 'days before expiration',
          expiringDelayHint: 'The user will be notified this many days before their subscription expires',
          subscriptionCancelled: 'Subscription cancelled',
          subscriptionCancelledHint: 'Cancellation confirmation',
          rentalExpired: 'Rental expired',
          rentalExpiredHint: 'When a rental period ends',
        },
        content: {
          title: 'Content & Access',
          accessRequestApproved: 'Access request approved',
          accessRequestApprovedHint: 'When an admin approves a request',
          accessRequestDenied: 'Access request denied',
          accessRequestDeniedHint: 'When an admin denies a request',
          accessGranted: 'Access granted',
          accessGrantedHint: 'When direct access is granted (outside of a request)',
        },
      },
      adminAlerts: {
        title: 'Admin alerts',
        description: 'Choose which events you want to be notified about.',
        newUserRegistered: 'New user registered',
        newUserRegisteredHint: 'When a user registers on the website',
        invitationAccepted: 'Invitation accepted',
        invitationAcceptedHint: 'When an invited user accepts the invitation',
        newPaymentReceived: 'New payment received',
        newPaymentReceivedHint: 'When a payment is confirmed',
        paymentFailed: 'Payment failed',
        paymentFailedHint: 'When a payment fails',
        accessRequestSubmitted: 'Access request submitted',
        accessRequestSubmittedHint: 'When a user submits an access request',
        contactFormSubmitted: 'Contact form submitted',
        contactFormSubmittedHint: 'When a visitor submits the contact form',
      },
      history: {
        title: 'Notification history',
        description: 'View sent notifications.',
        emptyTitle: 'No notifications sent',
        emptySubtitle: 'Sent notifications will appear here once the system is activated.',
        searchPlaceholder: 'Search by subject or recipient...',
        columns: {
          date: 'Date',
          recipient: 'Recipient',
          subject: 'Subject',
          status: 'Status',
        },
        statuses: {
          sent: 'Sent',
          queued: 'Queued',
          failed: 'Failed',
        },
      },
      validation: {
        expiringDaysRange: 'The reminder delay must be between 1 and 90 days',
      },
      loadError: 'Failed to load the notifications. Please try again.',
    },
    monetization: {
      deactivateSuccess: 'Monetization turned off',
      activateSuccess: 'Selling is open',
      landing: {
        title: 'Sell your content straight from your channel',
        subtitle:
          'One-off purchases, rentals, subscriptions. Payments are collected on a payment account in your name and paid out to your bank account.',
        startButton: 'Request activation',
        approvedSubtitle:
          'Your request was approved. Open selling, then set up your payment account to get paid.',
        openButton: 'Open selling',
        features: {
          commission: {
            title: 'Simple commission',
            description: 'One all-in percentage on each sale, with no fixed fee and no subscription.',
          },
          secure: {
            title: 'Secure payments',
            description: 'Our payment partner handles collection, compliance and fraud prevention.',
          },
          instant: {
            title: 'Instant access',
            description: 'Buyers get access as soon as the payment is confirmed.',
          },
          analytics: {
            title: 'Sales tracking',
            description: 'Revenue, subscribers and best-selling content, at a glance.',
          },
        },
        howItWorksTitle: 'How it works',
        howItWorksSubtitle: 'From request to first sale, in four steps.',
        howItWorks: {
          steps: {
            configure: {
              title: 'Tell us about your company',
              description: 'The details that will appear on your buyers\' invoices.',
            },
            request: {
              title: 'Get your request approved',
              description: 'Our team reviews it within 2 business days.',
            },
            connectStripe: {
              title: 'Open your payment account',
              description: 'Identity document, proof of company registration and bank details.',
            },
            startSelling: {
              title: 'Set your prices and sell',
              description: 'Your revenue is paid straight into your bank account.',
            },
          },
        },
      },
      pricing: {
        title: 'What it costs',
        subtitle: 'No upfront fee: you only pay on what you sell.',
        commission: {
          title: '{platform} commission',
          description: 'Taken on each sale, payment fees included. No fixed fee, no subscription.',
          features: {
            noSetupFees: 'No setup fee',
            noMonthlyFees: 'No monthly subscription',
            payAsYouEarn: 'You only pay when you sell',
          },
        },
        stripe: {
          title: 'Payment fees',
          fees: 'Included',
          description: 'Included in the commission: nothing else is taken from your sales.',
          features: {
            securePayments: 'Secure, compliant payments',
            globalReach: 'International cards accepted',
            automaticTaxes: 'Automatic VAT handling',
          },
        },
        example: {
          title: 'Example on a €10 sale',
          description:
            'On a €10 sale including tax, only the {commission} commission is taken, payment fees included. The rest is yours.',
        },
      },
      faq: {
        title: 'Frequently asked questions',
        subtitle: 'What people ask us most before getting started.',
        items: {
          whoCanSell: {
            question: 'Can I sell as a private individual?',
            answer:
              'No. Your sales are invoiced in the name of an entity registered in France and identified by its SIREN: a company, a non-profit or a sole trader (micro-entrepreneurs included). Not a French entity? Contact us directly at support{\'@\'}videas.fr to discuss it. Once your request is approved, you register that entity as the seller, then our payment partner verifies your identity and the bank account that will receive the payouts.',
          },
          whenPaid: {
            question: 'When do I get paid?',
            answer:
              'Each payment is collected on your payment account, with the {platform} commission already deducted, then paid out automatically to the bank account you gave when your file was verified: there is nothing for you to do.',
          },
          howLong: {
            question: 'How long does activation take?',
            answer:
              'Our team first reviews your request, and its status is shown on this page. Once it is approved, you register your entity, complete your verification file and sign the invoicing mandate. Verification takes from a few minutes to a few days depending on the documents requested.',
          },
          refunds: {
            question: 'How do I issue a refund?',
            answer:
              'From Sales → Transactions, with the sale\'s "Refund…" action: full or partial, the refund is taken from your funds and cannot be undone. The {platform} commission taken on the sale is not returned. A full refund of a purchase or a rental removes access to the content; refunding a subscription payment does not cancel the subscription.',
          },
        },
      },
      wizard: {
        previous: 'Back',
        next: 'Continue',
        identity: {
          step: 'Identity',
          title: 'Who is selling?',
          subtitle:
            'These details will appear on the invoices given to your buyers and are checked by our payment partner. They must match exactly the registered entity that will collect the payments.',
          entityRequiredTitle: 'Selling requires a registered entity',
          entityRequiredDescription:
            'You cannot collect payments as a private individual. You need a registered company, non-profit or sole trader. Our payment partner checks these details against official registries before opening sales: an incomplete or inaccurate identity gets the file rejected.',
          allRequired: 'Every field is required.',
          required: 'This field is required.',
          legalName: 'Legal name',
          legalForm: 'Legal form',
          legalFormPlaceholder: 'Ltd, LLC, non-profit…',
          registrationNumber: 'Company registration number',
          registrationNumberHint: 'Your entity\'s registration number.',
          taxId: 'VAT number',
          addressLine1: 'Registered address',
          postalCode: 'Postal code',
          city: 'City',
          country: 'Country',
          invoiceEmail: 'Billing email',
          invoiceEmailHint: 'Where your invoices and sales statements will be sent.',
          invoiceEmailInvalid: 'This email address is not valid.',
          invoiceEmailReservedDomain:
            'This domain is not accepted: reserved test domains (.test, .local, .example) cannot receive email.',
          rejected: 'This value was refused. Check it and send the request again.',
          sharedTitle: 'Once per organization',
          sharedDescription:
            'Your other channels will reuse this identity and the same payment account. You will not have to do this again.',
        },
        activity: {
          step: 'Activity',
          title: 'Your activity',
          subtitle: 'What you plan to sell, and in which currency.',
          currency: 'Selling currency',
          taxCountry: 'Country of taxation',
          description: 'What do you sell?',
          descriptionHint: 'Sent to our payment partner to verify your account.',
          salesModes: 'Sale types you plan to use',
          allModes: 'Once selling is approved, you can sell by purchase, rental and subscription.',
          currencyEur: 'Euro (EUR)',
          countryFr: 'France',
          modes: {
            purchase: { label: 'One-off purchase', hint: 'Permanent access to the content' },
            subscription: { label: 'Subscription', hint: 'Recurring access to the catalogue' },
            rental: { label: 'Rental', hint: 'Time-limited access' },
          },
        },
        review: {
          step: 'Review',
          title: 'Check and send',
          subtitle: 'One last look before our team reviews your request.',
          requestButton: 'Send request',
          stripeInfo: {
            title: 'What happens next',
            description:
              'Once our team approves it, you will be invited to open your payment account — identity document, proof of company registration and bank details. It takes about ten minutes.',
          },
        },
      },
      request: {
        recapTitle: 'Your request',
      },
      // The doors into the payment provider, beside the mirror of what it said.
      account: {
        title: 'Set up your payments',
        intro: 'To get paid for your sales, {platform} opens a payment account in your organization\'s name. The money your viewers pay goes to that account, then to your bank account. Allow about ten minutes, in three steps, all from this page.',
        noPermission: 'The payment account that collects sales is your organization\'s: only someone who manages billing can set it up.',
        unavailable: 'The invoicing service is not answering right now. Try again later.',
        readError: 'Your payment account status could not be read. Try again.',
        ready: 'Your organization can take and invoice sales.',
        notReady: 'Your organization cannot take sales yet.',
        steps: {
          identity: 'Your business',
          payments: 'Your payment account',
          mandate: 'Permission to invoice',
        },
        identity: {
          description: 'Tell us which business you sell as: it will appear on your viewers\' invoices. The SIREN cannot be changed afterwards.',
          start: 'Enter my business details',
          done: 'Your business is registered.',
          legalName: 'Legal name',
          tradeName: 'Trade name (optional)',
          legalForm: 'Legal form (optional)',
          siren: 'SIREN',
          sirenHint: 'Nine digits.',
          sirenInvalid: 'This is not a valid SIREN: check the nine digits.',
          line1: 'Registered office address',
          line2: 'Address line 2 (optional)',
          postalCode: 'Postal code',
          city: 'City',
          invoiceEmail: 'Address that receives invoices (optional)',
          invoiceEmailHint: 'Where copies of the invoices issued in your name are sent.',
          vatLiable: 'My business is liable to VAT',
          vatLiableHint: 'Untick if you are under the VAT franchise (franchise en base).',
          vatNumber: 'Intra-community VAT number (optional)',
          vatPlaceholder: 'FR12345678901',
          submit: 'Save and continue',
          created: 'Your business is registered. Next step: your payment account.',
          error: 'Your business could not be registered.',
        },
        payments: {
          description: 'Our payment partner, Stripe, collects your sales and pays them out to you. It verifies your account on its secure form, then brings you back here: have an ID document and the IBAN your payouts go to ready.',
          onboarding: 'Set up my payment account',
          update: 'Edit my details',
          dashboard: 'Open my Stripe dashboard',
          verifying: 'Your file is being verified. If you sent everything, there is nothing for you to do: it takes from a few minutes to 2 business days. You can already give the permission to invoice below. If you stopped part way, pick your file up where you left it.',
          resume: 'Resume my file',
          refresh: 'Refresh',
          actionNeeded: 'Our payment partner needs more information to finish verifying your account. Reopen your file to see what is missing and send it.',
          complete: 'Complete my file',
          done: 'Your payment account is active: you can take sales.',
          linkError: 'The link could not be created. Try again.',
          due: 'Still to provide:',
          dueBy: 'Still to provide, before {date}:',
          nothingDue: 'You have nothing more to provide.',
          requirements: {
            individual_verification_document: 'An identity document',
            business_profile_url: 'The address of your business\'s website',
            external_account: 'The IBAN your payouts go to',
            tos_acceptance_date: 'Accepting the payment partner\'s terms',
          },
        },
        mandate: {
          description: 'Last step: allow Videas to send invoices to your viewers in your name, so you have no invoices to issue yourself.',
          text: 'I authorise Videas to issue, in my name and on my behalf, the invoices and credit notes of my sales to the viewers of my channels, and to keep a copy of them. This mandate may be revoked at any time through support.',
          accept: 'I allow Videas to invoice',
          accepted: 'Mandate accepted on {date} (version {version}).',
          signed: 'The mandate is accepted.',
          unknown: 'The invoicing service is not answering: the mandate\'s state is unknown.',
          error: 'The mandate could not be recorded.',
        },
        vat: {
          title: 'VAT',
          liable: 'Liable to VAT',
          exempt: 'VAT franchise',
          unknown: 'Not known yet',
          change: 'Declare a change',
          newStatus: 'My business is now liable to VAT',
          effectiveFrom: 'Effective date',
          effectiveFromHint: 'The day the change took effect, never in the future.',
          submit: 'Declare',
          declared: 'The VAT change is declared.',
          error: 'The change could not be declared.',
        },
      },
      seller: {
        onboarding: 'Your verification file',
        note: 'This comes from our payment partner, as of the last sync. It alone decides whether a payment is accepted.',
        status: {
          none: 'Not opened yet',
          pending: 'In progress',
          complete: 'Complete',
          restricted: 'Restricted',
        },
      },
      track: {
        submitted: 'Request sent',
        review: '{platform} review',
        reviewPending: 'In progress',
        stripe: 'Payment account',
        stripePending: 'Nothing left to provide | 1 item to provide | {count} items to provide',
        stripeVerifying: 'Being verified',
        stripeBlocked: 'Account suspended',
        stripeDone: 'Complete',
        stripePendingSeller: 'Your turn: set up your account',
        selling: 'First sale',
      },
      pending: {
        title: 'Request sent on {date}',
        description:
          'Our team reviews it within 2 business days. You will get an email as soon as it is handled.',
        editBtn: 'Edit request',
        cancelBtn: 'Withdraw request',
        toast: 'Request sent',
        cancelled: 'Request withdrawn',
      },
      rejected: {
        title: 'Request declined on {date}',
        retryBtn: 'Fix and resend',
      },
      setup: {
        notStarted: {
          title: 'Your request is approved',
          description:
            'One step left: set up your payment account. Have an identity document, a proof of company registration and your bank details ready.',
          action: 'Set up my payments',
        },
        incomplete: {
          title: 'Setup left unfinished',
          description:
            'You started setting up your payments but did not reach the end. Your answers were kept — pick up where you left off.',
          action: 'Resume setup',
        },
        verifying: {
          title: 'Your information is being verified',
          description:
            'Nothing to do on your side. Verification usually takes a few minutes, sometimes up to two business days. We will email you as soon as sales open — you can already set your prices.',
          action: 'View my file',
        },
        blocked: {
          title: 'Your payment account is suspended',
          description:
            'Your payments are stopped. Contact our support: we look into the reason with our payment partner and help you appeal the decision.',
          action: 'Contact support',
        },
        remainingTitle: 'What is left to provide',
        remainingSubtitle: 'Around ten minutes, with your documents at hand.',
        remainingNote:
          'More may be asked of you depending on your situation. The exact list appears during setup.',
      },
      restricted: {
        title: 'Your payouts are waiting on a document',
        description:
          'Your sales carry on as usual and your funds are held. They will be paid out as soon as your file is complete.',
        descriptionDated:
          'Your sales carry on as usual and your funds are held. They will be paid out as soon as your file is complete — due before {date}.',
        fixBtn: 'Complete my file',
      },
      stripe: {
        title: 'Payment account',
        leavingNotice:
          'You will move to the secure form of our payment partner, Stripe, during setup, then come back here automatically. Your prices and content stay available in the meantime.',
        chips: {
          notConnected: 'Not set up',
          chargesOn: 'Payments active',
          chargesOff: 'Payments blocked',
          payoutsOn: 'Payouts active',
          payoutsOff: 'Payouts on hold',
        },
        // Families of information Stripe asks for — never the raw keys.
        requirements: {
          personalInfo: {
            label: 'Your personal information',
            hint: 'The director\'s name, date of birth and address.',
          },
          identityDocument: {
            label: 'Director\'s identity document',
            hint: 'A valid ID card, passport or residence permit.',
          },
          companyDocument: {
            label: 'Proof of company registration',
            hint: 'Your registration certificate, or its equivalent in your country.',
          },
          businessInfo: {
            label: 'Company information',
            hint: 'Legal name, registered address, industry and website.',
          },
          bankAccount: {
            label: 'Bank details',
            hint: 'The account your revenue will be paid into.',
          },
          terms: {
            label: 'Partner terms acceptance',
            hint: 'A checkbox at the end of the flow.',
          },
          other: {
            label: 'Additional information',
            hint: 'You will be asked for it during setup.',
          },
        },
      },
      tax: {
        title: 'Your tax and social obligations',
        description: 'Sums you receive from your sales are taxable and must be declared to the tax authorities. Depending on your situation, they may also give rise to social contributions. {platform} makes no declaration on your behalf.',
        summaryHint: 'Every January we send you a summary of the past year\'s transactions. It stays available under Sales → Revenue & invoicing.',
      },
      notifications: {
        title: 'Your notifications',
        newSale: 'Notify me of each sale',
        newSaleHint:
          'A "New sale" email and a line in your notifications for each purchase, rental or new subscription — not for renewals. This setting is yours alone, and for this channel only: {settings}, in your account settings, turns it off on all your channels.',
        settingsLink: 'Notifications',
      },
      contributions: {
        sectionTitle: 'Checkout',
        title: 'Support contribution',
        subtitle:
          'At checkout, your buyers can add the amount of their choice (€1, €2 or €5, or an amount of their own) to support your channel. It is paid to you with their purchase.',
        unavailable: "Available once the channel's monetization is approved.",
      },
      settings: {
        active: 'Monetization active',
        activeDescription: 'Your channel can collect payments.',
        generalTitle: 'Terms',
        seller: 'Seller',
        lastSync: 'Last synced',
        currencyHint:
          'Currency and terms are set at activation. Contact support to change them.',
        stripeAccountId: 'Account ID',
        stripeDashboard: 'Stripe dashboard',
        quickActionsTitle: 'What now',
        managePrices: 'Set your prices',
        manageSubscriptions: 'Subscriptions',
        manageCoupons: 'Promo codes',
        salesReports: 'Sales',
        statements: 'Statements & invoices',
        dangerWarning:
          'Turning monetization off cuts access to paid content for your current subscribers.',
        deactivateBtn: 'Turn off monetization',
        deactivateTitle: 'Turn off monetization?',
        deactivateConfirm:
          'Paid content will no longer be accessible and no new sale will be possible.',
        deactivateIrreversible:
          'Your current subscribers will immediately lose access to paid content.',
        deactivateTypePrompt: 'Type "{word}" to confirm:',
      },
      errors: {
        submit: 'Sending the request failed. Try again in a moment.',
        submitInvalid: 'The request was refused: one of the details you entered is not accepted.',
        submitInvalidField: 'The request was refused — fix "{field}", the field is flagged above.',
        cancel: 'Withdrawing the request failed. Try again in a moment.',
        onboarding: 'The setup could not be opened. Try again in a moment.',
        dashboard: 'Stripe could not open the dashboard. Try again in a moment.',
        deactivate: 'Turning monetization off failed. Try again in a moment.',
        activate: 'Opening selling failed. Try again in a moment.',
        needsMembers: 'Selling needs member accounts: a buyer needs an account to find what they bought. Turn them on in Settings › Authentication, then open selling.',
      },
    },
    danger: {
      title: 'Danger zone',
      description: 'Actions that remove things from the site',
      deleteTitle: 'Delete this website',
      deleteDescription: 'Once deleted, the site is no longer reachable by its visitors, and its items, pages and members are deleted with it.',
      deleteButton: 'Delete website',
      deleteDone: 'Website deleted',
      deleteFailed: 'Deleting failed.',
    },
  },
  domain: {
    title: 'Domain',
    subtitle: 'Manage your website domains.',
    addButton: 'Add a domain',
    primaryDomain: {
      title: 'Primary domain',
      description: 'The primary domain is displayed in the address bar of your website.',
      empty: 'No primary domain',
      emptyDescription: 'Add a custom domain or use a free subdomain of {suffix}.',
      emptyDescriptionNoSuffix: 'Add a custom domain or use a free subdomain of the platform.',
    },
    secondaryDomains: {
      title: 'Secondary domains',
      description: 'These domains automatically redirect to your primary domain.',
    },
    platformDomains: {
      title: 'Platform domains',
      description: 'Domains provided by the Videas platform.',
    },
    customDomains: {
      title: 'Custom domains',
      description: 'Your own domains pointing to this website.',
      empty: 'No custom domains',
      emptyDescription: 'Add a custom domain to use your own URL for this website.',
      addFirst: 'Add your first domain',
    },
    helpBanner: {
      text: 'Need help configuring your domain?',
      button: 'Open the help centre',
    },
    readiness: {
      switched_off: 'Switched off',
      redirecting: 'Redirecting',
      awaiting_dns: 'DNS required',
      awaiting_certificate: 'Certificate pending',
      serving: 'Live',
    },
    refusal: {
      malformed: 'That is not a valid domain name.',
      connected: 'This channel already answers on that address.',
      taken: 'Another channel already answers on that address.',
      other_organization: 'That name already belongs to another organization.',
      reserved: 'That name was released recently and stays reserved for thirty days.',
      segmented: 'That name is already shared by language, or is not shared at all: the two do not mix.',
    },
    setActiveError: 'That name could not be switched.',
    badges: {
      primary: 'Primary',
      active: 'Active',
      inactive: 'Inactive',
      platform: 'Platform',
      custom: 'Custom',
      online: 'Online',
      dnsRequired: 'DNS required',
      verifying: 'Verifying…',
      billingExempt: 'Included',
      monthlyFee: '+{amount}/month',
    },
    // The custom name's monthly fee in its dunning (`DomainCard.vue`, `apps/billing/fees.ts`).
    fee: {
      active: 'Active · next charge on {date}',
      unpaid:
        'Unpaid · offline on {date} without a top-up | Unpaid · offline in 1 day, on {date}, without a top-up | Unpaid · offline in {n} days, on {date}, without a top-up',
      offline: 'Offline for non-payment · a top-up brings it back until {date}',
      topUp: 'Top up',
      topUpReactivate: 'Top up and reactivate',
    },
    card: {
      ssl: {
        active: 'SSL active',
        pending: 'SSL pending',
        none: 'No SSL',
      },
      dns: {
        verified: 'DNS verified',
        pending: 'DNS pending',
      },
      actions: {
        setPrimary: 'Set as primary',
        verifyDns: 'Check status',
        configureDns: 'Configure DNS',
        viewDns: 'View DNS instructions',
        toggleDetails: 'Show details',
        delete: 'Remove',
        switchOn: 'Switch back on',
        switchOff: 'Switch off',
      },
      pendingMessage: 'DNS configuration pending. Your domain will be active once DNS records are verified.',
      createdAt: 'Verified on',
      steps: {
        dns: 'DNS',
        ssl: 'SSL certificate',
        active: 'Active',
      },
      stepMessages: {
        not_required: 'Nothing to set up: this name is the platform\'s own.',
        awaiting_dns: 'Add the records below at your provider. Verification happens on its own.',
        records_mismatch: 'That name points somewhere other than our servers.',
        unpublished: 'The addresses to point at are not published on this installation.',
        verified: 'The name points at us.',
      },
      mismatch: 'That name answers {resolved}, where it should answer {expected}.',
      checkedAt: 'Checked on {date}',
      certificate: {
        platform_wildcard: 'The platform certificate covers this name.',
        per_host: 'This name needs its own certificate, issued by our team. Nothing to do on your side.',
      },
    },
    billing: {
      domains: 'no connected address included | including {n} connected address | including {n} connected addresses',
      online: 'The channel is online: this amount is debited every month.',
      offline: 'The channel is offline: nothing is debited while it serves nobody.',
      exempt: 'This channel went online before publishing became paid: it stays free, its custom addresses included. Nothing is charged.',
      channelNotBilled: 'The channel itself is not billed.',
      nothingDue: 'The channel is online: nothing is debited while this total stays at zero.',
    },
    // Connecting a custom name on a channel online (`components/domain/DomainConnectForm.vue`): the
    // wallet against the next month of every fee the channel will run.
    confirmCharge: {
      title: 'Connect {host}',
      summary: 'This domain is added to your channel’s monthly fees, debited from your wallet.',
      atPublication: 'Billed when the channel is published',
      billedNow: 'Already billed every month',
      theChannel: 'The channel',
      domainLine: 'Domain {host}',
      channelAndDomains: 'the channel and {n} active domain | the channel and {n} active domains',
      domainsOnly: '{n} active domain | {n} active domains',
      thisDomain: 'New domain',
      newTotal: 'New monthly total',
      balanceNow: 'Current balance',
      debitedNow: 'Debited on connection',
      nothingNow: 'Nothing for now',
      balanceAfter: 'Balance after',
      fineprintOnline: 'The domain’s first month is debited when you connect, then every month on the same date. Removing the domain stops the fee: the current month stays due, nothing is charged after.',
      fineprintOffline: 'Your channel is offline: nothing is debited now. The fees start when it is published, for the channel and its domains.',
      confirm: 'Confirm and connect',
    },
    connectBilling: {
      alreadyBilled: 'Already billed for this channel',
      theChannel: 'the channel',
      missing: 'Your balance does not cover next month of the channel’s fees ({total}).',
      neverFunded: 'Your wallet has not been funded yet: the credit you buy pays for the channel, its addresses and your usage.',
      dunning: 'Without credit, the domain becomes unpaid. It is taken offline after 14 days, and can be reactivated for 30 days.',
      channelOffline: 'Nothing is billed for now: the fees start when the channel goes online.',
      topUpTitle: 'Top up the wallet',
      topUpAndConnect: 'Top up {amount} and connect',
      connectPaid: 'Connect · {amount}/month',
      firstMonthNow: 'The first month is debited when you connect, then every month.',
      creditPending: 'Payment accepted. The credit can take a minute to arrive — the address connects as soon as it does.',
      creditLate: 'Your payment went through, but the credit has not arrived yet. Try connecting the address again in a moment.',
    },
    editSubdomain: {
      title: 'Edit subdomain',
      description: 'Customize your Videas subdomain. The old subdomain will be kept as a redirect.',
      preview: 'Preview:',
      save: 'Save',
      success: 'Subdomain updated successfully',
      error: 'Failed to update subdomain',
      unavailable: 'This subdomain is already taken',
      available: 'Name available',
      checking: 'Checking availability...',
    },
    addModal: {
      title: 'Add a domain',
      subtitle: 'Choose the type of domain',
      preview: 'Preview:',
      setPrimary: 'Set as primary domain',
      setPrimaryHint: 'This domain will be displayed in the address bar',
      subdomain: {
        title: 'Videas subdomain',
        description: 'Quick and free — use a subdomain of {suffix}',
        descriptionNoSuffix: 'Quick and free — use a subdomain of the platform',
        free: 'Free',
        placeholder: 'my-website',
        activate: 'Activate',
        tooShort: 'Minimum 3 characters',
      },
      custom: {
        title: 'Custom domain',
        includedChip: 'Included',
        description: 'Connect an address you already own, for a professional look',
        placeholder: 'videos.example.com',
        addAndConfigure: 'Add and configure',
        invalidFormat: 'Invalid domain format',
        alreadyAdded: 'This domain is already added',
        apex: 'Root domain',
        subdomain: 'Subdomain',
      },
      dns: {
        title: 'DNS configuration required',
        description: 'Add the following DNS records at your domain provider to connect your domain.',
        name: 'Name:',
        value: 'Value:',
        copy: 'Copy',
        applies: {
          below_apex: 'If this name is not the root of its DNS zone',
          zone_apex: 'If this name is the root of its DNS zone (a CNAME is illegal there)',
        },
        providersTitle: 'Instructions by provider',
        otherProvider: 'Other',
        propagation: 'DNS propagation can take up to 48 hours (usually a few minutes).',
        ovh: {
          step1: 'Log in to your OVH control panel',
          step2: 'Go to "Domains" then "DNS Zone"',
          step3: 'Click "Add an entry"',
          step4: 'Add the records shown above',
        },
        cloudflare: {
          step1: 'Log in to your Cloudflare dashboard',
          step2: 'Select your domain',
          step3: 'Go to "DNS" then "Records"',
          step4: 'Add the records shown above',
        },
        gandi: {
          step1: 'Log in to your Gandi account',
          step2: 'Go to "Domains" then "DNS Records"',
          step3: 'Click "Add"',
          step4: 'Add the records shown above',
        },
        generic: {
          step1: 'Log in to your hosting provider\'s management interface',
          step2: 'Find the "DNS" or "DNS Zone" section',
          step3: 'Add the records shown above',
          step4: 'Save the changes',
        },
      },
      addSuccess: 'Domain added successfully',
    },
    dnsConfig: {
      title: 'DNS Configuration',
      description: 'To connect a custom domain, add the following DNS record at your domain provider:',
      recordType: 'Type',
      host: 'Host',
      value: 'Value',
      cnameHost: 'your-subdomain',
    },
    deleteConfirm: {
      title: 'Remove domain',
      message: 'Are you sure you want to remove this domain? This action cannot be undone.',
      // U7: a fee is the whole month, charged at its anniversary and never prorated.
      feeStops: 'The current month remains due; nothing more is charged for this domain after it.',
    },
    setPrimaryConfirm: {
      title: 'Set as primary domain',
      message: 'Are you sure you want to set this domain as the primary domain? The current primary domain will be replaced.',
    },
    deleteSuccess: 'Domain removed successfully',
    setPrimarySuccess: 'Primary domain updated successfully',
    verifySuccess: 'DNS verification launched',
    cannotDeletePrimary: 'Cannot remove the primary domain. Set another domain as primary first.',
    cannotDeletePlatform: 'Platform domains cannot be removed.',
    addError: 'Failed to add domain',
    deleteError: 'Failed to remove domain',
    setPrimaryError: 'Failed to set primary domain',
    verifyError: 'Failed to launch DNS verification',
    updateError: 'Failed to update domain',
    list: {
      title: 'Domains',
      description: 'Domains currently associated with your website.',
      empty: 'No domains',
      emptyDescription: 'No domain has been configured for this website yet.',
    },
    info: {
      title: 'Domain management',
      description: 'Custom domain management is handled by your administrator. Contact support if you need to add or modify a domain.',
    },
    table: {
      domain: 'Domain',
      type: 'Type',
      status: 'Status',
      createdAt: 'Created',
    },
  },
  groups: {
    subtitle: 'Manage access groups for your website.',
    searchPlaceholder: 'Search groups...',
    btnCreate: 'Create group',
    tabs: {
      access: 'Access',
    },
    table: {
      name: 'Name',
      members: 'Members',
      grants: 'Grants',
      createdAt: 'Created',
      expiresAt: 'Expires',
    },
    staleMessage: 'Somebody saved first and nothing was written. The record has just been re-read.',
    emptyState: {
      title: 'No groups',
      description: 'Create your first group to organize user access.',
      noResults: 'No groups found',
      noResultsDescription: 'Try adjusting your search terms.',
    },
    deleteTitle: 'Delete group',
    deleteMessage: 'Are you sure you want to delete "{name}"? All memberships will be removed.',
    deleteSuccess: 'Group deleted successfully',
    deleteError: 'Failed to delete group',
    picker: {
      label: 'Groups',
      placeholder: 'Select groups...',
      noResults: 'No groups found',
    },
    detail: {
      backToGroups: 'Back to groups',
      newGroup: 'New group',
      saveSuccess: 'Group updated successfully',
      saveError: 'Failed to update group',
      createSuccess: 'Group created successfully',
      createError: 'Failed to create group',
      nameTaken: 'Another live group of this channel already carries that name.',
      deleteSuccess: 'Group deleted successfully',
      deleteError: 'Failed to delete group',
      deleteConfirmMessage: 'Are you sure you want to delete this group?',
      invalidForm: 'Please fill in all required fields',
      saveGroupFirst: 'Save the group to enable this tab',
      expiredBadge: 'Expired',
      expiredAlert: 'This group has expired. Members no longer have access to the group\'s content.',
      tabs: {
        information: 'Information',
        members: 'Members',
        access: 'Access',
      },
      information: {
        name: 'Group name',
        namePlaceholder: 'e.g. Premium Members',
        groupTitle: 'Group',
        groupDescription: 'What this group is called in the back office, and what it is for.',
        fullAccessTitle: 'Full access',
        nameRequired: 'Group name is required',
        nameTooShort: 'Name must be at least 2 characters',
        nameTooLong: 'Name must not exceed 100 characters',
        description: 'Description',
        descriptionPlaceholder: 'Describe this group...',
        fullAccess: 'Access to all content',
        fullAccessHint: 'Every member of this group reaches all the channel\'s content, while the group is active and unexpired.',
        expirationTitle: 'Expiration',
        expiresAt: 'Expiration date',
        expiresAtHint: 'After this date, members of this group lose access to the content shared through this group. Their other access (direct grants, other groups, their own account) is unaffected. Leave empty for no expiration.',
        expiresAtUserNote: 'A user\'s own expiration date (set on their profile) takes precedence: an expired user loses access to the entire site, regardless of their groups.',
      },
      members: {
        title: 'Members',
        addMembers: 'Add members',
        searchUsers: 'Search users',
        searchUsersPlaceholder: 'Search by name or email...',
        addHint: 'People already in the group stay in it: select freely.',
        addAlreadyThere: '{changed} of {requested} added: the rest were already in the group.',
        noUsersFound: 'No users found',
        addSelected: 'Add ({count})',
        removeMember: 'Remove',
        removeConfirm: 'Remove this member from the group?',
        table: {
          user: 'User',
          joinedAt: 'Joined',
        },
        emptyTitle: 'No members yet',
        emptyState: 'Add members to grant them access to this group\'s content.',
        addSuccess: 'Member added successfully',
        addSuccessCount: '{count} member(s) added',
        addPartial: '{completed} added, {failed} failed',
        addError: 'Failed to add member',
        removeSuccess: 'Member removed successfully',
        removeError: 'Failed to remove member',
      },
      access: {
        title: 'Accessible content',
        fullAccessActive: 'This group already grants every publication: the grants below change nothing while that switch is on.',
        btnGrant: 'Grant access',
        bannerTitle: '{count} item accessible to {members} members | {count} items accessible to {members} members',
        bannerDescription: 'Items granted to this group are accessible to all its members.',
        emptyWithMembers: 'This group has {count} members but no restricted items with access. Grant access to start.',
        emptyTitle: 'No accessible items',
        emptyDescription: 'This group has no items with access. Use the button above to grant access to restricted items.',
        searchPlaceholder: 'Search items...',
        revokeConfirmTitle: 'Revoke access',
        revokeConfirmMessage: 'Are you sure you want to revoke access to "{title}" for this group?',
        revokeSuccess: 'Access revoked successfully',
        revokeError: 'Failed to revoke access',
        grantSuccess: 'Access granted successfully',
        grantError: 'Failed to grant access',
        published: 'Published',
        unpublished: 'Unpublished',
        selectedCount: '{count} item(s) selected',
        revokeSelection: 'Revoke selection',
        batchRevokeMessage: 'Are you sure you want to revoke access to {count} item(s) for this group?',
        batchRevokeSuccess: '{count} access(es) revoked successfully',
        batchRevokePartial: '{completed} revoked, {failed} failed',
        table: {
          title: 'Title',
          slug: 'Slug',
          status: 'Status',
        },
        grantDialog: {
          title: 'Grant item access',
          searchPlaceholder: 'Search restricted items...',
          noResults: 'No restricted items without access found',
          grantByCategory: 'Grant by category',
          categoryHint: 'Grant access to all items in the selected category.',
          grantItems: 'Grant access',
          grantCategory: 'Grant category',
        },
      },
      danger: {
        title: 'Danger zone',
        deleteTitle: 'Delete this group',
        deleteDescription: 'Once deleted, all memberships will be permanently removed.',
        deleteButton: 'Delete group',
      },
    },
  },
  subscriptions: {
    title: 'Subscriptions',
    subtitle: 'The plans your visitors subscribe to, and what each one unlocks.',
    tabs: {
      subscriptions: 'Subscriptions',
      coupons: 'Coupons',
    },
    btnCreate: 'Create subscription',
    featured: 'Featured',
    conflict: 'This plan was saved elsewhere while you were typing, and nothing was written. Reload, then send your changes again.',
    filterStatus: 'Status',
    searchPlaceholder: 'Search subscriptions...',
    filterAll: 'All',
    filterActive: 'On sale',
    filterInactive: 'Withdrawn',
    table: {
      name: 'Name',
      price: 'Price',
      term: 'Term',
      includes: 'Items',
      subscribers: 'Active subscribers',
      perPeriod: 'per {period}',
      fullAccess: 'Whole catalogue',
      itemCount: {
        zero: 'No items',
        one: '1 item',
        other: '{count} items',
      },
      renews: 'Renews',
      cycles: '{count} cycles',
      createdAt: 'Created',
      status: 'Status',
    },
    status: {
      notSoldOnline: 'Not sold online',
      active: 'On sale',
      inactive: 'Withdrawn',
    },
    emptyState: {
      title: 'No subscription plans',
      description: 'Create your first subscription plan to start monetizing your content.',
      noResults: 'No plans found',
      noResultsDescription: 'Try another status.',
    },
    deleteTitle: 'Delete subscription plan',
    deleteMessage: 'Delete "{name}"? The plan stops being sold; current subscribers keep the access they paid for.',
    deleteSuccess: 'Subscription plan deleted',
    periods: {
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      halfYearly: 'Half-yearly',
      yearly: 'Yearly',
      custom: 'Custom',
      oneTime: 'One-time',
    },
    detail: {
      newSubscription: 'New subscription',
      saveFirst: 'Save the plan to enable this tab',
      saveError: 'Saving the plan failed',
      createSuccess: 'Subscription plan created',
      updateSuccess: 'Subscription plan updated',
      tabs: {
        information: 'Information',
        items: 'Items',
        subscribers: 'Subscribers',
      },
      information: {
        basicTitle: 'Basic information',
        basicDescription: 'Name, address and description of the plan.',
        name: 'Plan name',
        namePlaceholder: 'e.g. Premium monthly',
        nameRequired: 'Plan name is required',
        nameTooShort: 'Name must be at least 2 characters',
        slug: 'Address',
        slugHint: 'Where the plan answers on your site. It stops following the name as soon as you edit it.',
        slugInvalid: 'An address is lowercase words joined by single hyphens, like "yearly-plan"',
        description: 'Description',
        descriptionPlaceholder: 'Describe this subscription plan...',
        thumbnailTitle: 'Illustration',
        thumbnailDescription: 'The picture the pricing page draws.',
        thumbnailHint: 'A picture from your library.',
        pricingTitle: 'Pricing',
        pricingDescription: 'What the plan costs, and what it unlocks.',
        currency: 'Currency',
        fullAccess: 'Access to the whole catalogue',
        fullAccessHint: 'Subscribers get every piece of content, new releases included. Otherwise, attach the included content from the Items tab.',
        billingTitle: 'Billing period',
        billingDescription: 'How long one cycle lasts: a count and a unit, never a number of days.',
        period: 'Period',
        periodCount: 'Count',
        periodUnit: 'Unit',
        periodEcho: 'Billed {price} per {period}.',
        periodInvalid: 'A cycle lasts at least one unit',
        advancedTitle: 'Term',
        advancedDescription: 'How many cycles the agreement runs for, and the trial before it.',
        autoRenew: 'Renews indefinitely',
        autoRenewHint: 'The subscription renews until the subscriber ends it.',
        maxIterations: 'Number of cycles',
        maxIterationsHint: 'That is {total} of access in total; a single cycle is a one-time payment.',
        maxIterationsInvalid: 'A plan runs for at least one cycle',
        trial: 'Free trial',
        trialHint: 'A first cycle given before the first charge.',
        trialNotSold: 'Not sold online: checkout does not offer free trials yet. Remove the trial to sell this plan.',
        trialCount: 'Length',
        trialUnit: 'Unit',
        trialInvalid: 'A trial lasts at least one unit',
        publicationTitle: 'Publication',
        publicationDescription: 'What the pricing page does with this plan.',
        isFeatured: 'Feature it',
        isFeaturedHint: 'This plan is drawn ahead of the others on the pricing page.',
        isActive: 'On sale',
        isActiveHint: 'This plan is visible and can be subscribed to.',
        withdrawnNote: 'Withdrawing a plan cancels no subscription: current subscribers keep their access until the term they paid for.',
      },
      items: {
        title: 'Items included',
        description: 'The publications this plan unlocks for its subscribers.',
        btnAdd: 'Add items',
        fullAccessNote: 'This plan unlocks the whole catalogue, new releases included: there is no list to keep.',
        missing: 'Item removed',
        remove: 'Remove from the plan',
        showMore: 'Show more ({shown} of {total})',
        duplicate: 'This item has just been added to the plan elsewhere; the list has been reloaded.',
        saveError: 'Saving the plan failed',
        emptyState: {
          title: 'No items in this plan',
          description: 'Add content to make it available to subscribers.',
        },
        addDialog: {
          title: 'Add items to the plan',
          searchPlaceholder: 'Search the catalogue...',
          noResults: 'No items',
          alreadyIn: 'Already included',
          add: 'Add',
        },
        addSuccess: 'Items added to the plan',
        removeSuccess: 'Item removed from the plan',
      },
      subscribers: {
        title: 'Subscribers',
        description: 'Users subscribed to this plan.',
        searchPlaceholder: 'Search subscribers...',
        stats: {
          activeSubscribers: 'Active subscribers',
          monthlyRevenue: 'Monthly revenue',
          cancelling: 'Leaving',
        },
        filterStatus: 'Status',
        filterAll: 'All',
        extendSuccess: 'Subscription extended.',
        extendError: 'Could not extend the subscription.',
        grantSuccess: 'Free access granted.',
        grantError: 'Could not grant free access.',
        grantNotMember: 'No member of this channel has this address: invite them first.',
        table: {
          user: 'User',
          status: 'Status',
          startDate: 'Start date',
          endDate: 'End date',
          autoRenew: 'Auto-renew',
        },
        status: {
          active: 'Active',
          cancelled: 'Cancelled',
          expired: 'Ended',
          paused: 'Paused',
          past_due: 'Payment pending',
        },
        emptyState: {
          title: 'No subscribers yet',
          description: 'Subscribers will appear here once users purchase this plan.',
        },
      },
      danger: {
        title: 'Danger zone',
        description: 'Irreversible and destructive actions',
        deleteTitle: 'Delete this plan',
        deleteDescription: 'The plan stops being sold and leaves the pricing page. No current subscription is cancelled.',
        deleteButton: 'Delete plan',
      },
    },
  },
  coupons: {
    title: 'Promo codes',
    subtitle: 'The discounts your buyers type at checkout, and what each one brought in.',
    conflict: 'This coupon was saved elsewhere while you were editing: nothing was written. Reload, then send your changes again.',
    deleteError: 'The coupon could not be deleted.',
    tabs: {
      subscriptions: 'Subscriptions',
      coupons: 'Coupons',
    },
    searchPlaceholder: 'Search coupons...',
    btnCreate: 'Create coupon',
    filterStatus: 'Status',
    filterAll: 'All',
    filterActive: 'Active',
    filterInactive: 'Inactive',
    table: {
      code: 'Code',
      discount: 'Discount',
      usage: 'Usage',
      expiration: 'Expiration',
      status: 'Status',
    },
    status: {
      active: 'Active',
      scheduled: 'Scheduled',
      inactive: 'Inactive',
      expired: 'Expired',
      exhausted: 'Limit reached',
    },
    emptyState: {
      title: 'No promo codes',
      description: 'Create your first promo code to offer discounts to your users.',
      noResults: 'No coupons found',
      noResultsDescription: 'Try adjusting your filters or search terms.',
    },
    deleteTitle: 'Delete coupon',
    deleteMessage: 'Are you sure you want to delete "{code}"? This action cannot be undone.',
    deleteSuccess: 'Coupon deleted successfully',
    unlimited: 'Unlimited',
    noExpiration: 'No expiration',
    detail: {
      newCoupon: 'New coupon',
      createSuccess: 'Coupon created',
      codeTaken: 'That code is already one of this channel\'s promo codes.',
      updateSuccess: 'Coupon updated',
      saveError: 'Saving the coupon failed',
      backToCoupons: 'Back to coupons',
      saveFirst: 'Save the coupon to enable this tab',
      tabs: {
        information: 'Information',
        usage: 'Usage',
      },
      information: {
        basicTitle: 'Basic information',
        basicDescription: 'Code and description.',
        code: 'Coupon code',
        codePlaceholder: 'e.g. SUMMER2024',
        codeRequired: 'Coupon code is required',
        codeHint: 'Uppercase letters, numbers, and hyphens only. The code, the discount and the start date cannot change once the coupon is created.',
        codeInvalid: 'A code holds only letters, numbers and hyphens',
        percentInvalid: 'A percentage is between 1 and 100',
        endBeforeStart: 'The end date is before the start date',
        generateCode: 'Generate',
        name: 'Name',
        namePlaceholder: 'e.g. Summer Sale',
        nameRequired: 'Name is required',
        description: 'Description',
        descriptionPlaceholder: 'Describe this promo code...',
        discountTitle: 'Discount',
        discountDescription: 'Set the discount type and value.',
        discountType: 'Discount type',
        percentage: 'Percentage',
        fixedAmount: 'Fixed amount',
        discountValue: 'Discount value',
        discountValuePlaceholder: '0',
        discountValueRequired: 'Discount value is required',
        currency: 'Currency',
        maxDiscountAmount: 'Maximum discount amount',
        maxDiscountAmountPlaceholder: 'No limit',
        maxDiscountAmountHint: 'Cap the discount for percentage-based coupons',
        limitsTitle: 'Usage limits',
        limitsDescription: 'Control how many times this coupon can be used.',
        hasMaxUses: 'Limit total uses',
        maxUses: 'Maximum total uses',
        maxUsesPlaceholder: 'Unlimited',
        maxUsesPerUser: 'Max uses per user',
        maxUsesPerUserPlaceholder: '1',
        validityTitle: 'Validity period',
        validityDescription: 'Set when this coupon is valid.',
        startDate: 'Start date',
        hasEndDate: 'Set end date',
        endDate: 'End date',
        minimumAmount: 'Minimum purchase amount',
        minimumAmountPlaceholder: 'No minimum',
        publicationTitle: 'Publication',
        publicationDescription: 'Control the availability of this coupon.',
        isActive: 'Active',
        isActiveHint: 'This coupon can be used by customers',
      },
      usage: {
        title: 'Usage statistics',
        description: 'Track how this coupon is being used.',
        stats: {
          totalUses: 'Total uses',
          uniqueUsers: 'Unique users',
          totalDiscount: 'Total discount given',
          revenue: 'Revenue collected',
          remainingUses: 'Remaining uses',
          daysUntilExpiry: 'Days until expiry',
        },
        unlimited: 'Unlimited',
        noExpiration: 'No expiration',
        table: {
          user: 'User',
          usedAt: 'Used at',
          item: 'Purchase',
          paid: 'Paid (incl. tax)',
          discountAmount: 'Discount',
        },
        refunded: 'Refunded',
        emptyState: {
          title: 'No usage yet',
          description: 'Usage data will appear here once customers use this coupon.',
        },
      },
      danger: {
        title: 'Danger zone',
        deleteTitle: 'Delete this coupon',
        deleteDescription: 'Once deleted, this coupon will no longer be usable.',
        deleteButton: 'Delete coupon',
      },
    },
  },
  categoryTreeSelect: {
    search: 'Search categories...',
    noResults: 'No categories found',
    nSelected: '{count} categories selected',
    currentCategory: '(current category)',
    resultCount: '{count} result | {count} results',
    clearSearch: 'Clear search',
  },
  salesSubscriptions: {
    title: 'Active Subscriptions',
    subtitle: 'All active subscriptions across all plans.',
    searchPlaceholder: 'Search by email...',
    filterStatus: 'Status',
    filterPlan: 'Plan',
    filterAll: 'All',
    table: {
      user: 'User',
      plan: 'Plan',
      status: 'Status',
      startDate: 'Start date',
      endDate: 'End date',
      autoRenew: 'Auto-renew',
      daysRemaining: 'Days left',
    },
    status: {
      active: 'Active',
      cancelled: 'Cancelled',
      expired: 'Ended',
      paused: 'Paused',
      past_due: 'Payment pending',
    },
    emptyState: {
      title: 'No subscriptions',
      description: 'Active subscriptions will appear here once users subscribe to your plans.',
      noResults: 'No subscriptions found',
      noResultsDescription: 'Try adjusting your filters or search terms.',
    },
    cancelSuccess: 'Subscription cancelled.',
    cancelError: 'Could not cancel the subscription.',
    leaving: 'Ends at the end of the period',
    drawer: {
      plan: 'Plan',
      startDate: 'Start date',
      endDate: 'End date',
      daysRemaining: 'Days left',
      autoRenew: 'Auto-renew',
      complimentary: 'Complimentary',
      cancel: 'Cancel',
      manageUser: 'Manage subscriber',
      cancelRequested: 'Cancellation requested on',
    },
  },
  sales: {
    revenue: {
      subtitle: 'Export your sales and download your monthly {platform} fee invoices.',
      currentMonth: 'Current month',
      inProgress: 'in progress',
      salesCount: 'Sales',
      downloadError: 'The document could not be downloaded.',
      month: 'Month',
      grossTTC: 'Sales (incl. tax)',
      ht: 'of which excl. tax',
      vat: 'of which VAT',
      fees: 'Commission withheld (incl. tax)',
      refunds: 'Refunds',
      commissionReturned: 'incl. {amount} of commission returned',
      processingFees: 'incl. {fees} of payment fees · invoice {invoiced}',
      net: 'Net',
      documents: 'Documents',
      salesExport: 'Sales',
      feeInvoice: 'Fee invoice',
      feeCreditNote: 'Credit note',
      annual: {
        title: 'Annual summaries',
        subtitle: 'The summary of your transactions for the year, across all your channels. We email it to you in January.',
        operations: '{count} operations',
        gross: 'gross',
        download: 'Summary',
      },
      empty: {
        title: 'No statement yet',
        description: 'Your monthly statements will appear here at the end of each month.',
      },
    },
    overview: {
      title: 'Sales Overview',
      subtitle: 'Track your subscription and one-time sales at a glance.',
      vsPrevious: 'vs previous period',
      kpi: {
        revenue: 'Revenue',
        revenueDesc: 'Total collected over the period, tax and support contributions included',
        subscriptionRevenue: 'Subscription revenue',
        subscriptionRevenueDesc: 'Recurring revenue from active subscriptions',
        oneTimeRevenue: 'One-time revenue',
        oneTimeRevenueDesc: 'Revenue from one-time purchases and rentals',
        activeSubscribers: 'Active subscribers',
        activeSubscribersDesc: 'Number of subscribers with an active subscription',
        sales: 'One-time sales',
        salesDesc: 'Number of one-time purchases in the period',
        avgSale: 'Average sale',
        avgSaleDesc: 'Average amount per one-time sale',
      },
      trend: {
        title: 'Revenue trend',
        subtitle: 'Subscription vs one-time revenue over the period',
        subscriptions: 'Subscriptions',
        oneTime: 'One-time',
      },
      breakdown: {
        title: 'Revenue breakdown',
        subtitle: 'Excludes tax, so it totals less than collected revenue',
      },
      topContent: {
        title: 'Best-selling content',
        subtitle: 'Amounts exclude tax',
      },
      topPlans: {
        title: 'Popular plans',
        subtitle: 'Amounts exclude tax',
      },
      topCustomers: {
        title: 'Top customers',
        subtitle: 'Spent over the period, tax included, refunds deducted',
        count: 'Customers',
        revenue: 'Revenue from customers',
        average: 'Average per customer',
        unattributed: 'Unattributed',
        unattributedHint: 'Paid by members removed since',
        empty: 'No customer over the period.',
      },
      recent: {
        title: 'Recent sales',
        empty: 'No sales yet',
      },
      streams: {
        subscription: 'Subscription',
        purchase: 'Purchase',
        rental: 'Rental',
        contribution: 'Support contribution',
      },
    },
    transactions: {
      title: 'Transactions',
      subtitle: 'Every payment — purchases, rentals and subscription terms.',
      searchPlaceholder: 'Search by email...',
      filterStatus: 'Status',
      filterType: 'Type',
      table: {
        id: 'ID',
        customer: 'Customer',
        item: 'Item',
        type: 'Type',
        amount: 'Amount',
        status: 'Status',
        date: 'Date',
        actions: 'Actions',
      },
      refund: 'Refund…',
      alreadyRefunded: 'Already refunded.',
      refundedAmount: '{amount} refunded',
      refundBlocker: {
        fully_refunded: 'Already refunded.',
        refund_pending: 'A refund is on its way on this sale: wait for its confirmation.',
        disputed: 'Disputed sale: while the dispute is open, contact support.',
        imported: 'This sale predates in-app refunds — contact support.',
        contribution: 'Support contribution: it cannot be refunded here — contact support.',
      },
      notRefundable: 'This sale predates in-app refunds — contact support.',
      refundModal: {
        title: 'Refund this sale',
        warning: 'A full refund removes the buyer\'s access to this purchase or rental; a partial one does not. Neither cancels a subscription. A refund cannot be undone.',
        customer: 'Customer',
        content: 'Content',
        paid: 'Paid',
        amount: 'Amount to refund (incl. tax)',
        amountHint: 'Refund less than the full amount to make a partial refund.',
        amountRequired: 'Enter an amount to refund.',
        amountTooHigh: 'More than what is left to refund on this sale.',
        reason: 'Reason (optional)',
        commissionNote: 'The amount refunded is taken from your payment account balance and returned to the buyer. The {platform} commission taken on this sale is not returned, including on a full refund.',
        confirm: 'Refund',
        success: 'Sale refunded.',
        requested: 'Refund requested: it will show as done once our payment partner confirms it.',
        error: 'The refund could not be completed.',
      },
      type: {
        purchase: 'Purchase',
        rental: 'Rental',
        subscription: 'Subscription',
        contribution: 'Support contribution',
      },
      status: {
        paid: 'Paid',
        partially_refunded: 'Partly refunded',
        disputed: 'Disputed',
        charged_back: 'Dispute lost',
        active: 'Completed',
        expired: 'Expired',
        refunded: 'Refunded',
        cancelled: 'Cancelled',
      },
      emptyState: {
        title: 'No transactions yet',
        description: 'Transactions will appear here once payments are processed.',
        noResults: 'No transactions found',
        noResultsDescription: 'Try adjusting your filters or search terms.',
      },
    },
  },
  monetization: {
    // A period is a count and a unit, never a number of days. Two keys per unit rather than a
    // plural form: vue-i18n's pluralisation is not typed in this tree.
    period: {
      day: 'day',
      days: '{count} days',
      week: 'week',
      weeks: '{count} weeks',
      month: 'month',
      months: '{count} months',
      year: 'year',
      years: '{count} years',
    },
    // The unit on its own, for a select. The counted forms above read as a duration and would
    // show a count nobody picked.
    periodUnit: {
      day: 'Days',
      week: 'Weeks',
      month: 'Months',
      year: 'Years',
    },
    status: {
      closed: {
        title: 'Selling is not open yet',
        description: 'Open it to put a price on your content.',
        action: 'Open selling',
      },
      review: {
        title: 'Your request is under review',
        description: 'We email you as soon as it has been looked at.',
      },
      rejected: {
        title: 'Your request was declined',
        description: 'Read why, correct it, and send it again.',
        action: 'See the reason',
      },
      paymentsPending: {
        title: 'Connect your payments',
        description: 'Selling is open on your channel, but nobody can pay yet: connect your payment account to receive your first sales.',
        action: 'Set up',
      },
      open: {
        title: 'Selling is open',
        description: 'Mark a piece of content as paid and set its price.',
      },
    },
    currency: {
      placeholder: '0.00',
      hint: 'Enter the amount',
    },
    billingPeriod: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      biannual: 'Biannual',
      annual: 'Annual',
      custom: 'Custom',
      months: 'Months',
      years: 'Years',
    },
    trial: {
      addTrial: 'Add a free trial',
      days: 'Trial days',
      hint: 'Number of free trial days (1-365)',
    },
    price: {
      download: 'Download',
      exclTax: 'excl. tax',
      inclTax: 'incl. tax',
      isDefault: 'Featured price',
      makeDefault: 'Make this the featured price',
      downloadHint: 'What the buyer may take away, beyond watching online.',
      downloadNone: 'Streaming only',
      downloadRenditions: 'Encoded versions',
      downloadSource: 'Original file',
      downloadAll: 'Everything downloadable',
      title: 'Add a price',
      editTitle: 'Edit price',
      name: 'Name',
      namePlaceholder: 'e.g. Standard, Premium...',
      price: 'Price',
      priceExclVat: 'Price excl. VAT',
      priceInclVat: 'Public price incl. VAT',
      breakdown: 'That is {net} excl. VAT + {vat} VAT ({rate}), at the French rate.',
      breakdownFranchise: 'That is {net} excl. VAT: VAT franchise, no VAT added.',
      breakdownRounded: 'Rounding the VAT makes it {shown}.',
      grossHint: 'What you take, before commission. The visitor pays {gross} incl. VAT ({rate}); a buyer elsewhere in the EU sees their own country\'s rate.',
      priceRequired: 'Price is required',
      priceMinimum: 'Minimum price is {amount}',
      period: 'Access period',
      unlimited: 'Unlimited',
      limited: 'Limited duration',
      hours: 'Hours',
      days: 'Days',
      months: 'Months',
      years: 'Years',
      download: 'Download',
      allowDownload: 'Allow download',
    },
    subscriptions: {
      freeAccessModal: {
        title: 'Grant free access',
        description: 'Grant a user free access to this subscription plan.',
        user: 'User email',
        userPlaceholder: "user{'@'}example.com",
        duration: 'Duration (days)',
        durationHint: 'Leave 0 for unlimited access',
        emailInvalid: 'Valid email required',
        grant: 'Grant access',
      },
      cancelModal: {
        title: 'Cancel subscription',
        warning: 'This action will cancel the subscription. The user will lose access at the end of their current billing period.',
        user: 'User',
        endDate: 'Current end date',
        confirm: 'Cancel subscription',
      },
      extendModal: {
        title: 'Extend subscription',
        description: 'Extend the end date of this subscription.',
        user: 'User',
        currentEndDate: 'Current end date',
        newEndDate: 'New end date',
        daysAdded: '+ {days} days',
        confirm: 'Extend',
      },
    },
  },
  access: {
    revokeTitle: 'Revoke access',
    revokeConfirm: 'Revoke access',
    levels: {
      public: 'Public',
      authenticated: 'Login required',
      restricted: 'Restricted',
      paid: 'Paid',
      accessRequest: 'Access Request',
    },
    alsoViaGroup: 'Also accessible via group "{name}"',
  },
  grantAccessModal: {
    title: 'Grant access to content',
    titleTarget: 'Grant access — {name}',
    tabItems: 'By content',
    tabContent: 'By content',
    tabCategory: 'By category',
    tabCategoryLabel: 'By category',
    categoryInfo: 'Select a category to grant access to all its access-controlled items at once.',
    selectedSummary: '{items} and {count} more',
    restrictedHintUser: 'Access-controlled content (restricted, paid or on request) is shown below. Select the content you want this user to access.',
    restrictedHintGroup: 'Access-controlled content (restricted, paid or on request) this group cannot access yet is shown below.',
    searchPlaceholder: 'Search content...',
    categoryFilter: 'Category',
    categoryHint: 'Grant access to all access-controlled items in the selected category.',
    grantCategory: 'Grant category',
    columnTitle: 'Title',
    columnAccess: 'Access',
    columnStatus: 'Status',
    restricted: 'Restricted',
    published: 'Published',
    unpublished: 'Unpublished',
    noItems: 'No controlled-access items found',
    selectedCount: '{count} item(s) selected',
    grantItems: 'Grant {count} item(s)',
    processing: 'Granting access... {completed}/{total}',
    grantSuccess: '{count} access(es) granted successfully',
    grantPartial: '{completed} granted, {failed} failed',
    grantError: 'Failed to grant access',
    grantCategorySuccess: 'Category access granted successfully',
  },
  campaigns: {
    title: 'Campaigns',
    subtitle: 'Create and send personalized emails to your users.',
    btnCreate: 'New campaign',
    searchPlaceholder: 'Search campaigns...',
    filterByStatus: 'Filter by status',
    filterByType: 'Filter by type',
    tabs: {
      campaigns: 'Campaigns',
      audiences: 'Audiences',
      suppressions: 'Do-not-mail list',
    },
    status: {
      draft: 'Draft',
      scheduled: 'Scheduled',
      sending: 'Sending',
      sent: 'Sent',
      paused: 'Paused',
      cancelled: 'Cancelled',
      partially_failed: 'Partial failure',
      failed: 'Failed',
      pending: 'Pending',
    },
    type: {
      one_shot: 'One-time',
      recurring: 'Recurring',
    },
    table: {
      name: 'Name',
      status: 'Status',
      type: 'Type',
      recipients: 'Recipients',
      lastSent: 'Last sent',
      nextSend: 'Next send',
      sendCount: '{count} send | {count} sends',
    },
    emptyState: {
      title: 'No campaigns yet',
      description: 'Create your first email campaign to engage your audience.',
      noResults: 'No campaigns found',
      noResultsDescription: 'Try adjusting your search or filters.',
    },
    actions: {
      duplicate: 'Duplicate',
      delete: 'Delete',
    },
    deleteTitle: 'Delete campaign',
    deleteMessage: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
    deleteSuccess: 'Campaign deleted',
    deleteError: 'The campaign could not be deleted.',
    duplicateSuccess: 'Campaign duplicated as a new draft.',
    copyName: 'Copy of {name}',
    conflict: 'This campaign changed since you opened it: it was reloaded, and your edits are kept.',
    actionError: 'The action did not go through.',
    detail: {
      newCampaign: 'New campaign',
      saveFirst: 'Save the campaign first',
      createSuccess: 'Campaign created',
      updateSuccess: 'Campaign saved',
      saveError: 'The campaign could not be saved.',
      cancelledReadOnly: 'This campaign is cancelled: it will never go out again, and its past sends stay in its history. Duplicate it to start again from its content.',
      sentReadOnly: 'This campaign has gone out: its message, recipients and date are history. Duplicate it to send another.',
      testSuccess: 'Test sent to {to}',
      verbSuccess: {
        schedule: 'Campaign scheduled',
        pause: 'Campaign paused',
        resume: 'Campaign resumed',
        cancel: 'Campaign cancelled',
      },
      tabs: {
        content: 'Content',
        targeting: 'Targeting',
        schedule: 'Schedule',
        history: 'History',
      },
      content: {
        sectionTitle: 'Email content',
        campaignTitle: 'Campaign',
        name: 'Campaign name',
        namePlaceholder: 'e.g. Spring newsletter',
        nameHint: 'Only you see it; left blank, the subject names it.',
        nameRequired: 'Give the campaign a name or a subject.',
        description: 'Internal note',
        replyToInvalid: 'The reply-to is not a valid address.',
        contentRequired: 'A scheduled or sent campaign keeps a subject and a body.',
        subject: 'Subject',
        subjectPlaceholder: 'Enter the email subject...',
        fromName: 'From name',
        fromNamePlaceholder: 'e.g. The MonSite Team',
        replyTo: 'Reply-to email',
        replyToPlaceholder: "e.g. contact{'@'}example.com",
        fromAddress: 'Your sends go out from {address}, set by the Videas team.',
        customLayout:
          'Your campaigns wear a layout set up by the Videas team: the preview is indicative, send a test to see exactly how it looks.',
        body: 'Email body',
        bodyPlaceholder: 'Write your email content...',
        contentMode: 'Content type',
        contentModes: {
          manual: 'Custom content',
          latest_items: 'Latest published items',
        },
        manualItems: 'Selected items',
        manualItemsDescription: 'Choose the items to feature in this campaign.',
        addItems: 'Add items',
        noItemsSelected: 'No items selected',
        noItemsSelectedDescription: 'Click "Add items" to choose the content to include in this email.',
        searchItems: 'Search items...',
        addSelected: 'Add selected ({count})',
        noResults: 'No items found',
        noItems: 'No items available',
        itemsCount: 'Number of items to include',
        itemsCategory: 'Filter by category',
        itemsCategoryAll: 'All categories',
      },
      targeting: {
        sectionTitle: 'Recipients',
        description: 'Choose who will receive this campaign. Recipients from all sources below are combined, then exclusions are applied.',
        audiences: 'Audiences',
        audiencesPlaceholder: 'Select audiences...',
        groups: 'Groups',
        groupsPlaceholder: 'Select groups...',
        users: 'Individual users',
        usersPlaceholder: 'Search and select users...',
        excludeUsers: 'Exclusions',
        excludeUsersPlaceholder: 'Search users to exclude...',
        recipientPreview: 'Recipients',
        memberCount: '{count} member | {count} members',
        inAndOut: 'A member cannot be both included and excluded.',
        unsubscribeNote: 'A member who unsubscribed from the newsletter receives no campaign, whatever the targeting, and neither does an address on the do-not-mail list.',
        excluded: {
          by_name: '{count} excluded by name | {count} excluded by name',
          unsubscribed: '{count} unsubscribed from the newsletter | {count} unsubscribed from the newsletter',
          suppressed: '{count} address on the do-not-mail list | {count} addresses on the do-not-mail list',
          closed: '{count} membership suspended, expired or pending | {count} memberships suspended, expired or pending',
        },
      },
      schedule: {
        sectionTitle: 'Schedule',
        type: 'Send type',
        types: {
          one_shot: 'One-time send',
          recurring: 'Recurring',
        },
        typeDescriptions: {
          one_shot: 'Send this campaign once, now or at a specific date.',
          recurring: 'Automatically send on a recurring schedule.',
        },
        scheduledAt: 'Send date',
        scheduledAtHint: 'Leave empty to send manually.',
        recurrence: 'Frequency',
        recurrences: {
          daily: 'Every day',
          weekly: 'Every week',
          biweekly: 'Every 2 weeks',
          monthly: 'Every month',
        },
        recurrenceDay: 'Day of the week',
        days: {
          monday: 'Monday',
          tuesday: 'Tuesday',
          wednesday: 'Wednesday',
          thursday: 'Thursday',
          friday: 'Friday',
          saturday: 'Saturday',
          sunday: 'Sunday',
        },
        recurrenceTime: 'Time',
        recurrenceTimezone: 'Timezone',
        nextSend: 'Next send: {date}',
        pausedNote: 'Paused: nothing goes out until the campaign is resumed.',
      },
      history: {
        sectionTitle: 'Send history',
        emptyTitle: 'No sends yet',
        emptyDescription: 'This campaign has not been sent yet.',
        table: {
          date: 'Date',
          status: 'Status',
          recipients: 'Recipients',
          sent: 'Sent',
          failed: 'Failed',
          delivered: 'Delivered',
          pending: 'Pending',
          unsubscribed: 'Unsubscribes',
          excluded: 'Left out',
        },
        excluded: {
          by_name: '{count} excluded by name | {count} excluded by name',
          closed: '{count} suspended, expired or pending access | {count} suspended, expired or pending accesses',
          unsubscribed: '{count} unsubscribed from the newsletter | {count} unsubscribed from the newsletter',
          suppressed: '{count} address on the do-not-mail list | {count} addresses on the do-not-mail list',
        },
      },
      actions: {
        send: 'Send now',
        schedule: 'Schedule',
        previewAndTest: 'Preview & test',
        duplicate: 'Duplicate',
        pause: 'Pause',
        resume: 'Resume',
        cancel: 'Cancel campaign',
        scheduleBlocked: {
          content: 'Add a subject and a body before scheduling.',
          date: 'Pick a send date in the Schedule tab.',
          past: 'The send date has passed: pick a new one.',
          recurrence: 'Complete the recurrence in the Schedule tab.',
        },
      },
      cancelDialog: {
        title: 'Cancel the campaign?',
        message: '“{name}” will never go out again: neither on its date nor by a manual send. Its past sends stay in its history. To stop it for a while only, pause it instead.',
        confirm: 'Cancel for good',
      },
      sendDialog: {
        title: 'Send campaign',
        subject: 'Subject',
        sender: 'Sender',
        recipients: 'Recipients',
        recipientsSuffix: 'recipient | recipients',
        content: 'Content',
        contentManual: '{count} selected item | {count} selected items',
        contentLatest: '{count} latest item | {count} latest items',
        noRecipients: 'This campaign has no recipients. Configure the targeting to add recipients before sending.',
        warning: 'This action will send the email to all recipients immediately. This cannot be undone.',
        confirm: 'Send to {count} recipient | Send to {count} recipients',
        sendSuccess: 'Campaign sent successfully',
        dropped: '{count} targeted member will not be written to: unsubscribed, blocked address or closed access. | {count} targeted members will not be written to: unsubscribed, blocked addresses or closed access.',
      },
      danger: {
        title: 'Danger zone',
        deleteTitle: 'Delete this campaign',
        deleteDescription: 'Permanently delete this campaign and all its history.',
        deleteButton: 'Delete campaign',
      },
    },
    preview: {
      title: 'Email preview',
      from: 'From:',
      replyTo: 'Reply-to:',
      subject: 'Subject:',
      noFromName: 'No sender name',
      noSubject: 'No subject',
      noBody: 'No content yet.',
      featuredItems: 'Featured items',
      latestItems: 'Latest {count} items',
      latestItemsAuto: 'Items will be automatically selected at send time.',
      watchNow: 'Watch now',
      itemPlaceholder: 'Item {n}',
      footerAuto: 'This email was sent automatically by {name}.',
      unsubscribe: 'Unsubscribe',
      sendTest: 'Send test',
      testEmailPlaceholder: "test{'@'}example.com",
    },
    suppressions: {
      title: 'Do-not-mail list',
      subtitle: 'The addresses no campaign of your organization is sent to, on any of its channels.',
      btnAdd: 'Add an address',
      searchPlaceholder: 'Search an address...',
      allReasons: 'All reasons',
      reason: {
        unsubscribed: 'Unsubscribed',
        bounced: 'Bounced',
        operator: 'Added by hand',
      },
      table: {
        email: 'Address',
        reason: 'Reason',
        since: 'Since',
      },
      emptyState: {
        title: 'No address is excluded',
        noResults: 'No address found',
        description: 'An address lands here when its owner unsubscribes from an email, when the mail server refuses it for good, or when you add it.',
      },
      lift: 'Allow again',
      ownerOnly: 'The owner of this address unsubscribed: only they can allow it again, by switching the newsletter back on from their account on the channel.',
      addDialog: {
        title: 'Stop writing to an address',
        description: 'No campaign of any channel of your organization will be sent to it. Account emails (password, receipts) are not affected.',
        email: 'Email address',
        note: 'Note (optional)',
        confirm: 'Add',
      },
      liftDialog: {
        title: 'Allow the address again?',
        message: '{email} will receive your organization\'s campaigns that target it again.',
      },
      addSuccess: '{email} is on the do-not-mail list',
      addError: 'The address could not be added.',
      liftSuccess: '{email} can receive your campaigns again',
      liftError: 'The address could not be allowed again.',
    },
    audiences: {
      title: 'Audiences',
      subtitle: 'Manage your email audiences and mailing lists.',
      btnCreate: 'New audience',
      searchPlaceholder: 'Search audiences...',
      // Keys mirror the backend `system_key`: system audiences are stored with
      // an English name, and re-rendered from the catalogue instead.
      system: {
        all_users: {
          name: 'All users',
          description: 'All active users of the website',
        },
        newsletter: {
          name: 'Newsletter subscribers',
          description: 'Users who opted in to newsletters',
        },
        active_subscribers: {
          name: 'Active subscribers',
          description: 'Users with an active subscription',
        },
        free_users: {
          name: 'Free users',
          description: 'Users without an active subscription',
        },
      },
      table: {
        name: 'Name',
        type: 'Type',
        members: 'Members',
        createdAt: 'Created',
        updatedAt: 'Updated',
      },
      type: {
        static: 'Static',
        dynamic: 'Dynamic',
        combined: 'Combined',
      },
      typeDescription: {
        static: 'Manually manage members.',
        dynamic: 'Automatically based on rules.',
        combined: 'A hand-kept list, plus rules.',
      },
      systemBadge: 'System',
      emptyState: {
        title: 'No audiences yet',
        description: 'Create audiences to target users for your email campaigns.',
        noResults: 'No audiences found',
        noResultsDescription: 'Try adjusting your search.',
      },
      presets: {
        sectionTitle: 'Suggested audiences',
        dismiss: 'Hide',
        engaged_users: {
          name: 'Engaged users',
          description: 'Users active in the last 7 days.',
          suggestedDescription: 'Targets users who have been active in the last 7 days, ideal for retention and new content campaigns.',
        },
        premium_members: {
          name: 'Premium members',
          description: 'Users with full access.',
          suggestedDescription: 'Targets users with full access, ideal for exclusive content and loyalty campaigns.',
        },
        non_newsletter: {
          name: 'Non-newsletter subscribers',
          description: 'Users not opted in to the newsletter.',
          suggestedDescription: 'Targets users who haven\'t subscribed to the newsletter yet, ideal for opt-in campaigns.',
        },
        engaged_subscribers: {
          name: 'Engaged subscribers',
          description: 'Active subscribers active in the last 7 days.',
          suggestedDescription: 'Targets active subscribers who have been active recently, ideal for upsell and retention campaigns.',
        },
      },
      deleteTitle: 'Delete audience',
      deleteMessage: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
      deleteSuccess: 'Audience deleted',
      deleteError: 'The audience could not be deleted.',
      conflict: 'This audience changed since you opened it: it was reloaded, and your edits are kept.',
      detail: {
        newAudience: 'New audience',
        saveFirst: 'Save the audience first',
        createSuccess: 'Audience created',
        updateSuccess: 'Audience saved',
        saveError: 'The audience could not be saved.',
        tabs: {
          information: 'Information',
          members: 'Members',
        },
        info: {
          sectionTitle: 'Audience information',
          name: 'Name',
          namePlaceholder: 'e.g. Premium Subscribers',
          description: 'Description',
          descriptionPlaceholder: 'Describe this audience...',
          type: 'Type',
          nameRequired: 'Give the audience a name.',
          systemReadOnly: 'This audience comes with the channel: it cannot be renamed, edited or deleted.',
        },
        rules: {
          sectionTitle: 'Rules',
          description: 'A member is in the audience when they match every Include rule and no Exclude rule.',
          subscriptionStatus: 'Subscription status',
          groups: 'Groups',
          newsletterOptIn: 'Newsletter opt-in only',
          hasFullAccess: 'Has full access',
          joinedAfter: 'Joined after',
          joinedBefore: 'Joined before',
          lastActiveWithinDays: 'Last active within (days)',
          statusActive: 'Active subscription',
          statusNone: 'No active subscription',
          empty: 'No rule yet: add at least one.',
          addRule: 'Add a rule',
          removeRule: 'Remove the rule',
          plans: 'Plans',
          plansAll: 'Any plan',
          newsletterYes: 'Subscribed to the newsletter',
          newsletterNo: 'Unsubscribed from the newsletter',
          fullAccessYes: 'Has full access',
          fullAccessNo: 'Has no full access',
          operators: {
            include: 'Include',
            exclude: 'Exclude',
          },
          kinds: {
            subscription_status: 'Subscription',
            group_membership: 'Group',
            newsletter_consent: 'Newsletter',
            full_access: 'Full access',
            joined: 'Join date',
            last_active: 'Last activity',
          },
          problems: {
            ruleRequired: 'A dynamic or combined audience has at least one rule, or it would target everybody.',
            groupRequired: 'Pick at least one group.',
            dateRequired: 'Give at least one date.',
            dateOrder: 'The first date is after the second.',
            daysRequired: 'Give a number of days, at least 1.',
          },
        },
        members: {
          sectionTitle: 'Members',
          staticDescription: 'Add or remove members manually.',
          dynamicDescription: 'Members are automatically computed based on rules.',
          combinedDescription: 'The members added by hand, plus those the rules find. Only the former are removed here.',
          filterPlaceholder: 'Filter members...',
          tableSource: 'Source',
          byRule: 'By rule',
          addedOn: 'Added {date}',
          unsubscribed: 'Unsubscribed',
          unsubscribedHint: 'This member unsubscribed from the newsletter: no campaign will be sent to them.',
          added: '{count} member added | {count} members added',
          changeError: 'The list could not be changed.',
          count: '{count} member | {count} members',
          addMembers: 'Add members',
          removeMember: 'Remove',
          searchPlaceholder: 'Search users...',
          emptyTitle: 'No members',
          tableUser: 'User',
          tableJoined: 'Joined',
        },
        danger: {
          title: 'Danger zone',
          deleteTitle: 'Delete this audience',
          deleteDescription: 'Permanently delete this audience.',
          deleteButton: 'Delete audience',
        },
      },
    },
  },
  comments: {
    title: 'Comments',
    subtitle: 'Moderate and manage comments from your audience.',
    searchPlaceholder: 'Search by content or email...',
    filterByStatus: 'Filter by status',
    // The list's filter by publication or article, which no server serves yet.
    filterByTarget: 'Item or article',
    targetKinds: {
      item: 'Item',
      article: 'Article',
    },
    reply: 'Reply',
    adminBadge: 'Admin',
    replyForm: {
      label: 'Your reply',
      asChannelHint: 'The reply is posted in the channel\'s name through your own membership — you need one on this channel.',
      placeholder: 'Write a reply as admin...',
    },
    scrubbed: '(content erased)',
    scrubbedAuthor: 'Author erased',
    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    filters: {
      all: 'All',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    stats: {
      shown: 'Shown',
      total: 'Total',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    table: {
      user: 'User',
      comment: 'Comment',
      item: 'Item',
      status: 'Status',
      date: 'Date',
    },
    actions: {
      approve: 'Approve',
      reject: 'Reject',
      delete: 'Delete',
      reply: 'Reply',
      sendReply: 'Send reply',
    },
    quick: {
      approved: 'Comment approved',
      rejected: 'Comment rejected',
      removed: 'Comment deleted',
      rejectWithReason: 'Reject with a reason',
      more: 'More actions on the comment',
    },
    bulk: {
      selected: '{count} selected',
    },
    emptyState: {
      title: 'No comments yet',
      description: 'Comments from your audience will appear here.',
      noResults: 'No comments found',
      noResultsDescription: 'Try adjusting your search or filters.',
      cleared: 'All caught up',
      clearedDescription: 'No comment is waiting for a decision.',
    },
    rejectModal: {
      title: 'Reject comment',
      description: 'Please provide a reason for rejecting this comment.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Enter the reason for rejection...',
      reasonRequired: 'A reason is required for rejection',
    },
    deleteDialog: {
      title: 'Delete comment',
      message: 'This comment will be permanently deleted. This action cannot be undone.',
    },
    time: {
      justNow: 'Just now',
      minutesAgo: '{count} min ago',
      hoursAgo: '{count}h ago',
      daysAgo: '{count}d ago',
    },
    detail: {
      title: 'Comment details',
      user: 'User',
      email: 'Email',
      item: 'Item',
      postedOn: 'Posted on',
      editedOn: 'Edited on',
      likes: 'Likes',
      replies: 'Replies',
      moderation: 'Moderation',
      moderatedBy: 'Moderated by',
      moderatedOn: 'Moderated on',
      rejectionReason: 'Rejection reason',
    },
    toast: {
      moderateSuccess: 'Comment moderated successfully',
      moderateError: 'Failed to moderate comment',
      deleteSuccess: 'Comment deleted successfully',
      deleteError: 'Failed to delete comment',
      replySuccess: 'Reply sent successfully',
      replyError: 'Failed to send reply',
      replyNoMembership: 'You hold no membership of this channel: a reply is written as a member, not as an operator.',
      bulkPartial: '{done} done, {failed} failed',
      bulkSuccess: '{count} comments moderated successfully',
      bulkError: 'Failed to moderate comments',
    },
  },
  giftSubscriptionModal: {
    title: 'Gift a subscription',
    description: 'Offer a complimentary subscription to {name}.',
    selectPlan: 'Subscription plan',
    selectPlanPlaceholder: 'Choose a plan...',
    endDate: 'End date',
    endDateHint: 'The subscription will expire on this date — one period of the plan by default. Clear it for a subscription that never ends.',
    autoRenewOff: 'Auto-renew is disabled for complimentary subscriptions.',
    gift: 'Gift subscription',
    giftSuccess: 'Subscription gifted successfully',
    giftError: 'Failed to gift subscription',
    alreadySubscribed: 'Already subscribed',
    alreadyHasPlan: 'User already has an active subscription for this plan',
  },
  blog: {
    title: 'Blog',
    subtitle: 'Create and manage blog articles for your website.',
    btnCreate: 'New article',
    searchPlaceholder: 'Search articles...',
    filterByStatus: 'Filter by status',
    featured: 'Featured',
    status: {
      draft: 'Draft',
      published: 'Published',
      scheduled: 'Scheduled',
      archived: 'Archived',
    },
    table: {
      title: 'Title',
      status: 'Status',
      date: 'Published',
      author: 'Author',
    },
    unknownAuthor: '—',
    emptyState: {
      title: 'No articles yet',
      description: 'Create your first blog article to engage your audience.',
      noResults: 'No articles found',
      noResultsDescription: 'Try adjusting your filters or search terms.',
    },
    deleteTitle: 'Delete article',
    deleteMessage: 'Are you sure you want to delete "{title}"? This action cannot be undone.',
    conflict: 'Somebody else saved this article since you opened it, and nothing was written. It has just been reloaded: check your text, then save again.',
    validation: {
      titleRequired: 'An article needs a title.',
      slugInvalid: 'An address is lowercase words joined by single hyphens.',
      contentRequired: 'An article needs a body: an empty one is a page that answers and says nothing.',
    },
    danger: {
      title: 'Danger zone',
      deleteTitle: 'Delete this article',
      deleteDescription: 'Once deleted, this article will be permanently removed.',
      deleteButton: 'Delete article',
    },
    form: {
      newArticle: 'New article',
      titleSection: 'Title',
      titlePlaceholder: 'Enter article title...',
      slug: 'Address',
      slugPlaceholder: 'behind-the-scenes',
      slugHint: 'Where the article answers on your blog. Lowercase words joined by hyphens.',
      content: 'Content',
      contentPlaceholder: 'Write your article...',
      excerpt: 'Excerpt',
      excerptPlaceholder: 'A short summary of the article...',
      options: 'Options',
      tags: 'Tags',
      tagsPlaceholder: 'Add tags...',
      tagsHint: 'Type a tag, then Enter.',
      featured: 'Featured',
      featuredHint: 'Highlight this article on your blog',
      allowsComments: 'Allow comments',
      allowsCommentsHint: 'Visitors can comment on this article on your blog',
      language: 'Language',
      languageHint: 'The language the article is written in. Left empty, it is the channel\'s.',
      languages: {
        fr: 'French',
        en: 'English',
      },
      thumbnail: 'Cover image',
      thumbnailHint: 'Used as the article preview and for social sharing (recommended 1200×630).',
      categories: 'Categories',
      categoriesPlaceholder: 'Assign categories...',
      categoriesCreate: 'Create category "{name}"',
      categoriesCreated: 'Category created',
      seo: 'SEO settings',
      metaTitle: 'Meta title',
      metaDescription: 'Meta description',
      metaDescriptionPlaceholder: 'A brief description for search engines...',
      saveDraft: 'Save draft',
      publish: 'Publish',
      unpublish: 'Unpublish',
    },
    toast: {
      createSuccess: 'Article created successfully',
      updateSuccess: 'Article updated successfully',
      deleteSuccess: 'Article deleted successfully',
      publishSuccess: 'Article published',
      publishError: 'Could not publish the article',
      unpublishSuccess: 'Article unpublished',
      unpublishError: 'Could not unpublish the article',
      error: 'An error occurred. Please try again.',
      bodyImagesError: 'The article is saved, but its images could not be attached to it.',
    },
  },
  accessRequests: {
    title: 'Access Requests',
    subtitle: 'Manage user requests for restricted content',
    itemCount: '{count} item | {count} items',
    stats: {
      shown: 'Shown',
      total: 'Total',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    table: {
      user: 'User',
      items: 'Items',
      message: 'Message',
      submittedAt: 'Submitted',
      status: 'Status',
    },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    filters: {
      all: 'All statuses',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Request approved',
    rejected: 'Request rejected',
    approveError: 'Failed to approve request',
    rejectError: 'Failed to reject request',
    approveDialog: {
      title: 'Approve the request',
      description: 'The requested access will be granted. You can give it an end date.',
      expiresAt: 'Access until',
      expiresAtHint: 'Left empty, the access never expires.',
    },
    rejectDialog: {
      title: 'Reject Access Request',
      reason: 'Reason (visible to user)',
      confirm: 'Reject Request',
    },
    detail: {
      message: 'Message',
      rejectionReason: 'Rejection reason',
      reviewedBy: 'Reviewed by',
      requestedItems: '{count} requested item(s)',
      viewUser: 'View user profile',
    },
    filterByStatus: 'Filter by status',
    searchPlaceholder: 'Search by user name or email...',
    empty: 'No access requests',
    quick: {
      approveUntil: 'Approve with an end date',
      rejectWithReason: 'Reject with a reason',
      more: 'More actions',
    },
    cleared: 'No request is waiting',
    answer: {
      full: 'Already reachable',
      locked: 'Locked',
      nothing: 'Invisible',
    },
  },
  grants: {
    neverExpires: 'Never expires',
    kind: {
      item: 'Publication',
      category: 'Category',
      page: 'Page',
    },
    table: {
      kind: 'Kind',
      expires: 'Expires',
    },
  },
  submissions: {
    title: 'Submissions',
    subtitle: 'The files your members have deposited, and your decision on each.',
    tabs: {
      pending: 'Pending',
      pendingCount: 'Pending ({count})',
      approved: 'Accepted',
      rejected: 'Refused',
      interrupted: 'Interrupted',
      empty: {
        pending: 'No deposit waiting',
        approved: 'No deposit accepted',
        rejected: 'No deposit refused',
        interrupted: 'No interrupted upload',
      },
    },
    approve: 'Accept',
    reject: 'Refuse',
    approved: 'Submission accepted',
    rejected: 'Submission refused',
    approveError: 'Failed to accept the submission',
    rejectError: 'Failed to refuse the submission',
    files: '{count} file | {count} file | {count} files',
    selection: {
      all: 'Select all',
      sending: 'Select “{title}”',
      unfold: 'Show the files',
      bar: 'Selection',
      count: '{count} selected | {count} selected | {count} selected',
      clear: 'Clear',
      approve: 'Accept the selection',
      reject: 'Refuse the selection',
      approved: '{count} deposit accepted | {count} deposit accepted | {count} deposits accepted',
      rejected: '{count} deposit refused | {count} deposit refused | {count} deposits refused',
      skipped: {
        decided:
          '{count} file left alone: already decided | {count} file left alone: already decided | {count} files left alone: already decided',
        arriving:
          '{count} file left alone: still uploading | {count} file left alone: still uploading | {count} files left alone: still uploading',
        unfinished:
          '{count} file left alone: its upload never finished | {count} file left alone: its upload never finished | {count} files left alone: their upload never finished',
        gone:
          '{count} file left alone: no longer in the inbox | {count} file left alone: no longer in the inbox | {count} files left alone: no longer in the inbox',
      },
    },
    sending: {
      approve: 'Accept the whole sending',
      reject: 'Refuse the whole sending',
    },
    quick: {
      approveElsewhere: 'Accept elsewhere…',
      rejectWithReason: 'Refuse with a reason',
      more: 'More actions',
    },
    status: {
      uploading: 'Uploading',
      interrupted: 'Upload interrupted',
      pending: 'Pending',
      approved: 'Accepted',
      rejected: 'Refused',
    },
    inbox: {
      closed: 'The inbox is closed: no member may deposit anything.',
      suspended: 'The workspace receiving deposits is in the trash: choose another one in the channel\'s options.',
    },
    detail: {
      message: 'Message',
      rejectionReason: 'Reason for refusal',
      asset: 'Media',
      openLibrary: 'See in the library',
      uploading: 'The file is still uploading: it can be played here once it has arrived.',
      neverFinished: 'The upload never finished.',
      assetGone: 'This media is no longer in the library.',
    },
    rejectDialog: {
      title: 'Refuse the submission',
      titleMany: 'Refuse {count} files',
      reason: 'Reason (shown to the member)',
    },
    acceptDialog: {
      title: 'Accept the deposit',
      titleMany: 'Accept {count} files',
      hint:
        'It stays where it is, unless you file it elsewhere. | It stays where it is, unless you file it elsewhere. | They stay where they are, unless you file them elsewhere.',
      elsewhere: 'File elsewhere (optional)',
      folder: 'Folder',
      destination: 'Will be filed in',
      stays:
        'Will stay in {workspace}. | Will stay in {workspace}. | Will stay in {workspace}.',
      staysUnnamed:
        'Will stay where it is. | Will stay where it is. | Will stay where they are.',
      root: 'the workspace\'s root',
      noWorkspace: 'You cannot add media to any workspace.',
    },
  },
  messages: {
    title: 'Messages',
    subtitle: 'What your visitors have written through the contact form.',
    empty: 'No messages',
    noSubject: '(no subject)',
    unreadCount: '{count} unread | {count} unread | {count} unread',
    markRead: 'Mark as read',
    markUnread: 'Mark as unread',
    markError: 'Failed to update the message',
    replyByMail: 'Reply by email',
    filters: {
      unread: 'Unread',
      all: 'All',
    },
    table: {
      from: 'From',
      subject: 'Subject',
      receivedAt: 'Received',
    },
    phone: 'Phone',
    delete: 'Delete',
    deleteTitle: 'Delete this message?',
    deleteMessage: 'The message from {name} will be deleted for good. This cannot be undone.',
    deleted: 'Message deleted',
    deleteError: 'Failed to delete the message',
    pick: 'Select a message to read it',
    back: 'Back to messages',
  },
  pending: {
    chip: 'Preview – mock data',
    tooltip: 'No server serves this screen yet: the figures are made up, in development only, and nothing you do here is saved.',
    // On a served screen, beside the one control that is not served yet.
    extensionTooltip: 'This screen reads your real data. What carries this mark — a filter, a sort, a total, a setting — is not served by any server yet: it is simulated in the browser, in development only, and a setting made there is not saved.',
  },
}
